import React, { useState, useEffect, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Users, LogOut, Send, Phone, History, Award, 
  DollarSign, CheckCircle, XCircle, Clock, TrendingUp,
  UserPlus, AlertCircle, UserCheck, Wallet, Bell, Trophy,
  Key, Gift, Crown, Medal, Star, RefreshCw, AlertTriangle,
  Tv, Play, ShoppingCart, Tag, CreditCard, CalendarDays,
  MessageSquareText, Timer, ArrowUpCircle, RotateCcw, Bitcoin, Upload, Download,
  LayoutDashboard, Package, Settings, FolderTree, Radio, Video, BookOpen, GraduationCap, FileSpreadsheet, Gamepad2, ArrowRightLeft, Receipt, MessageCircle, Megaphone, Mail, Trash2
} from "lucide-react";

// Lightweight components used in login + main shells: imported eagerly.
import ClientDashboard from "@/ClientDashboard";
import StoreFront from "@/StoreFront";
import ProfessorDashboard from "@/ProfessorDashboard";
import { BalanceWidget, AdminBalancePanel, AffiliatePresenceToggle } from "@/BalanceAndAffiliates";
import { Sidebar } from "@/Sidebar";
import PromoVideoPopup from "@/PromoVideoPopup";
import RecipientInfo from "@/RecipientInfo";
import { useExchangeRate, formatUSD, formatHTG } from "@/lib/currency";
import { uploadPaymentProof } from "@/lib/uploadProof";
import { useVersionCheck } from "@/useVersionCheck";
import CookieConsent from "@/CookieConsent";
import NotificationsPrompt, { subscribePushIfPossible } from "@/NotificationsPrompt";
import NotificationCenter from "@/NotificationCenter";
import "@/StoreFront.css";

// Heavy admin-only sub-panels: lazy-loaded so the initial bundle is small and
// the dashboard renders fast (each panel only downloads when first opened).
const AdminServices         = lazy(() => import("@/AdminServices"));
const AdminCourses          = lazy(() => import("@/AdminCourses"));
const AdminProfessors       = lazy(() => import("@/AdminProfessors"));
const AdminBalanceHistory   = lazy(() => import("@/AdminBalanceHistory"));
const AdminGlobalHistory    = lazy(() => import("@/AdminGlobalHistory"));
const AdminSettings         = lazy(() => import("@/AdminSettings"));
const AdminFreeFire         = lazy(() => import("@/AdminFreeFire"));
const AdminReceipts         = lazy(() => import("@/AdminReceipts"));
const AdminClientAudit      = lazy(() => import("@/AdminClientAudit"));
const AdminCreateAffiliatePanel    = lazy(() => import("@/AdminCreateAffiliatePanel"));
const AdminP2PVerification         = lazy(() => import("@/AdminP2PVerification"));
const AffiliateClientTransactions  = lazy(() => import("@/AffiliateClientTransactions"));
const AdminGameTopup        = lazy(() => import("@/AdminGameTopup"));
const AdminClientContacts   = lazy(() => import("@/AdminClientContacts"));
const AdminChat             = lazy(() => import("@/AdminChat"));
const AffiliateMyClients    = lazy(() => import("@/AffiliateMyClients"));
const ActivateClientPage    = lazy(() => import("@/ActivateClientPage"));
const AdminWhatsAppRequests = lazy(() => import("@/AdminWhatsAppRequests"));
const AdminClientSafety     = lazy(() => import("@/AdminClientSafety"));
const AdminSMM              = lazy(() => import("@/AdminSMM"));
const LiveSmmWidget         = lazy(() => import("@/LiveSmmWidget"));
const PublicSMM             = lazy(() => import("@/PublicSMM"));
const StreamingAccountsPanel = lazy(() => import("@/StreamingAccountsPanel"));
const AdminHtgP2PExchanges  = lazy(() => import("@/AdminHtgP2PExchanges").then(m => ({ default: m.AdminHtgP2PExchanges })));
const AdminProfitDashboard  = lazy(() => import("@/AdminProfitDashboard").then(m => ({ default: m.AdminProfitDashboard })));
const CryptoExchangeUser    = lazy(() => import("@/CryptoExchange").then(m => ({ default: m.CryptoExchangeUser })));
const CryptoExchangeAdmin   = lazy(() => import("@/CryptoExchange").then(m => ({ default: m.CryptoExchangeAdmin })));

const LazyPanel = ({ children }) => (
  <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center", color: "#64748B" }}>Chargement…</div>}>
    {children}
  </Suspense>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Dynamic API URL detection for custom domains
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  if (hostname === 'smartechsystempro.com' || hostname === 'www.smartechsystempro.com') {
    return `${protocol}//${hostname}/api`;
  }
  
  if (hostname.includes('emergent.host') || hostname.includes('emergentagent.com')) {
    return `${protocol}//${hostname}/api`;
  }
  
  return BACKEND_URL ? `${BACKEND_URL}/api` : '/api';
};

const API = getApiUrl();

// Configure axios
axios.defaults.withCredentials = true;

// === Centralized auth helpers (token persistence + auto-refresh) ============
// The app uses 3 storage keys depending on role:
//   - "token"        → admin/affiliate
//   - "client_token" → client
//   - "prof_token"   → professor
// Refresh tokens are unified under one key per role family. The helpers below
// transparently pick the right key based on what's currently set.
const TOKEN_KEYS = ["token", "client_token", "prof_token"];
const REFRESH_KEYS = {
  token: "refresh_token",
  client_token: "client_refresh_token",
  prof_token: "prof_refresh_token",
};

const getActiveTokenKey = () => {
  for (const k of TOKEN_KEYS) {
    try { if (localStorage.getItem(k)) return k; } catch { /* ignore */ }
  }
  return "token"; // default
};
const getStoredToken = () => {
  for (const k of TOKEN_KEYS) {
    try {
      const v = localStorage.getItem(k);
      if (v) return v;
    } catch { /* ignore */ }
  }
  return "";
};
const getStoredRefresh = () => {
  const tk = getActiveTokenKey();
  try { return localStorage.getItem(REFRESH_KEYS[tk] || "refresh_token") || ""; } catch { return ""; }
};
let setStoredTokens = (access, refresh, role = null) => {
  // role hint: "client" | "professor" | otherwise admin/affiliate
  let key = "token";
  let refreshKey = "refresh_token";
  if (role === "client") { key = "client_token"; refreshKey = "client_refresh_token"; }
  else if (role === "professor") { key = "prof_token"; refreshKey = "prof_refresh_token"; }
  try {
    if (access) localStorage.setItem(key, access);
    if (refresh) localStorage.setItem(refreshKey, refresh);
  } catch { /* private mode etc */ }
};
let clearStoredTokens = () => {
  try {
    TOKEN_KEYS.forEach((k) => localStorage.removeItem(k));
    Object.values(REFRESH_KEYS).forEach((k) => localStorage.removeItem(k));
    sessionStorage.removeItem("token");
  } catch { /* ignore */ }
};

// Request interceptor — attach Bearer if missing
axios.interceptors.request.use((config) => {
  // Allow callers to opt-out by setting config._skipAuth = true
  if (config._skipAuth) return config;
  const token = getStoredToken();
  if (token && !(config.headers && config.headers.Authorization)) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
}, (e) => Promise.reject(e));

// Response interceptor — auto-refresh on 401 (single-flight queue)
let _refreshPromise = null;
let _proactiveTimer = null;

// Decode JWT exp claim (no verification — used only to schedule proactive refresh)
const decodeJwtExp = (token) => {
  if (!token || typeof token !== "string") return 0;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return 0;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    return Number(payload.exp || 0) * 1000; // ms epoch
  } catch { return 0; }
};

// Proactive refresh: schedule a /auth/refresh call ~5 min before expiry.
// Re-arms itself after each successful refresh. Cleared on logout/visibility change.
const scheduleProactiveRefresh = () => {
  try {
    if (_proactiveTimer) { clearTimeout(_proactiveTimer); _proactiveTimer = null; }
    const access = getStoredToken();
    if (!access) return;
    const expMs = decodeJwtExp(access);
    if (!expMs) return;
    const now = Date.now();
    // Refresh 5 min before expiry, with a min of 30s and max of 23h to avoid runaway timers
    const lead = 5 * 60 * 1000;
    const delay = Math.max(30 * 1000, Math.min(expMs - now - lead, 23 * 60 * 60 * 1000));
    _proactiveTimer = setTimeout(async () => {
      _proactiveTimer = null;
      try {
        const refresh = getStoredRefresh();
        if (!refresh) return;
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        if (!_refreshPromise) {
          _refreshPromise = axios.post(
            `${apiBase}/auth/refresh`,
            { refresh_token: refresh },
            { _skipAuth: true, withCredentials: true }
          ).then((res) => {
            const newAccess = res.data?.access_token || res.data?.token;
            const newRefresh = res.data?.refresh_token;
            const role = res.data?.role;
            const storageRole = role === "client" ? "client" : role === "professor" ? "professor" : null;
            if (newAccess) setStoredTokens(newAccess, newRefresh, storageRole);
            return newAccess;
          }).finally(() => {
            setTimeout(() => { _refreshPromise = null; }, 100);
          });
        }
        await _refreshPromise;
      } catch { /* silent — reactive interceptor will catch any subsequent 401 */ }
      // Re-arm for next cycle (post-refresh exp is further in the future)
      scheduleProactiveRefresh();
    }, delay);
  } catch { /* ignore */ }
};

// Wrap setStoredTokens to also re-schedule proactive refresh
const _origSetStored = setStoredTokens;
const setStoredTokensAndSchedule = (access, refresh, role = null) => {
  _origSetStored(access, refresh, role);
  scheduleProactiveRefresh();
};
// Re-bind exported helper used elsewhere (closure assigned at module load)
// eslint-disable-next-line no-func-assign
setStoredTokens = setStoredTokensAndSchedule;

// Wrap clearStoredTokens to also cancel any pending refresh
const _origClear = clearStoredTokens;
const clearStoredTokensAndCancel = () => {
  if (_proactiveTimer) { clearTimeout(_proactiveTimer); _proactiveTimer = null; }
  _origClear();
};
// eslint-disable-next-line no-func-assign
clearStoredTokens = clearStoredTokensAndCancel;

// Re-arm on visibility change (browsers throttle background timers heavily)
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      // If timer was throttled and the token is now near-expired, refresh immediately
      const access = getStoredToken();
      if (!access) return;
      const expMs = decodeJwtExp(access);
      if (expMs && expMs - Date.now() < 5 * 60 * 1000) {
        scheduleProactiveRefresh(); // will fire its 30s minimum delay
      }
    }
  });
}

// Initial scheduling at module load (in case the user reloads with a valid token)
scheduleProactiveRefresh();

axios.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail || "";
    const isAuthExpired =
      status === 401 &&
      (detail.includes("expir") || detail.includes("Token") || detail.includes("Non authentif"));

    // Don't try to refresh if (1) it's the refresh call itself, (2) we already retried, (3) it's a non-auth 401
    const url = (original.url || "").toString();
    const isRefreshCall = url.includes("/auth/refresh");
    const isLoginCall = url.includes("/auth/login") || url.includes("/auth/register");

    if (!isAuthExpired || isRefreshCall || isLoginCall || original._retried) {
      return Promise.reject(error);
    }

    try {
      // Single-flight: only ONE refresh request in flight at a time
      if (!_refreshPromise) {
        const refresh = getStoredRefresh();
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        _refreshPromise = axios.post(
          `${apiBase}/auth/refresh`,
          refresh ? { refresh_token: refresh } : {},
          { _skipAuth: true, withCredentials: true }
        ).then((res) => {
          const newAccess = res.data?.access_token || res.data?.token;
          const newRefresh = res.data?.refresh_token;
          const role = res.data?.role; // "admin"|"affiliate"|"client"|"professor"
          if (newAccess) {
            const storageRole = role === "client" ? "client" : role === "professor" ? "professor" : null;
            setStoredTokens(newAccess, newRefresh, storageRole);
          }
          return newAccess;
        }).finally(() => {
          // Clear after a short tick so concurrent failures share the same flight
          setTimeout(() => { _refreshPromise = null; }, 100);
        });
      }
      const newAccess = await _refreshPromise;
      if (newAccess) {
        original._retried = true;
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newAccess}` };
        return axios.request(original);
      }
    } catch (e) {
      // Refresh failed → force logout (only if not already on /login)
      clearStoredTokens();
      try {
        if (typeof window !== "undefined" && !/\/login(\b|\/)/.test(window.location.pathname)) {
          window.location.replace("/login?expired=1");
        }
      } catch { /* ignore */ }
    }
    return Promise.reject(error);
  }
);
// =============================================================================

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(res.data);
    } catch {
      clearStoredTokens();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (code, password) => {
    const res = await axios.post(`${API}/auth/login`, { code, password });
    setStoredTokens(res.data.token, res.data.refresh_token);
    setUser(res.data);
    // Re-subscribe to web push now that we have a token
    try { subscribePushIfPossible(); } catch { /* ignore */ }
    return res.data;
  };

  const register = async (code, password, sponsorCode, whatsapp) => {
    const res = await axios.post(`${API}/auth/register`, {
      code,
      password,
      whatsapp,
      sponsor_code: sponsorCode || null
    });
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {}
    clearStoredTokens();
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// API Helper
const apiCall = async (method, url, data = null) => {
  const token = localStorage.getItem("token");
  const config = {
    method,
    url: `${API}${url}`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    data
  };
  return axios(config);
};

// Format helpers
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("fr-FR", { 
    day: "2-digit", 
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const getLevelClass = (level) => {
  const l = level?.toLowerCase() || "bronze";
  if (l === "bronze") return "level-bronze";
  if (l === "silver") return "level-silver";
  if (l === "gold") return "level-gold";
  if (l === "vip") return "level-vip";
  if (l === "admin") return "level-admin";
  return "level-bronze";
};

const getStatusClass = (status) => {
  if (status === "approved") return "status-approved";
  if (status === "rejected") return "status-rejected";
  return "status-pending";
};

const getRankIcon = (rank) => {
  if (rank === 1) return <Crown size={16} className="text-yellow-500" />;
  if (rank === 2) return <Medal size={16} className="text-gray-400" />;
  if (rank === 3) return <Medal size={16} className="text-orange-400" />;
  return <span className="rank-number">{rank}</span>;
};

// Phone Check Hook
const usePhoneCheck = () => {
  const [checking, setChecking] = useState(false);
  const [phoneStatus, setPhoneStatus] = useState(null);
  const debounceRef = useRef(null);

  const checkPhone = useCallback(async (phone) => {
    if (!phone || phone.length < 8) {
      setPhoneStatus(null);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await axios.post(`${API}/check-phone`, { phone_number: phone });
        setPhoneStatus(res.data);
      } catch (err) {
        setPhoneStatus(null);
      } finally {
        setChecking(false);
      }
    }, 500);
  }, []);

  const clearStatus = useCallback(() => {
    setPhoneStatus(null);
  }, []);

  return { checking, phoneStatus, checkPhone, clearStatus };
};

// Confirmation Dialog Component
const ConfirmDialog = ({ isOpen, title, message, onConfirm, onCancel, confirmText = "Confirmer", cancelText = "Annuler", type = "warning" }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="confirm-dialog">
      <div className="modal-content">
        <div className={`modal-icon ${type}`}>
          {type === "warning" ? <AlertTriangle size={24} /> : <AlertCircle size={24} />}
        </div>
        <h3 className="modal-title">{title}</h3>
        <p className="modal-message">{message}</p>
        <div className="modal-actions">
          <button onClick={onCancel} className="btn btn-outline btn-sm">
            {cancelText}
          </button>
          <button onClick={onConfirm} className="btn btn-primary btn-sm">
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Phone Input with Status
const PhoneInputWithStatus = ({ value, onChange, phoneStatus, checking, placeholder, disabled, testId }) => {
  const isOwner = phoneStatus?.is_owner;
  return (
    <div className="phone-input-wrapper">
      <input
        type="tel"
        className={`form-input ${phoneStatus?.exists && !isOwner ? 'input-error' : ''} ${isOwner ? 'input-owner' : ''}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        data-testid={testId}
      />
      {checking && (
        <div className="phone-status checking">
          <RefreshCw size={14} className="spin" /> Vérification...
        </div>
      )}
      {phoneStatus?.exists && !isOwner && (
        <div className="phone-status error">
          <AlertCircle size={14} /> {phoneStatus.message}
        </div>
      )}
      {phoneStatus?.exists && isOwner && (
        <div className="phone-status owner">
          <CheckCircle size={14} /> {phoneStatus.message}
        </div>
      )}
    </div>
  );
};

// === Free Fire Topup inline actions for admin pending orders ===
// Two buttons: "Débloquer le code" (assigns) + "Lancer Topup" (queues bot job).
// Polls the job every 4s while running.
const FreeFireTopupActions = ({ order, apiCall, onChange }) => {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState(order.freefire_code || "");

  const fetchJob = useCallback(async () => {
    try {
      const r = await apiCall("get", `/admin/orders/${order.id}/freefire/job`);
      setJob(r.data?.job || null);
    } catch { /* noop */ }
  }, [apiCall, order.id]);

  useEffect(() => { fetchJob(); }, [fetchJob]);
  useEffect(() => {
    if (!job || (job.status !== "queued" && job.status !== "running")) return;
    const t = setInterval(fetchJob, 4000);
    return () => clearInterval(t);
  }, [job, fetchJob]);

  const handleAssign = async () => {
    setBusy(true);
    try {
      const r = await apiCall("post", `/admin/orders/${order.id}/freefire/assign-code`);
      setCode(r.data?.code || "");
      toast.success(r.data?.already_assigned ? "Code déjà assigné" : `Code assigné: ${r.data?.code}`);
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const handleLaunch = async () => {
    setBusy(true);
    try {
      const r = await apiCall("post", `/admin/orders/${order.id}/freefire/launch-topup`);
      // Auto-copy code to clipboard + open pagostore in new tab
      if (code) {
        try { await navigator.clipboard.writeText(code); } catch { /* noop */ }
      }
      try {
        window.open("https://pagostore.garena.com", "_blank", "noopener,noreferrer");
      } catch { /* noop */ }
      toast.success(r.data?.duplicate ? "Job déjà en cours" : "Code copié + pagostore ouvert dans un nouvel onglet");
      fetchJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const fileInputRef = useRef(null);

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await apiCall("post", `/admin/freefire/jobs/${job.id}/upload-proof`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (r.data?.status === "done") {
        toast.success("✅ AI a confirmé le Top-Up");
      } else if (r.data?.status === "suspect") {
        toast.warning("⚠️ AI n'a pas confirmé — vérifiez manuellement");
      }
      fetchJob();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const STATUS_DEF = {
    queued: { label: "En attente", color: "#92400E", bg: "#FEF3C7" },
    running: { label: "Vérification AI…", color: "#1D4ED8", bg: "#DBEAFE" },
    awaiting_proof: { label: "📤 En attente de preuve", color: "#B45309", bg: "#FEF3C7" },
    done: { label: "✅ Réussi & vérifié AI", color: "#15803D", bg: "#DCFCE7" },
    suspect: { label: "⚠️ Suspect — AI non confirmé", color: "#B45309", bg: "#FEF3C7" },
    failed: { label: "❌ Échoué", color: "#B91C1C", bg: "#FEE2E2" },
  };
  const def = job ? (STATUS_DEF[job.status] || STATUS_DEF.queued) : null;

  const handleOverride = async (decision) => {
    if (!job) return;
    const msg = decision === "confirm"
      ? "Confirmer ce Top-Up comme réussi ? Le code sera marqué utilisé."
      : "Rejeter ce Top-Up ? Le code sera libéré et l'admin devra rembourser le client.";
    if (!window.confirm(msg)) return;
    try {
      await apiCall("post", `/admin/freefire/jobs/${job.id}/override`, { decision });
      toast.success(decision === "confirm" ? "Top-Up confirmé" : "Top-Up rejeté");
      fetchJob();
      if (onChange) onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      data-testid={`freefire-actions-${order.id}`}
      style={{
        marginTop: "0.45rem",
        padding: "0.55rem",
        background: "#F0F9FF",
        borderRadius: 8,
        border: "1px solid #93C5FD",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, fontSize: "0.72rem", fontWeight: 700, color: "#1D4ED8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        <Gamepad2 size={13} /> Topup Bot Free Fire
      </div>

      {code ? (
        <div style={{ background: "#FFF", border: "1px solid #DBEAFE", borderRadius: 6, padding: "0.35rem 0.5rem", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: "0.62rem", color: "#64748B", fontWeight: 600 }}>CODE ASSIGNÉ</div>
            <code data-testid={`ff-code-${order.id}`} style={{ fontSize: "0.85rem", fontWeight: 700, color: "#0F172A", wordBreak: "break-all" }}>{code}</code>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(code); toast.success("Copié"); }} style={{ background: "#2563EB", color: "white", border: "none", borderRadius: 6, padding: "0.2rem 0.45rem", cursor: "pointer", fontSize: "0.65rem", fontWeight: 600 }}>
            Copier
          </button>
        </div>
      ) : (
        <button
          onClick={handleAssign}
          disabled={busy}
          data-testid={`ff-assign-${order.id}`}
          style={{ background: "#2563EB", color: "white", border: "none", borderRadius: 6, padding: "0.35rem 0.6rem", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, marginRight: 6 }}
        >
          🔓 {busy ? "..." : "Débloquer le code"}
        </button>
      )}

      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
        <button
          onClick={handleLaunch}
          disabled={busy || !code || (job && (job.status === "queued" || job.status === "running"))}
          data-testid={`ff-launch-${order.id}`}
          style={{ background: "#7C3AED", color: "white", border: "none", borderRadius: 6, padding: "0.35rem 0.65rem", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4, opacity: (!code || busy || (job && (job.status === "queued" || job.status === "running"))) ? 0.55 : 1 }}
        >
          🚀 Lancer Topup
        </button>
        {def && (
          <span style={{ padding: "0.15rem 0.5rem", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700, background: def.bg, color: def.color }} data-testid={`ff-status-${order.id}`}>
            {def.label}{job.attempts > 1 ? ` · tentative ${job.attempts}` : ""}
          </span>
        )}
      </div>

      {job && job.logs && job.logs.length > 0 && (job.status === "running" || job.status === "failed") && (
        <div style={{ marginTop: 6, background: "#0F172A", color: "#E2E8F0", borderRadius: 6, padding: "0.4rem 0.5rem", fontFamily: "monospace", fontSize: "0.68rem", maxHeight: 120, overflowY: "auto" }}>
          {job.logs.slice(-6).map((l, i) => (
            <div key={l.ts ? `${l.ts}-${i}` : `log-${i}`}>
              <span style={{ color: "#64748B" }}>{(l.ts || "").slice(11, 19)}</span> {l.message}
            </div>
          ))}
        </div>
      )}
      {job && job.status === "awaiting_proof" && (
        <div style={{ marginTop: 8, padding: "0.6rem", background: "#FFFBEB", border: "1px solid #FBBF24", borderLeft: "4px solid #F59E0B", borderRadius: 6 }} data-testid={`ff-awaiting-proof-${order.id}`}>
          <div style={{ fontSize: "0.74rem", fontWeight: 700, color: "#B45309", marginBottom: 4 }}>
            📋 Étapes à suivre :
          </div>
          <ol style={{ fontSize: "0.7rem", color: "#7C2D12", margin: "0 0 8px 18px", padding: 0 }}>
            <li>Le code est déjà copié dans votre presse-papiers + pagostore.garena.com s'est ouvert</li>
            <li>Sélectionnez "Rest of LATAM" + collez le Player ID + Login</li>
            <li>Choisissez Redeem + collez le code + Validez</li>
            <li>Faites une <strong>capture d'écran</strong> de la page de confirmation</li>
            <li>Uploadez-la ci-dessous — l'AI Gemini Vision vérifiera automatiquement</li>
          </ol>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={handleProofUpload}
            data-testid={`ff-proof-input-${order.id}`}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={busy}
            data-testid={`ff-upload-proof-${order.id}`}
            style={{ background: "#F59E0B", color: "white", border: "none", borderRadius: 6, padding: "0.4rem 0.8rem", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            📸 {busy ? "Vérification…" : "Uploader la preuve de Top-Up"}
          </button>
        </div>
      )}
      {job && job.status === "done" && job.screenshot_url && (
        <div style={{ fontSize: "0.65rem", color: "#15803D", marginTop: 4 }}>
          📸 <a href={`/api/admin/freefire/screenshot/${job.screenshot_url.split("/").pop()}`} target="_blank" rel="noopener noreferrer" style={{ color: "#15803D", textDecoration: "underline", wordBreak: "break-all" }}>Voir la capture</a>
          {job.verification && job.verification.verified && (
            <span style={{ marginLeft: 6, padding: "0.1rem 0.4rem", background: "#DCFCE7", color: "#15803D", borderRadius: 999, fontWeight: 700 }}>
              🔎 AI ✓ {Math.round((job.verification.confidence || 0) * 100)}%
            </span>
          )}
        </div>
      )}
      {job && job.status === "suspect" && (
        <div style={{ marginTop: 6, padding: "0.5rem 0.6rem", background: "#FFFBEB", border: "1px solid #FBBF24", borderRadius: 6 }} data-testid={`ff-suspect-${order.id}`}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#B45309", marginBottom: 4 }}>
            ⚠️ Top-Up SUSPECT — Vérification visuelle requise
          </div>
          <div style={{ fontSize: "0.68rem", color: "#7C2D12", marginBottom: 6 }}>
            La page de Garena s'est affichée mais l'OCR n'a pas trouvé les mots-clés de succès. Vérifiez le screenshot manuellement avant de décider.
          </div>
          {job.screenshot_url && (
            <div style={{ fontSize: "0.65rem", marginBottom: 6 }}>
              📸 <a href={`/api/admin/freefire/screenshot/${job.screenshot_url.split("/").pop()}`} target="_blank" rel="noopener noreferrer" style={{ color: "#7C2D12", textDecoration: "underline" }}>Voir la capture uploadée</a>
            </div>
          )}
          {job.verification?.extracted_text && (
            <details style={{ fontSize: "0.65rem", marginBottom: 6 }}>
              <summary style={{ cursor: "pointer", color: "#7C2D12" }}>Voir le texte extrait par OCR</summary>
              <div style={{ background: "#FFF", padding: "0.4rem", borderRadius: 4, marginTop: 4, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
                {job.verification.extracted_text}
              </div>
            </details>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => handleOverride("confirm")}
              data-testid={`ff-suspect-confirm-${order.id}`}
              style={{ background: "#16A34A", color: "white", border: "none", borderRadius: 6, padding: "0.3rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
            >
              ✅ Confirmer manuellement
            </button>
            <button
              onClick={() => handleOverride("reject")}
              data-testid={`ff-suspect-reject-${order.id}`}
              style={{ background: "#DC2626", color: "white", border: "none", borderRadius: 6, padding: "0.3rem 0.6rem", fontSize: "0.72rem", fontWeight: 700, cursor: "pointer" }}
            >
              ❌ Rejeter & libérer le code
            </button>
          </div>
        </div>
      )}
      {job && job.status === "failed" && job.error && (
        <div style={{ fontSize: "0.7rem", color: "#B91C1C", marginTop: 4 }}>
          {job.error}
        </div>
      )}
    </div>
  );
};

// Notification Popup Component
const NotificationPopup = ({ notifications, onClose, onMarkRead }) => {
  if (!notifications || notifications.length === 0) return null;

  return (
    <div className="notification-popup" data-testid="notification-popup">
      <div className="notification-header">
        <h4><Bell size={16} /> Notifications</h4>
        <button onClick={onClose} className="close-btn">×</button>
      </div>
      <div className="notification-list">
        {notifications.map((notif) => (
          <div 
            key={notif.id} 
            className={`notification-item ${notif.type} ${notif.read ? 'read' : 'unread'}`}
            onClick={() => onMarkRead(notif.id)}
          >
            <div className="notif-icon">
              {notif.type === 'reward' ? <Gift size={16} /> : 
               notif.type === 'success' ? <CheckCircle size={16} /> :
               <Bell size={16} />}
            </div>
            <div className="notif-content">
              <div className="notif-title">{notif.title}</div>
              <div className="notif-message">{notif.message}</div>
              <div className="notif-time">{formatDate(notif.created_at)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Leaderboard Component
const Leaderboard = ({ leaderboard, title = "Top 10 Affiliés", showMonthly = false }) => {
  return (
    <div className="leaderboard-card" data-testid="leaderboard">
      <h3 className="card-title"><Trophy size={18} /> {title}</h3>
      {leaderboard.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏆</div>
          <div className="empty-state-text">Aucun affilié</div>
        </div>
      ) : (
        <div className="leaderboard-list">
          {leaderboard.map((item) => (
            <div key={item.code} className={`leaderboard-item rank-${item.rank}`}>
              <div className="rank-badge">{getRankIcon(item.rank)}</div>
              <div className="affiliate-info">
                <span className="affiliate-code">{item.code}</span>
                <span className={`level-badge ${getLevelClass(item.level)}`}>{item.level}</span>
              </div>
              <div className="clients-count">
                <strong>{showMonthly ? item.monthly_clients : item.clients}</strong>
                <span>clients</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Login Page
const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") || "affiliate";
  const initialAction = searchParams.get("action");
  const redirectParam = searchParams.get("redirect");
  const [authMode, setAuthMode] = useState(redirectParam?.startsWith("/client") ? "client" : initialMode);
  const [isLogin, setIsLogin] = useState(initialAction !== "register");
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetKind, setResetKind] = useState("affiliate");  // "affiliate" | "client"
  const [resetPhone, setResetPhone] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [sponsorCode, setSponsorCode] = useState("");
  const [affWhatsapp, setAffWhatsapp] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientPassword, setClientPassword] = useState("");
  const [clientAffiliateCode, setClientAffiliateCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  // Professor state
  const [profEmail, setProfEmail] = useState("");
  const [profPassword, setProfPassword] = useState("");
  const [profName, setProfName] = useState("");
  const [profDesc, setProfDesc] = useState("");
  const [profPhoto, setProfPhoto] = useState(null);
  const [profCV, setProfCV] = useState(null);

  // Capture ?redirect=… (sent by the public SMM landing page) so that, after
  // a successful login or register, we land the user back where they wanted
  // to go (e.g. /client/smm to finish their order).
  useEffect(() => {
    if (redirectParam) {
      try { sessionStorage.setItem("client_post_login_redirect", redirectParam); } catch {}
    }
  }, [redirectParam]);

  // Client login/register
  const handleClientSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      if (isLogin) {
        const res = await axios.post(`${API}/auth/login-client`, {
          code: clientPhone,
          password: clientPassword
        });
        setStoredTokens(res.data.token, res.data.refresh_token, "client");
        localStorage.setItem("client_user", JSON.stringify(res.data));
        toast.success("Connexion réussie!");
        try { subscribePushIfPossible(); } catch { /* ignore */ }
        {
          const post = sessionStorage.getItem("client_post_login_redirect");
          if (post) {
            sessionStorage.removeItem("client_post_login_redirect");
            navigate(post);
          } else {
            navigate("/client");
          }
        }
      } else {
        const res = await axios.post(`${API}/auth/register-client`, {
          phone: clientPhone,
          password: clientPassword,
          name: clientName || undefined,
          email: clientEmail || undefined,
          affiliate_code: clientAffiliateCode || undefined
        });
        setStoredTokens(res.data.token, res.data.refresh_token, "client");
        localStorage.setItem("client_user", JSON.stringify(res.data));
        toast.success("Compte créé!");
        try { subscribePushIfPossible(); } catch { /* ignore */ }
        {
          const post = sessionStorage.getItem("client_post_login_redirect");
          if (post) {
            sessionStorage.removeItem("client_post_login_redirect");
            navigate(post);
          } else {
            navigate("/client");
          }
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Erreur de connexion");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      if (isLogin) {
        const user = await login(code, password);
        toast.success("Connexion réussie!");
        navigate(["admin", "sub_admin"].includes(user.role) ? "/admin" : "/dashboard");
      } else {
        if (!affWhatsapp.trim() || affWhatsapp.trim().replace(/\D/g, "").length < 8) {
          setError("Numéro WhatsApp obligatoire (min. 8 chiffres) pour l'inscription affilié.");
          setLoading(false);
          return;
        }
        const result = await register(code, password, sponsorCode, affWhatsapp.trim());
        setSuccess(result.message || "Inscription réussie! En attente d'approbation.");
        setCode("");
        setPassword("");
        setSponsorCode("");
        setAffWhatsapp("");
        toast.success("Inscription soumise!");
      }
    } catch (err) {
      console.error("Login error:", err);
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      } else if (err.message === "Network Error") {
        setError("Erreur de connexion au serveur. Veuillez réessayer.");
      } else {
        setError(err.message || "Une erreur est survenue");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      const payload = resetKind === "client"
        ? { kind: "client", phone: resetPhone.trim() }
        : { kind: "affiliate", code: resetCode };
      await axios.post(`${API}/auth/request-password-reset`, payload);
      setSuccess("Demande envoyée! L'administrateur vous contactera sur WhatsApp.");
      setResetCode("");
      setResetPhone("");
      setShowPasswordReset(false);
      toast.success("Demande envoyée!");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la demande");
    } finally {
      setIsLoading(false);
    }
  };

  // Professor login/register
  const handleProfessorSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setIsLoading(true);
    try {
      if (isLogin) {
        const res = await axios.post(`${API}/auth/login-professor`, { email: profEmail, password: profPassword });
        setStoredTokens(res.data.token, res.data.refresh_token, "professor");
        localStorage.setItem("prof_user", JSON.stringify(res.data));
        toast.success("Connexion reussie!");
        navigate("/professor");
      } else {
        const fd = new FormData();
        fd.append("email", profEmail);
        fd.append("password", profPassword);
        fd.append("name", profName);
        fd.append("description", profDesc);
        if (profPhoto) fd.append("photo", profPhoto);
        if (profCV) fd.append("cv", profCV);
        const res = await axios.post(`${API}/auth/register-professor`, fd);
        setSuccess(res.data.message || "Inscription envoyee!");
        toast.success("Inscription soumise!");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur");
    } finally {
      setIsLoading(false);
    }
  };

  if (showPasswordReset) {
    return (
      <div className="login-page" data-testid="password-reset-page">
        <div className="login-card">
          <div className="login-logo">
            <h1>SMARTECH SYSTEM PRO</h1>
            <span>Réinitialisation du mot de passe</span>
          </div>
          {error && <div className="alert alert-error"><AlertCircle size={18} /> {error}</div>}
          {success && <div className="alert alert-success"><CheckCircle size={18} /> {success}</div>}
          {/* Reset kind toggle */}
          <div className="auth-mode-toggle" data-testid="reset-mode-toggle" style={{ marginBottom: "1rem" }}>
            <button type="button" className={`mode-btn ${resetKind === "affiliate" ? "active" : ""}`}
              onClick={() => setResetKind("affiliate")} data-testid="reset-mode-affiliate">
              <Users size={16} /> Affilié
            </button>
            <button type="button" className={`mode-btn ${resetKind === "client" ? "active" : ""}`}
              onClick={() => setResetKind("client")} data-testid="reset-mode-client">
              <Phone size={16} /> Client
            </button>
          </div>
          <form onSubmit={handlePasswordResetRequest}>
            {resetKind === "client" ? (
              <div className="form-group">
                <label className="form-label">Votre numéro de téléphone</label>
                <input type="tel" className="form-input" placeholder="Ex: 50912345678"
                  value={resetPhone} onChange={(e) => setResetPhone(e.target.value)} required data-testid="reset-phone-input" />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">Votre Code Affilié</label>
                <input type="text" className="form-input" placeholder="Entrez votre code"
                  value={resetCode} onChange={(e) => setResetCode(e.target.value)} required data-testid="reset-code-input" />
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={isLoading} data-testid="reset-submit-btn">
              <Key size={18} /> {isLoading ? "Envoi..." : "Demander réinitialisation"}
            </button>
          </form>
          <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
            <button type="button" onClick={() => setShowPasswordReset(false)} className="text-link"
              style={{ background: "none", border: "none", cursor: "pointer" }}>Retour à la connexion</button>
          </div>
          <div className="info-box">
            <AlertCircle size={16} />
            <span>L'administrateur recevra votre demande et vous contactera pour réinitialiser votre mot de passe.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-card">
        <div className="login-logo">
          <img src="https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/24go47ow_20251231_131530.png" alt="SMARTECH" className="login-logo-img" />
          <h1>SMARTECH SYSTEM PRO</h1>
          <span>{authMode === "client" ? "Espace Client" : authMode === "professor" ? "Espace Professeur" : "Gestion d'Affilies"}</span>
        </div>

        {/* Auth Mode Toggle */}
        <div className="auth-mode-toggle" data-testid="auth-mode-toggle">
          <button
            type="button"
            className={`mode-btn ${authMode === "affiliate" ? "active" : ""}`}
            onClick={() => { setAuthMode("affiliate"); setError(""); setSuccess(""); setIsLogin(true); }}
            data-testid="mode-affiliate"
          >
            <Users size={16} /> Affilie
          </button>
          <button
            type="button"
            className={`mode-btn ${authMode === "client" ? "active" : ""}`}
            onClick={() => { setAuthMode("client"); setError(""); setSuccess(""); setIsLogin(true); }}
            data-testid="mode-client"
          >
            <ShoppingCart size={16} /> Client
          </button>
          <button
            type="button"
            className={`mode-btn ${authMode === "professor" ? "active" : ""}`}
            onClick={() => { setAuthMode("professor"); setError(""); setSuccess(""); setIsLogin(true); }}
            data-testid="mode-professor"
          >
            <Award size={16} /> Professeur
          </button>
        </div>

        {error && <div className="alert alert-error" data-testid="login-error"><AlertCircle size={18} /> {error}</div>}
        {success && <div className="alert alert-success" data-testid="register-success"><CheckCircle size={18} /> {success}</div>}

        {/* CLIENT AUTH */}
        {authMode === "client" && (
          <>
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/client";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="client-google-signin-btn"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.6rem",
                padding: "0.7rem 1rem",
                background: "#FFF",
                color: "#1F2937",
                border: "1px solid #E5E7EB",
                borderRadius: 10,
                fontWeight: 600,
                fontSize: "0.92rem",
                cursor: "pointer",
                marginBottom: "0.75rem",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#FFF")}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Continuer avec Google
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", margin: "0.5rem 0 0.75rem" }}>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
              <span style={{ fontSize: "0.7rem", color: "#94A3B8", fontWeight: 600 }}>OU</span>
              <div style={{ flex: 1, height: 1, background: "#E5E7EB" }} />
            </div>
          <form onSubmit={handleClientSubmit}>
            {!isLogin && (
              <div className="form-group">
                <label className="form-label">Email (optionnel)</label>
                <input type="email" className="form-input" placeholder="vous@exemple.com"
                  value={clientEmail} onChange={e => setClientEmail(e.target.value)} data-testid="client-email-input" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Numéro de téléphone</label>
              <input type="tel" className="form-input" placeholder="Ex: 50912345678"
                value={clientPhone} onChange={e => setClientPhone(e.target.value)} required data-testid="client-phone-login" />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <input type="password" className="form-input" placeholder="Mot de passe"
                value={clientPassword} onChange={e => setClientPassword(e.target.value)} required data-testid="client-password-login" />
            </div>
            {!isLogin && (
              <div className="form-group">
                <label className="form-label">Code Affilié (optionnel, choisissez plus tard si non)</label>
                <input type="text" className="form-input" placeholder="Code de l'affilié qui vous a référé"
                  value={clientAffiliateCode} onChange={e => setClientAffiliateCode(e.target.value)} data-testid="client-affiliate-code" />
              </div>
            )}
            <button type="submit" className="btn btn-primary" disabled={isLoading} data-testid="client-submit-btn">
              {isLoading ? "Chargement..." : isLogin ? "Se connecter" : "Créer mon compte"}
            </button>
            {isLogin && (
              <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                <button type="button"
                  onClick={() => { setShowPasswordReset(true); setResetKind("client"); }}
                  className="text-link"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2563EB", fontSize: "0.85rem", fontWeight: 600 }}
                  data-testid="client-forgot-password-btn">
                  <Key size={14} /> Mot de passe oublié ?
                </button>
              </div>
            )}
          </form>
          </>
        )}

        {/* AFFILIATE AUTH — login only (registration is admin-only) */}
        {authMode === "affiliate" && (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Code Affilié</label>
              <input type="text" className="form-input" placeholder="Votre code"
                value={code} onChange={(e) => setCode(e.target.value)} required data-testid="login-code-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe</label>
              <input type="password" className="form-input" placeholder="Mot de passe"
                value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={isLoading} data-testid="login-submit-btn">
              {isLoading ? "Chargement..." : "Se connecter"}
            </button>
          </form>
        )}

        {/* PROFESSOR AUTH */}
        {authMode === "professor" && (
          <form onSubmit={handleProfessorSubmit}>
            {!isLogin && (
              <div className="form-group">
                <label className="form-label">Nom complet *</label>
                <input type="text" className="form-input" placeholder="Votre nom" value={profName} onChange={e => setProfName(e.target.value)} required data-testid="prof-name-input" />
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Email *</label>
              <input type="email" className="form-input" placeholder="email@exemple.com" value={profEmail} onChange={e => setProfEmail(e.target.value)} required data-testid="prof-email-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Mot de passe *</label>
              <input type="password" className="form-input" placeholder="Min. 6 caracteres" value={profPassword} onChange={e => setProfPassword(e.target.value)} required data-testid="prof-password-input" />
            </div>
            {!isLogin && (
              <>
                <div className="form-group">
                  <label className="form-label">Description / Experience</label>
                  <textarea className="form-input" rows={2} placeholder="Decrivez votre experience" value={profDesc} onChange={e => setProfDesc(e.target.value)} style={{ resize: "vertical" }} />
                </div>
                <div className="form-group" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0.4rem 0.75rem", background: "#EFF6FF", color: "#2563EB", borderRadius: 8, fontSize: "0.8rem", cursor: "pointer", border: "1px dashed #93C5FD" }}>
                    Photo de profil
                    <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => setProfPhoto(e.target.files[0])} />
                  </label>
                  {profPhoto && <span style={{ fontSize: "0.75rem", color: "#16A34A" }}>{profPhoto.name}</span>}
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0.4rem 0.75rem", background: "#F0FDF4", color: "#16A34A", borderRadius: 8, fontSize: "0.8rem", cursor: "pointer", border: "1px dashed #BBF7D0" }}>
                    CV (PDF)
                    <input type="file" accept=".pdf" style={{ display: "none" }} onChange={e => setProfCV(e.target.files[0])} />
                  </label>
                  {profCV && <span style={{ fontSize: "0.75rem", color: "#16A34A" }}>{profCV.name}</span>}
                </div>
              </>
            )}
            <button type="submit" className="btn btn-primary" disabled={isLoading} data-testid="prof-submit-btn">
              {isLoading ? "Chargement..." : isLogin ? "Se connecter" : "S'inscrire comme professeur"}
            </button>
          </form>
        )}

        <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
          {authMode === "affiliate" ? (
            <a
              href="https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="affiliate-request-whatsapp-btn"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
                background: "#25D366", color: "white", padding: "0.65rem 1.1rem",
                borderRadius: 10, fontWeight: 700, textDecoration: "none", fontSize: "0.88rem",
                boxShadow: "0 2px 8px rgba(37, 211, 102, 0.25)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 211, 102, 0.35)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 2px 8px rgba(37, 211, 102, 0.25)"; }}
            >
              <Send size={14} /> Pas de compte ? Demander un accès sur WhatsApp
            </a>
          ) : (
            <button type="button" onClick={() => { setIsLogin(!isLogin); setError(""); setSuccess(""); }}
              className="text-link" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="toggle-auth-mode">
              {isLogin ? "Pas de compte? S'inscrire" : "Déjà inscrit? Se connecter"}
            </button>
          )}
        </div>

        {authMode === "affiliate" && (
          <>
            <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
              <button type="button" onClick={() => { setShowPasswordReset(true); setResetKind("affiliate"); }} className="text-link"
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.875rem", color: "#64748B" }}
                data-testid="forgot-password-btn">
                <Key size={14} /> Mot de passe oublié?
              </button>
            </div>
            <div style={{
              marginTop: "0.9rem", padding: "0.6rem 0.7rem",
              background: "#FFF7ED", border: "1px solid #FED7AA", borderRadius: 8,
              fontSize: "0.74rem", color: "#9A3412", lineHeight: 1.4,
            }} data-testid="affiliate-register-info">
              <AlertCircle size={13} style={{ verticalAlign: -2, marginRight: 3 }} />
              <strong>Inscription désormais gérée par l'administrateur.</strong> Cliquez sur le bouton vert ci-dessus
              pour faire votre demande d'accès au programme d'affiliation. Les affiliés déjà inscrits peuvent se
              connecter normalement avec leur code et mot de passe.
            </div>
          </>
        )}

        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button type="button" onClick={() => navigate("/")} className="text-link"
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.8rem", color: "#94A3B8" }}
            data-testid="back-to-store-btn">
            Retour a la boutique
          </button>
        </div>
      </div>
    </div>
  );
};

// Affiliate Dashboard
const AffiliateDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedService, setSelectedService] = useState("netflix");
  const [selectedPlan, setSelectedPlan] = useState("daily");
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [paymentPhone, setPaymentPhone] = useState("");
  const [minWithdrawal, setMinWithdrawal] = useState(20);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRewardPopup, setShowRewardPopup] = useState(null);
  // Promotions
  const [availablePromos, setAvailablePromos] = useState([]);
  const [activatingPromo, setActivatingPromo] = useState(false);
  // Sol (Tontine)
  const [solStatus, setSolStatus] = useState(null);
  const [solHistory, setSolHistory] = useState(null);
  const [solPayMethod, setSolPayMethod] = useState("balance");
  const [solTxId, setSolTxId] = useState("");
  const [solPaying, setSolPaying] = useState(false);
  // Client Subscriptions
  const [clientSubs, setClientSubs] = useState([]);
  const [affSubSearch, setAffSubSearch] = useState("");
  // Subscription creation form (affiliate)
  const [affSubPhone, setAffSubPhone] = useState("");
  const [affSubName, setAffSubName] = useState("");
  const [affSubService, setAffSubService] = useState("Netflix");
  // Holds the last successful submission so we can render the one-click
  // "Send WhatsApp" CTA (with the activation link embedded) inline.
  const [affLastSubResult, setAffLastSubResult] = useState(null);
  const [affSubDays, setAffSubDays] = useState("");
  const [affSubEmail, setAffSubEmail] = useState("");
  const [affSubProofUrl, setAffSubProofUrl] = useState("");
  const [affSubProofUploading, setAffSubProofUploading] = useState(false);
  const [affSubPaymentMethod, setAffSubPaymentMethod] = useState("qr");
  const [affCreatingSub, setAffCreatingSub] = useState(false);
  const [affCatalogServices, setAffCatalogServices] = useState([]);
  // Validated orders for affiliate
  const [affValidatedOrders, setAffValidatedOrders] = useState([]);
  const [affOrderFilter, setAffOrderFilter] = useState("all");
  // Live exchange rate for USD display
  const exchangeRate = useExchangeRate();
  // Streaming connections for affiliate
  const [affStreamConns, setAffStreamConns] = useState([]);
  // Explainer video
  const [affVideo, setAffVideo] = useState(null);
  // WhatsApp profile (mandatory)
  const [affProfile, setAffProfile] = useState(null);
  const [waInput, setWaInput] = useState("");
  const [waSaving, setWaSaving] = useState(false);
  // Affiliate self-recharge (modal)
  const [showAffRecharge, setShowAffRecharge] = useState(false);
  const [affRechargeAmount, setAffRechargeAmount] = useState("");
  const [affRechargeProofUrl, setAffRechargeProofUrl] = useState("");
  const [affRechargeUploading, setAffRechargeUploading] = useState(false);
  const [affRechargeSubmitting, setAffRechargeSubmitting] = useState(false);
  const [affRechargeMethod, setAffRechargeMethod] = useState("MonCash");
  
  // Phone validation hook
  const { checking: phoneChecking, phoneStatus, checkPhone, clearStatus } = usePhoneCheck();

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, historyRes, pendingRes, settingsRes, leaderboardRes, notifRes, unreadRes] = await Promise.all([
        apiCall("get", "/affiliate/stats"),
        apiCall("get", "/affiliate/history"),
        apiCall("get", "/affiliate/pending"),
        apiCall("get", "/config/withdrawal-settings"),
        apiCall("get", "/leaderboard"),
        apiCall("get", "/notifications"),
        apiCall("get", "/notifications/unread-count")
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setPending(pendingRes.data);
      setMinWithdrawal(settingsRes.data.min_withdrawal);
      setLeaderboard(leaderboardRes.data);
      setNotifications(notifRes.data);
      setUnreadCount(unreadRes.data.count);
      
      try {
        const promosRes = await apiCall("get", "/affiliate/available-promos");
        setAvailablePromos(promosRes.data);
      } catch {}
      try {
        const solRes = await apiCall("get", "/affiliate/sol/status");
        setSolStatus(solRes.data);
      } catch {}
      try {
        const solHistRes = await apiCall("get", "/affiliate/sol/history");
        setSolHistory(solHistRes.data);
      } catch {}
      try {
        const subsRes = await apiCall("get", "/affiliate/client-subscriptions");
        setClientSubs(subsRes.data);
      } catch {}
      try {
        const svcRes = await apiCall("get", "/services");
        setAffCatalogServices(svcRes.data);
      } catch {}
      try {
        const ordRes = await apiCall("get", "/affiliate/validated-orders");
        setAffValidatedOrders(ordRes.data);
      } catch {}
      try {
        const profRes = await apiCall("get", "/affiliate/profile");
        setAffProfile(profRes.data);
      } catch {}
      try {
        const streamRes = await apiCall("get", "/affiliate/streaming-connections");
        setAffStreamConns(streamRes.data);
      } catch {}
      try {
        const vidRes = await axios.get(`${API}/public/explainer-video/affiliate`);
        if (vidRes.data.video_url) setAffVideo(vidRes.data);
      } catch {}
    } catch (err) {
      console.error("Erreur chargement:", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Poll for notifications + new validated orders every 15 seconds
    const interval = setInterval(async () => {
      try {
        const [unreadRes, notifRes, ordRes] = await Promise.all([
          apiCall("get", "/notifications/unread-count"),
          apiCall("get", "/notifications"),
          apiCall("get", "/affiliate/validated-orders").catch(() => ({ data: null })),
        ]);
        const newCount = unreadRes.data.count;
        const newNotifs = notifRes.data;

        // Check for new reward notifications to show popup
        if (newCount > unreadCount) {
          const rewardNotifs = newNotifs.filter(n => n.type === "reward" && !n.read);
          if (rewardNotifs.length > 0) {
            setShowRewardPopup(rewardNotifs[0]);
          }
        }

        setUnreadCount(newCount);
        setNotifications(newNotifs);

        // Refresh validated orders silently. If a brand-new "validated" order
        // appeared (admin just validated), notify the affiliate.
        if (ordRes.data && Array.isArray(ordRes.data)) {
          setAffValidatedOrders(prev => {
            const prevToProcess = (prev || []).filter(o => o.status === "validated").map(o => o.id);
            const nextToProcess = ordRes.data.filter(o => o.status === "validated").map(o => o.id);
            const fresh = nextToProcess.filter(id => !prevToProcess.includes(id));
            if (fresh.length > 0 && (prev || []).length > 0) {
              toast.success(`${fresh.length} nouvelle${fresh.length > 1 ? "s" : ""} commande${fresh.length > 1 ? "s" : ""} à traiter !`);
            }
            return ordRes.data;
          });
        }
      } catch {}
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchData, unreadCount]);

  const handleSubmitClient = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) return;
    
    // Check if phone exists and user is NOT the owner
    if (phoneStatus?.exists && !phoneStatus?.is_owner) {
      toast.error(phoneStatus.message);
      return;
    }

    setIsSubmitting(true);
    try {
      await apiCall("post", "/affiliate/submit-client", { 
        phone_number: phoneNumber,
        service: selectedService,
        plan: selectedPlan
      });
      const planLabel = selectedPlan === "vip" ? "VIP Mensuel" : "Quotidien";
      const serviceLabel = selectedService === "netflix" ? "Netflix" : "Prime Video";
      toast.success(`Client soumis! ${serviceLabel} - ${planLabel}`);
      setPhoneNumber("");
      setSelectedService("netflix");
      setSelectedPlan("daily");
      clearStatus();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActivatePromo = async (promoId) => {
    setActivatingPromo(true);
    try {
      const res = await apiCall("post", "/affiliate/activate-promotion", { promotion_id: promoId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setActivatingPromo(false);
    }
  };


  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    checkPhone(value);
  };

  const handleRequestWithdrawal = async () => {
    const amount = parseFloat(withdrawalAmount);
    
    if (!amount || amount <= 0) {
      toast.error("Entrez un montant valide");
      return;
    }
    
    if (amount < minWithdrawal) {
      toast.error(`Le montant minimum est de ${minWithdrawal} Gdes`);
      return;
    }
    
    if (!stats || amount > stats.gains) {
      toast.error("Solde insuffisant");
      return;
    }
    
    if (!paymentPhone.trim()) {
      toast.error("Entrez le numéro de téléphone pour recevoir le paiement");
      return;
    }

    try {
      const res = await apiCall("post", "/affiliate/request-withdrawal", { 
        amount,
        payment_phone: paymentPhone.trim()
      });
      const { whatsapp_url } = res.data;
      
      // Route affiliate to the official support group instead of admin DM to protect the admin number
      const whatsappUrl = whatsapp_url || "https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2";
      window.open(whatsappUrl, "_blank");
      
      toast.success("Demande enregistrée! En attente d'approbation.");
      setWithdrawalAmount("");
      setPaymentPhone("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la demande");
    }
  };

  const handleMarkNotificationRead = async (notifId) => {
    try {
      await apiCall("post", `/notifications/mark-read/${notifId}`);
      setNotifications(prev => prev.map(n => n.id === notifId ? {...n, read: true} : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getHistoryIcon = (action) => {
    if (action.includes("valide") || action.includes("approuve")) return <CheckCircle size={16} />;
    if (action.includes("soumission")) return <Send size={16} />;
    if (action.includes("retrait")) return <DollarSign size={16} />;
    if (action.includes("bonus") || action.includes("parrainage") || action.includes("recompense")) return <Award size={16} />;
    if (action.includes("rejete")) return <XCircle size={16} />;
    if (action.includes("mot_de_passe")) return <Key size={16} />;
    return <Clock size={16} />;
  };

  const getHistoryIconClass = (action) => {
    if (action.includes("valide") || action.includes("parrainage") || action.includes("approuve") || action.includes("recompense")) return "validated";
    if (action.includes("soumission")) return "submitted";
    if (action.includes("retrait")) return "withdrawal";
    if (action.includes("bonus")) return "bonus";
    if (action.includes("rejete")) return "rejected";
    return "submitted";
  };

  if (!stats) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="dashboard has-sidebar" data-testid="affiliate-dashboard">
      <PromoVideoPopup role="affiliate" />
      <Sidebar
        title="SMARTECH PRO"
        subtitle={`Affilié · ${user?.code || ""}`}
        accent="#2563EB"
        active={activeTab}
        onSelect={setActiveTab}
        onLogout={handleLogout}
        items={[
          { key: "dashboard", label: "Accueil", icon: LayoutDashboard, testid: "tab-dashboard" },
          { key: "my-clients", label: "Mes Filleuls", icon: Users, testid: "tab-aff-my-clients" },
          { key: "leaderboard", label: "Classement", icon: Trophy, testid: "tab-leaderboard" },
          { key: "history", label: "Historique", icon: History, testid: "tab-history" },
          { key: "sol", label: "Sol", icon: Users, testid: "tab-sol" },
          { key: "orders", label: "Commandes", icon: Package, badge: affValidatedOrders.filter(o => o.status === "validated").length, testid: "tab-aff-orders" },
          { key: "client-tx", label: "Recharges clients", icon: Wallet, testid: "tab-aff-client-tx" },
          { key: "crypto", label: "Crypto", icon: Bitcoin, testid: "tab-aff-crypto" },
        ]}
      />
      <div className="sidebar-content">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1 className="header-title">SMARTECH PRO</h1>
          <div className="header-user">
            <NotificationCenter apiCall={apiCall} role="affiliate" />
            <span className="user-code" data-testid="user-code">{user?.code}</span>
            <button onClick={handleLogout} className="btn-logout" data-testid="logout-btn">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Notification Popup */}
      {showNotifications && (
        <NotificationPopup 
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onMarkRead={handleMarkNotificationRead}
        />
      )}

      {/* Reward Popup */}
      {showRewardPopup && (
        <div className="modal-overlay" data-testid="reward-popup">
          <div className="modal-content reward-popup-content">
            <div className="reward-popup-icon">
              <Gift size={32} />
            </div>
            <h3 className="modal-title">{showRewardPopup.title}</h3>
            <p className="modal-message">{showRewardPopup.message}</p>
            <button 
              onClick={() => {
                handleMarkNotificationRead(showRewardPopup.id);
                setShowRewardPopup(null);
                fetchData();
              }} 
              className="btn btn-primary"
              data-testid="reward-popup-close"
            >
              Super!
            </button>
          </div>
        </div>
      )}

      {/* WELCOME WhatsApp Modal — friendly prompt that blocks the dashboard until
          the affiliate provides a valid WhatsApp number. */}
      {affProfile && affProfile.has_whatsapp === false && (
        <div className="modal-overlay" data-testid="aff-whatsapp-required-modal" style={{ zIndex: 9999 }}>
          <div className="modal-content" style={{ maxWidth: 460 }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "0.75rem" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#DCFCE7", color: "#16A34A", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <MessageSquareText size={26} />
              </div>
            </div>
            <h3 className="modal-title" style={{ textAlign: "center" }}>Bienvenue !</h3>
            <p className="modal-message" style={{ textAlign: "center", color: "#475569", fontSize: "0.85rem" }}>
              Pour améliorer votre expérience et faciliter la communication, veuillez ajouter
              votre numéro WhatsApp dans votre profil. Cela nous permettra de vous contacter
              rapidement concernant vos commandes et notifications importantes.
            </p>
            <div style={{ marginTop: "0.75rem" }}>
              <input
                className="form-input"
                placeholder="Ex: +50946261180"
                value={waInput}
                onChange={(e) => setWaInput(e.target.value)}
                data-testid="aff-whatsapp-required-input"
              />
            </div>
            <button
              className="btn btn-primary"
              style={{ width: "100%", marginTop: "0.75rem" }}
              disabled={waSaving}
              data-testid="aff-whatsapp-required-save"
              onClick={async () => {
                const digits = waInput.replace(/[^0-9]/g, "");
                if (digits.length < 8) { toast.error("Min. 8 chiffres"); return; }
                setWaSaving(true);
                try {
                  await apiCall("post", "/affiliate/update-whatsapp", { whatsapp: waInput });
                  toast.success("Numéro WhatsApp enregistré !");
                  const profRes = await apiCall("get", "/affiliate/profile");
                  setAffProfile(profRes.data);
                  setWaInput("");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erreur");
                } finally {
                  setWaSaving(false);
                }
              }}
            >
              {waSaving ? "Enregistrement..." : "Enregistrer mon numéro"}
            </button>
          </div>
        </div>
      )}

      {/* AFFILIATE SELF-RECHARGE MODAL — 0% fee, validated by admin via proof */}
      {showAffRecharge && (
        <div className="modal-overlay" data-testid="aff-recharge-modal" onClick={(e) => { if (e.target === e.currentTarget) setShowAffRecharge(false); }}>
          <div className="modal-content" style={{ maxWidth: 480 }}>
            <h3 className="modal-title"><Wallet size={18} /> Recharger mon solde</h3>
            <p style={{ fontSize: "0.78rem", color: "#16A34A", background: "#F0FDF4", padding: "0.5rem", borderRadius: 8, border: "1px solid #BBF7D0", marginBottom: "0.75rem" }}>
              <strong>0% de frais.</strong> Envoyez le montant via MonCash / NatCash et joignez la preuve.
              Votre solde sera crédité après validation par l'admin.
            </p>
            <div style={{ background: "#EFF6FF", padding: "0.5rem", borderRadius: 8, marginBottom: "0.5rem", fontSize: "0.78rem" }}>
              <div><strong>MonCash :</strong> +50946261180</div>
              <div><strong>NatCash :</strong> +50955460588</div>
              <div style={{ fontSize: "0.68rem", color: "#64748B", marginTop: 4 }}>
                Contact admin : <a href="https://chat.whatsapp.com/ErJCTqoXDZ24WxqGd9LTOv?s=cl&p=a&mlu=0&amv=2" target="_blank" rel="noopener noreferrer" style={{ color: "#2563EB", fontWeight: 700 }}>Groupe service WhatsApp</a>
              </div>
            </div>
            <select className="form-input" value={affRechargeMethod} onChange={(e) => setAffRechargeMethod(e.target.value)} style={{ marginBottom: "0.5rem" }}>
              <option value="MonCash">MonCash</option>
              <option value="NatCash">NatCash</option>
              <option value="Other">Autre</option>
            </select>
            <input
              className="form-input"
              type="number"
              min="50"
              placeholder="Montant en Gdes (min. 50)"
              value={affRechargeAmount}
              onChange={(e) => setAffRechargeAmount(e.target.value)}
              style={{ marginBottom: "0.5rem" }}
              data-testid="aff-recharge-amount"
            />
            <div style={{ border: "2px dashed #CBD5E1", borderRadius: "8px", padding: "0.75rem", textAlign: "center", cursor: "pointer", background: affRechargeProofUrl ? "#F0FDF4" : "#F8FAFC", marginBottom: "0.5rem" }}
              onClick={() => document.getElementById("aff-recharge-proof").click()}>
              <input
                id="aff-recharge-proof"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  setAffRechargeUploading(true);
                  const formData = new FormData();
                  formData.append("file", file);
                  try {
                    const res = await axios.post(`${API}/affiliate/upload-delivery-proof`, formData, {
                      headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" }
                    });
                    setAffRechargeProofUrl(res.data.url);
                    toast.success("Preuve attachée");
                  } catch { toast.error("Erreur upload"); }
                  finally { setAffRechargeUploading(false); }
                }}
              />
              {affRechargeUploading ? "Téléversement..." : affRechargeProofUrl ? <span style={{ color: "#16A34A", fontWeight: 600 }}>Preuve attachée ✓</span> : <span style={{ color: "#64748B", fontSize: "0.8rem" }}>Joindre la capture du paiement</span>}
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button className="btn btn-sm" style={{ background: "#E2E8F0", flex: 1 }} onClick={() => setShowAffRecharge(false)}>Annuler</button>
              <button
                className="btn btn-primary btn-sm"
                style={{ flex: 2 }}
                disabled={affRechargeSubmitting || !affRechargeAmount || !affRechargeProofUrl}
                data-testid="aff-recharge-submit"
                onClick={async () => {
                  const amount = parseFloat(affRechargeAmount);
                  if (!amount || amount < 50) { toast.error("Montant min. 50 Gdes"); return; }
                  if (!affRechargeProofUrl) { toast.error("Preuve requise"); return; }
                  setAffRechargeSubmitting(true);
                  try {
                    const res = await apiCall("post", "/balance/deposit", {
                      amount,
                      proof_url: affRechargeProofUrl,
                      note: `Recharge ${affRechargeMethod}`,
                    });
                    toast.success(res.data.message || "Demande envoyée — en attente de validation admin (0% frais)");
                    setShowAffRecharge(false);
                    setAffRechargeAmount("");
                    setAffRechargeProofUrl("");
                    fetchData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur");
                  } finally {
                    setAffRechargeSubmitting(false);
                  }
                }}
              >
                {affRechargeSubmitting ? "Envoi..." : "Soumettre la demande"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="dashboard-content">
        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card" data-testid="stat-clients">
            <div className="stat-icon clients"><Users size={20} /></div>
            <div className="stat-value">{stats.clients}</div>
            <div className="stat-label">Clients</div>
          </div>
          <div className="stat-card" data-testid="stat-gains">
            <div className="stat-icon gains"><DollarSign size={20} /></div>
            <div className="stat-value" style={{ fontSize: "1.1rem" }}>{formatUSD(stats.gains, exchangeRate)}</div>
            <div className="stat-label">Gains (≈ {formatHTG(stats.gains)})</div>
          </div>
          <div className="stat-card" data-testid="stat-level">
            <div className="stat-icon level"><Award size={20} /></div>
            <div className={`level-badge ${getLevelClass(stats.level)}`}>{stats.level}</div>
            <div className="stat-label">Niveau</div>
          </div>
          <div className="stat-card" data-testid="stat-sponsor">
            <div className="stat-icon sponsor"><UserCheck size={20} /></div>
            <div className="stat-value" style={{ fontSize: "1rem" }}>{stats.sponsor?.code || "\u2014"}</div>
            <div className="stat-label">Parrain</div>
          </div>
        </div>

        {/* Tabs replaced by Sidebar */}

        {/* My Clients (referred) — new reseller flow */}
        {activeTab === "my-clients" && (
          <LazyPanel><AffiliateMyClients apiCall={apiCall} affiliateCode={user?.code} /></LazyPanel>
        )}

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && (
          <>
            {/* Presence toggle */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "0.5rem" }}>
              <AffiliatePresenceToggle apiCall={apiCall} />
            </div>

            {/* Balance widget */}
            <BalanceWidget apiCall={apiCall} API={API} tokenKey="token" role="affiliate" />

            {/* Affiliate self-recharge action — 0% fee */}
            <div style={{ marginBottom: "0.75rem" }}>
              <button
                className="btn btn-primary btn-sm"
                style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                onClick={() => setShowAffRecharge(true)}
                data-testid="aff-recharge-open-btn"
              >
                <Wallet size={16} /> Recharger mon solde (0% frais)
              </button>
            </div>

            {/* Alert for pending orders */}
            {affValidatedOrders.filter(o => o.status === "validated").length > 0 && (
              <div onClick={() => setActiveTab("orders")} style={{ cursor: "pointer", padding: "0.75rem", marginBottom: "1rem", borderRadius: "10px", background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)", border: "2px solid #2563EB", display: "flex", alignItems: "center", gap: "0.6rem" }} data-testid="orders-alert">
                <div style={{ background: "#2563EB", color: "white", borderRadius: "50%", width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "1rem", flexShrink: 0 }}>
                  {affValidatedOrders.filter(o => o.status === "validated").length}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1E40AF" }}>Commandes à traiter</div>
                  <div style={{ fontSize: "0.75rem", color: "#3B82F6" }}>Cliquez ici pour voir les détails et soumettre vos livraisons</div>
                </div>
              </div>
            )}

            {/* Explainer Video */}
            {affVideo && (
              <div className="card" data-testid="affiliate-video">
                <h3 className="card-title"><Play size={18} /> {affVideo.title || "Comment vendre efficacement"}</h3>
                <iframe src={affVideo.video_url.replace("watch?v=", "embed/").replace("youtu.be/", "youtube.com/embed/").replace("vimeo.com/", "player.vimeo.com/video/")}
                  style={{ width: "100%", height: "220px", border: "none", borderRadius: "8px" }} allowFullScreen title="Guide affilié" />
              </div>
            )}

            {/* Email notification prompt */}
            {user && !user.notification_email && (
              <div style={{ padding: "0.6rem", marginBottom: "0.75rem", borderRadius: "10px", background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1E40AF", marginBottom: "0.3rem" }}>Recevez vos commissions et notifications par email!</div>
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <input className="form-input" style={{ fontSize: "0.78rem", flex: 1 }} placeholder="Votre email" data-testid="aff-notif-email" id="aff-notif-email" />
                  <button className="btn btn-primary btn-sm" onClick={async () => {
                    const email = document.getElementById("aff-notif-email").value;
                    if (!email.trim()) return;
                    try { await apiCall("post", "/affiliate/update-notification-email", { notification_email: email.trim() }); toast.success("Email enregistré!"); fetchData(); } catch { toast.error("Erreur"); }
                  }}>OK</button>
                </div>
              </div>
            )}

            {/* Submit Client form removed — new affiliate model: clients have their own
                accounts and buy services themselves. Affiliates create accounts via
                the "Mes Filleuls" tab and can only recharge their balance. */}
            <div className="card" data-testid="affiliate-new-model-notice">
              <h3 className="card-title">
                <Users size={18} /> Programme affilié — Nouvelle politique
              </h3>
              <p style={{ color: "#475569", fontSize: "0.86rem", lineHeight: 1.5 }}>
                Les achats de services sont désormais effectués <strong>directement par les clients</strong> depuis leur propre compte.
                Votre rôle d'affilié est de <strong>créer des comptes</strong> pour vos filleuls et de <strong>recharger leur solde</strong>.
                Vous touchez automatiquement <strong>2,5 G de commission par achat</strong> et une <strong>prime de 5 G au 1<sup>er</sup> achat</strong> de chaque filleul.
              </p>
              <button
                type="button"
                onClick={() => setActiveTab("my-clients")}
                className="btn btn-primary"
                data-testid="goto-my-clients-btn"
                style={{ marginTop: "0.6rem" }}
              >
                <Users size={16} /> Gérer mes filleuls
              </button>
            </div>

            {/* Request Withdrawal */}
            <div className="card">
              <h3 className="card-title">
                <Wallet size={18} /> Demander un retrait
              </h3>
              <p style={{ color: "#64748B", marginBottom: "0.5rem", fontSize: "0.875rem" }}>
                Solde disponible: <strong>{formatUSD(stats.gains, exchangeRate)}</strong>
                <span style={{ fontSize: "0.7rem", color: "#94A3B8", marginLeft: 6 }}>(≈ {formatHTG(stats.gains)})</span>
              </p>
              <p style={{ color: "#94A3B8", marginBottom: "1rem", fontSize: "0.75rem" }}>
                Minimum: {formatUSD(minWithdrawal, exchangeRate)} (≈ {minWithdrawal} HTG)
              </p>
              <div className="withdrawal-form">
                <input
                  type="number"
                  className="form-input"
                  placeholder={`Montant (min ${minWithdrawal} Gdes)`}
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  min={minWithdrawal}
                  step="0.01"
                  data-testid="withdrawal-amount-input"
                />
                <input
                  type="tel"
                  className="form-input"
                  placeholder="Numéro de paiement (requis)"
                  value={paymentPhone}
                  onChange={(e) => setPaymentPhone(e.target.value)}
                  data-testid="payment-phone-input"
                />
                <button 
                  onClick={handleRequestWithdrawal}
                  className="btn btn-whatsapp"
                  disabled={stats.gains < minWithdrawal}
                  data-testid="request-withdrawal-btn"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Demander
                </button>
              </div>
            </div>


            {/* Available Bonus */}
            {availablePromos.length > 0 && (
              <div className="card promo-card" data-testid="promo-card">
                <h3 className="card-title"><Tag size={18} /> Bonus disponibles</h3>
                {availablePromos.map(promo => (
                  <div key={promo.id} className="promo-item" data-testid={`promo-item-${promo.id}`}>
                    <div className="promo-info">
                      <div className="promo-pct">{promo.discount_percent}%</div>
                      <div className="promo-desc">
                        {promo.already_activated ? "Bonus active pour vos clients" : `Bonus de ${promo.discount_percent}% disponible - Activez-le pour vos clients`}
                        <div style={{ fontSize: "0.65rem", color: "#94A3B8", marginTop: "0.15rem" }}>Utilisable une seule fois par client</div>
                      </div>
                    </div>
                    {!promo.already_activated ? (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleActivatePromo(promo.id)}
                        disabled={activatingPromo}
                        data-testid={`activate-promo-${promo.id}`}
                      >
                        Activer le bonus
                      </button>
                    ) : (
                      <span className="promo-activated-badge"><CheckCircle size={14} /> Active</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Create Subscription */}
            <div className="card" data-testid="affiliate-create-subscription">
              <h3 className="card-title"><CalendarDays size={18} /> Créer un abonnement</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                if (!affSubPhone.trim() || !affSubName.trim() || !affSubDays) return;
                // Email streaming is now optional — admin can attach later.
                const isCash = affSubPaymentMethod === "cash";
                if (!isCash && !affSubProofUrl) { toast.error("Preuve de paiement obligatoire (sauf paiement cash)"); return; }
                setAffCreatingSub(true);
                try {
                  const res = await apiCall("post", "/affiliate/create-subscription", {
                    client_phone: affSubPhone.trim(),
                    client_name: affSubName.trim(),
                    service_name: affSubService,
                    duration_days: parseInt(affSubDays),
                    streaming_email: affSubEmail.trim() ? affSubEmail.trim().toLowerCase() : "",
                    payment_proof_url: isCash ? null : affSubProofUrl,
                    payment_method: affSubPaymentMethod,
                  });
                  toast.success(res.data.message);
                  setAffSubPhone(""); setAffSubName(""); setAffSubDays(""); setAffSubService("Netflix");
                  setAffSubEmail(""); setAffSubProofUrl(""); setAffSubPaymentMethod("qr");
                  fetchData();
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Erreur");
                } finally {
                  setAffCreatingSub(false);
                }
              }} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <input type="tel" className="form-input" placeholder="Numéro du client" value={affSubPhone} onChange={e => setAffSubPhone(e.target.value)} required data-testid="aff-sub-phone" />
                <input type="text" className="form-input" placeholder="Nom du client" value={affSubName} onChange={e => setAffSubName(e.target.value)} required data-testid="aff-sub-name" />
                <select className="form-input" value={affSubService} onChange={e => setAffSubService(e.target.value)} data-testid="aff-sub-service">
                  <option value="Netflix">Netflix</option>
                  <option value="Prime Video">Prime Video</option>
                </select>
                <input type="number" className="form-input" placeholder="Durée (en jours)" value={affSubDays} onChange={e => setAffSubDays(e.target.value)} min="1" required data-testid="aff-sub-days" />
                <input type="email" className="form-input" placeholder="Email streaming (optionnel — l'admin pourra l'attribuer plus tard)" value={affSubEmail} onChange={e => setAffSubEmail(e.target.value)} data-testid="aff-sub-streaming-email" />
                <div style={{ display: "flex", gap: "0.3rem" }}>
                  <button type="button" onClick={() => setAffSubPaymentMethod("qr")} className="btn btn-sm" data-testid="aff-sub-pay-qr"
                    style={{ background: affSubPaymentMethod === "qr" ? "#2563EB" : "#E2E8F0", color: affSubPaymentMethod === "qr" ? "white" : "#0F172A", flex: 1 }}>
                    QR/Capture
                  </button>
                  <button type="button" onClick={() => setAffSubPaymentMethod("cash")} className="btn btn-sm" data-testid="aff-sub-pay-cash"
                    style={{ background: affSubPaymentMethod === "cash" ? "#16A34A" : "#E2E8F0", color: affSubPaymentMethod === "cash" ? "white" : "#0F172A", flex: 1 }}>
                    Cash (espèces)
                  </button>
                </div>
                {affSubPaymentMethod === "cash" ? (
                  <div style={{ padding: "0.5rem", background: "#F0FDF4", borderRadius: "8px", fontSize: "0.72rem", color: "#14532D" }}>
                    Paiement en espèces — le client vous paie directement. Aucune preuve requise.
                  </div>
                ) : (
                  <label style={{ display: "block", padding: "0.55rem", border: "2px dashed #CBD5E1", borderRadius: "8px", textAlign: "center", cursor: "pointer", fontSize: "0.75rem", color: "#475569", background: affSubProofUrl ? "#F0FDF4" : "#F8FAFC" }} data-testid="aff-sub-proof-label">
                    <Upload size={13} style={{ verticalAlign: "middle" }} />{" "}
                    {affSubProofUploading ? "Envoi..." : affSubProofUrl ? "Preuve jointe ✓ (cliquez pour remplacer)" : "Téléverser la preuve de paiement (reçu)"}
                    <input type="file" accept="image/*" style={{ display: "none" }} data-testid="aff-sub-proof-file"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        setAffSubProofUploading(true);
                        try {
                          const data = await uploadPaymentProof(f, { role: "affiliate", tokenKey: "token" });
                          setAffSubProofUrl(data.url);
                          toast.success("Preuve téléversée!");
                        } catch (err) {
                          toast.error(err.message || "Erreur lors du téléversement", { duration: 6000 });
                        } finally {
                          setAffSubProofUploading(false);
                          try { e.target.value = ""; } catch (_) { /* noop */ }
                        }
                      }} />
                  </label>
                )}
                <div style={{ fontSize: "0.7rem", color: "#64748B" }}>
                  Le client sera ajouté automatiquement au compte streaming correspondant.
                </div>
                <button type="submit" className="btn btn-primary" disabled={affCreatingSub} data-testid="aff-sub-create-btn">
                  {affCreatingSub ? "Envoi..." : <><Send size={16} /> Soumettre l'abonnement</>}
                </button>
              </form>

              {/* Post-creation CTA: send the client a WhatsApp invite so they
                  can set their password and access their dashboard. */}
              {affLastSubResult && (
                <div
                  data-testid="aff-sub-whatsapp-banner"
                  style={{
                    marginTop: "0.7rem",
                    background: "#F0FDF4",
                    border: "2px solid #16A34A",
                    borderRadius: 10,
                    padding: "0.7rem 0.85rem",
                  }}
                >
                  <div style={{ fontSize: "0.82rem", fontWeight: 800, color: "#14532D", marginBottom: 4 }}>
                    ✅ Abonnement {affLastSubResult.service_name} créé pour {affLastSubResult.client_name}
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "#166534", marginBottom: 8 }}>
                    {affLastSubResult.client_created
                      ? "Un compte client a été créé. Envoyez le lien WhatsApp pour qu'il définisse son mot de passe."
                      : "Le compte client existait déjà. Vous pouvez l'inviter à se connecter."}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {affLastSubResult.whatsapp_url && (
                      <a
                        href={affLastSubResult.whatsapp_url}
                        target="_blank" rel="noreferrer"
                        data-testid="aff-sub-whatsapp-btn"
                        className="btn btn-whatsapp"
                        style={{ padding: "0.45rem 0.95rem", fontSize: "0.82rem", fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, background: "#16A34A", color: "#FFF", borderRadius: 8 }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        Envoyer WhatsApp au client
                      </a>
                    )}
                    {affLastSubResult.activation_url && (
                      <button
                        type="button"
                        data-testid="aff-sub-copy-link"
                        onClick={async () => {
                          try { await navigator.clipboard.writeText(affLastSubResult.activation_url); toast.success("Lien copié"); }
                          catch { toast.error("Impossible de copier"); }
                        }}
                        className="btn btn-sm"
                        style={{ background: "#E0F2FE", color: "#075985", padding: "0.45rem 0.85rem", fontSize: "0.78rem", fontWeight: 700, border: "1px solid #BAE6FD", borderRadius: 8 }}
                      >📋 Copier le lien</button>
                    )}
                    <button
                      type="button"
                      onClick={() => setAffLastSubResult(null)}
                      className="btn btn-sm"
                      style={{ background: "transparent", color: "#475569", padding: "0.45rem 0.7rem", fontSize: "0.78rem" }}
                      data-testid="aff-sub-dismiss-banner"
                    >Fermer</button>
                  </div>
                </div>
              )}
            </div>

            {/* Client Subscriptions */}
            <div className="card" data-testid="affiliate-subscriptions">
              <h3 className="card-title"><CalendarDays size={18} /> Abonnements de vos clients</h3>
              {clientSubs.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><CalendarDays size={24} /></div>
                  <div className="empty-state-text">Aucun abonnement soumis</div>
                </div>
              ) : (
                <>
                  {/* Status summary + search — counts restricted to Netflix/Prime only. */}
                  {(() => {
                    const ALLOWED = new Set(["netflix", "prime video", "prime", "prime_video"]);
                    const visible = clientSubs.filter(s => ALLOWED.has((s.service_name || "").trim().toLowerCase()));
                    const fmtCount = (st) => visible.filter(s => s.status === st).length;
                    const soonCount = visible.filter(s => s.status === "active" && s.hours_left > 0 && s.hours_left <= 48).length;
                    return (
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#FEF3C7", color: "#92400E" }} data-testid="aff-subs-count-pending">
                      En attente: {fmtCount("pending")}
                    </span>
                    <span style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#DCFCE7", color: "#16A34A" }} data-testid="aff-subs-count-active">
                      Actifs: {fmtCount("active")}
                    </span>
                    <span style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#FEE2E2", color: "#DC2626" }} data-testid="aff-subs-count-expired">
                      Expirés: {fmtCount("expired")}
                    </span>
                    <span style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#FEF3C7", color: "#B45309", border: "1px solid #FBBF24" }} data-testid="aff-subs-count-soon">
                      ⏰ Bientôt: {soonCount}
                    </span>
                  </div>
                    );
                  })()}
                  <input
                    type="search"
                    placeholder="Rechercher un client (nom, téléphone, service)…"
                    value={affSubSearch || ""}
                    onChange={e => setAffSubSearch(e.target.value)}
                    className="form-input"
                    style={{ fontSize: "0.78rem", padding: "0.4rem 0.55rem", marginBottom: "0.5rem" }}
                    data-testid="aff-subs-search"
                  />
                  {(() => {
                    // Restrict the Outils Abonnements panel to Netflix + Prime Video only.
                    const ALLOWED_SERVICES = new Set(["netflix", "prime video", "prime", "prime_video"]);
                    const eligible = clientSubs.filter(s =>
                      ALLOWED_SERVICES.has((s.service_name || "").trim().toLowerCase())
                    );
                    const q = (affSubSearch || "").trim().toLowerCase();
                    const filtered = eligible.filter(s => !q || (
                      (s.client_name || "").toLowerCase().includes(q) ||
                      (s.client_phone || "").toLowerCase().includes(q) ||
                      (s.service_name || "").toLowerCase().includes(q) ||
                      (s.plan_name || "").toLowerCase().includes(q)
                    ));
                    if (filtered.length === 0) {
                      return <div className="empty-state-text" style={{ textAlign: "center", padding: "1rem", color: "#94A3B8", fontSize: "0.78rem" }}>Aucun abonnement ne correspond à votre recherche</div>;
                    }
                    return filtered.map((sub) => {
                    const isPending = sub.status === "pending";
                    const isExpired = sub.status === "expired";
                    const daysLeft = sub.days_left ?? 0;
                    const hoursLeft = sub.hours_left ?? 0;
                    const progressPct = sub.progress_pct ?? 0;
                    const expiringSoon = !isPending && !isExpired && hoursLeft > 0 && hoursLeft <= 48;
                    const bgColor = isPending ? "#FFFBEB" : isExpired ? "#FFF5F5" : (expiringSoon ? "#FFFBEB" : "#F0FDF4");
                    const borderColor = isPending ? "#FDE68A" : isExpired ? "#FECACA" : (expiringSoon ? "#FBBF24" : "#BBF7D0");
                    const barColor = isExpired ? "#DC2626" : (expiringSoon ? "#F59E0B" : "#16A34A");
                    return (
                      <div key={sub.id} style={{ padding: "0.6rem", marginBottom: "0.5rem", borderRadius: "8px", background: bgColor, border: `1px solid ${borderColor}` }} data-testid={`aff-sub-${sub.id}`}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{sub.client_name} <span style={{ fontWeight: 400, color: "#64748B", fontSize: "0.75rem" }}>({sub.client_phone})</span></div>
                            <div style={{ fontSize: "0.78rem", color: "#475569", marginTop: "0.2rem" }}>
                              {sub.service_name} {sub.plan_name && `· ${sub.plan_name}`} | {sub.duration_days} jour(s)
                            </div>
                          </div>
                          <span style={{ padding: "0.1rem 0.4rem", borderRadius: "999px", fontSize: "0.65rem", fontWeight: 600, background: isPending ? "#FEF3C7" : isExpired ? "#FEE2E2" : (expiringSoon ? "#FEF3C7" : "#DCFCE7"), color: isPending ? "#92400E" : isExpired ? "#DC2626" : (expiringSoon ? "#B45309" : "#16A34A"), whiteSpace: "nowrap" }}>
                            {isPending ? "En attente de validation" : isExpired ? "Expiré" : (
                              hoursLeft <= 24
                                ? `Actif (${hoursLeft}h restantes)`
                                : `Actif (${daysLeft}j restants)`
                            )}
                          </span>
                        </div>

                        {/* Real-time progress bar (active subs only) */}
                        {!isPending && (
                          <div style={{ marginTop: "0.45rem" }}>
                            <div style={{ height: 6, background: "#FFF", borderRadius: 999, overflow: "hidden", border: "1px solid #E2E8F0" }}>
                              <div style={{ width: `${progressPct}%`, height: "100%", background: barColor, transition: "width 0.4s ease" }} />
                            </div>
                            <div style={{ fontSize: "0.62rem", color: "#64748B", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                              <span>Début: {sub.start_date ? formatDate(sub.start_date) : "—"}</span>
                              <span>Fin: {sub.end_date ? formatDate(sub.end_date) : "—"}</span>
                            </div>
                          </div>
                        )}

                        {/* Action row: WhatsApp + Notify + Renew */}
                        <div style={{ display: "flex", gap: "0.3rem", marginTop: "0.45rem", flexWrap: "wrap", alignItems: "center" }}>
                          {sub.whatsapp_url && (
                            <a
                              href={sub.whatsapp_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "#25D366", color: "white", border: "none", borderRadius: "6px", padding: "0.25rem 0.5rem", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer", textDecoration: "none" }}
                              data-testid={`aff-sub-wa-${sub.id}`}
                            >
                              <MessageSquareText size={12} /> WhatsApp
                            </a>
                          )}
                          {!isPending && (
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const kind = isExpired ? "expired" : "expiring";
                                  await apiCall("post", `/affiliate/notify-subscription/${sub.id}`, { kind });
                                  toast.success("Rappel envoyé au client");
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Erreur");
                                }
                              }}
                              style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: expiringSoon || isExpired ? "#F59E0B" : "#2563EB", color: "white", border: "none", borderRadius: "6px", padding: "0.25rem 0.5rem", fontSize: "0.7rem", fontWeight: 600, cursor: "pointer" }}
                              data-testid={`aff-sub-notify-${sub.id}`}
                            >
                              <Bell size={12} /> Envoyer rappel
                            </button>
                          )}
                          {isExpired && (
                            <>
                              <input type="number" min="1" placeholder="Jours" style={{ width: 55, fontSize: "0.7rem", padding: "0.25rem 0.35rem", borderRadius: "5px", border: "1px solid #CBD5E1" }}
                                data-testid={`aff-renew-days-${sub.id}`} />
                              <button className="btn btn-sm" style={{ background: "#1E40AF", color: "white", fontSize: "0.7rem", padding: "0.25rem 0.5rem", borderRadius: "6px", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.2rem" }}
                                data-testid={`aff-renew-${sub.id}`}
                                onClick={async () => {
                                  const days = parseInt(document.querySelector(`[data-testid="aff-renew-days-${sub.id}"]`).value);
                                  if (!days || days < 1) { toast.error("Entrez le nombre de jours"); return; }
                                  try {
                                    const res = await apiCall("post", `/affiliate/renew-subscription/${sub.id}`, { duration_days: days });
                                    toast.success(res.data.message);
                                    fetchData();
                                  } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                                }}>
                                <RotateCcw size={11} /> Renouveler
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                    });
                  })()}
                </>
              )}
            </div>

            {/* Streaming Connections for Affiliate */}
            {affStreamConns.length > 0 && (
              <div className="card" data-testid="affiliate-streaming">
                <h3 className="card-title"><Tv size={18} /> Connexion Streaming</h3>
                {affStreamConns.map(conn => (
                  <div key={conn.id} style={{ padding: "0.5rem", marginBottom: "0.4rem", borderRadius: "8px", background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1E293B", wordBreak: "break-all" }}>{conn.email}</div>
                      <button style={{ background: "#2563EB", color: "white", border: "none", borderRadius: "6px", padding: "0.15rem 0.4rem", cursor: "pointer", fontSize: "0.6rem", whiteSpace: "nowrap" }}
                        onClick={() => { 
                          const all = `${conn.email}\nÉcrans: ${conn.profiles.join(", ")}${conn.vip_profiles?.length ? `\nVIP: ${conn.vip_profiles.join(", ")}` : ""}`;
                          navigator.clipboard.writeText(all); toast.success("Tout copié!"); 
                        }}>
                        Copier tout
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginTop: "0.2rem" }}>
                      {conn.profiles.map((p, i) => (
                        <span key={`${p}-${i}`} style={{ padding: "0.15rem 0.4rem", borderRadius: "999px", fontSize: "0.7rem", background: "#DBEAFE", color: "#1E40AF", fontWeight: 500 }}>{p}</span>
                      ))}
                    </div>
                    {conn.vip_profiles && conn.vip_profiles.length > 0 && (
                      <div style={{ marginTop: "0.25rem" }}>
                        <span style={{ fontSize: "0.6rem", fontWeight: 700, color: "#D97706" }}>VIP:</span>
                        <div style={{ display: "flex", gap: "0.25rem", flexWrap: "wrap", marginTop: "0.1rem" }}>
                          {conn.vip_profiles.map((p, i) => (
                            <span key={`vip-${p}-${i}`} style={{ padding: "0.15rem 0.4rem", borderRadius: "999px", fontSize: "0.7rem", background: "#FEF3C7", color: "#92400E", fontWeight: 500 }}>{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Bonus Info */}
            <div className="card rewards-info">
              <h3 className="card-title">
                <Star size={18} /> Barème des Commissions
              </h3>
              <div className="commission-grid">
                <div className="commission-item vip-item">
                  <div className="commission-header">
                    <Crown size={20} />
                    <span>VIP Mensuel</span>
                  </div>
                  <div className="commission-amount">100 Gdes</div>
                  <div className="commission-desc">Netflix ou Prime Video</div>
                </div>
                <div className="commission-item daily-item">
                  <div className="commission-header">
                    <Clock size={20} />
                    <span>Quotidien</span>
                  </div>
                  <div className="commission-amount">2.5 Gdes</div>
                  <div className="commission-desc">Netflix ou Prime Video</div>
                </div>
              </div>
              <div className="rewards-grid" style={{ marginTop: "1rem" }}>
                <div className="reward-item top1">
                  <Crown size={24} />
                  <div className="reward-title">TOP 1</div>
                  <div className="reward-amount">500 Gdes</div>
                  <div className="reward-condition">Min. 100 clients/mois</div>
                </div>
                <div className="reward-item top2">
                  <Medal size={24} />
                  <div className="reward-title">TOP 2</div>
                  <div className="reward-amount">250 Gdes</div>
                  <div className="reward-condition">Min. 50 clients/mois</div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <Leaderboard leaderboard={leaderboard} />
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="card">
            <h3 className="card-title">
              <History size={18} /> Historique des actions
            </h3>
            {history.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-text">Aucune activité</div>
              </div>
            ) : (
              <div className="history-list" data-testid="history-list">
                {history.map((item, idx) => (
                  <div key={item._id || item.id || `${item.timestamp}-${idx}`} className="history-item">
                    <div className={`history-icon ${getHistoryIconClass(item.action)}`}>
                      {getHistoryIcon(item.action)}
                    </div>
                    <div className="history-content">
                      <div className="history-action">{item.action.replace(/_/g, " ")}</div>
                      <div className="history-details">{item.details}</div>
                      <div className="history-time">{formatDate(item.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sol Tab */}
        {activeTab === "sol" && (
          <>
            {!solStatus?.active ? (
              <div className="card" data-testid="sol-inactive">
                <div className="empty-state">
                  <div className="empty-state-icon"><DollarSign size={24} /></div>
                  <div className="empty-state-text">Aucun Sol actif pour le moment</div>
                  <div className="empty-state-sub">L'administrateur lancera bientot un nouveau cycle.</div>
                </div>
              </div>
            ) : (
              <>
                {/* Sol Status Card */}
                <div className="card" data-testid="sol-status-card">
                  <h3 className="card-title"><DollarSign size={18} /> Sol SMARTECH</h3>
                  {solStatus.is_participant && !solStatus.paid_today && (
                    <div style={{ padding: "0.6rem 0.75rem", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem" }} data-testid="sol-reminder-banner">
                      <Bell size={16} style={{ color: "#2563EB", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.8rem", color: "#1E40AF", fontWeight: 600 }}>N'oubliez pas de payer votre cotisation de {solStatus.amount_per_day} Gdes aujourd'hui!</span>
                    </div>
                  )}
                  <div className="stats-grid" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
                    <div className="stat-card">
                      <div className="stat-value">{solStatus.amount_per_day} Gdes</div>
                      <div className="stat-label">Cotisation/jour</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{solStatus.total_collected?.toLocaleString()} Gdes</div>
                      <div className="stat-label">Cagnotte actuelle</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{solStatus.position || "-"}/{solStatus.total_participants}</div>
                      <div className="stat-label">Ma position</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{solStatus.current_round || 1}</div>
                      <div className="stat-label">Tour actuel</div>
                    </div>
                  </div>
                  {solStatus.next_distribution_date && (
                    <div style={{ marginTop: "0.75rem", padding: "0.6rem", background: "#EFF6FF", borderRadius: "8px", textAlign: "center" }}>
                      <div style={{ fontSize: "0.75rem", color: "#475569" }}>Prochaine distribution</div>
                      <div style={{ fontSize: "1rem", fontWeight: 700, color: "#2563EB" }}>{new Date(solStatus.next_distribution_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>
                      {solStatus.current_beneficiary && <div style={{ fontSize: "0.75rem", color: "#475569", marginTop: "0.25rem" }}>Beneficiaire: <strong>{solStatus.current_beneficiary}</strong></div>}
                    </div>
                  )}
                </div>

                {/* Join or Pay */}
                {!solStatus.is_participant ? (
                  <div className="card" data-testid="sol-join-card">
                    <h3 className="card-title">Rejoindre le Sol</h3>
                    <p style={{ fontSize: "0.85rem", color: "#475569", marginBottom: "0.75rem" }}>
                      Participez au Sol et recevez la cagnotte a votre tour! Cotisation: {solStatus.amount_per_day} Gdes/jour.
                    </p>
                    <button className="btn btn-primary" data-testid="sol-join-btn"
                      onClick={async () => {
                        try { await apiCall("post", "/affiliate/sol/join"); toast.success("Vous avez rejoint le Sol!"); fetchData(); }
                        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}>
                      Rejoindre le Sol
                    </button>
                  </div>
                ) : (
                  <div className="card" data-testid="sol-pay-card">
                    <h3 className="card-title">Payer ma cotisation</h3>
                    {solStatus.paid_today ? (
                      <div style={{ padding: "0.75rem", background: "#F0FDF4", borderRadius: "8px", textAlign: "center", color: "#16A34A", fontWeight: 600, fontSize: "0.85rem" }}>
                        Vous avez deja paye aujourd'hui
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                        <div style={{ fontSize: "0.85rem", color: "#475569" }}>Montant: <strong>{solStatus.amount_per_day} Gdes</strong></div>
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button className={`btn btn-sm ${solPayMethod === "balance" ? "btn-primary" : "btn-outline"}`}
                            onClick={() => setSolPayMethod("balance")} data-testid="sol-pay-balance">Balance</button>
                          <button className={`btn btn-sm ${solPayMethod === "qr" ? "btn-primary" : "btn-outline"}`}
                            onClick={() => setSolPayMethod("qr")} data-testid="sol-pay-qr">QR Code</button>
                        </div>
                        {solPayMethod === "qr" && (
                          <>
                            <div style={{ display: "flex", gap: "0.5rem", overflowX: "auto", padding: "0.25rem 0" }}>
                              {[
                                { img: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/7b1bzifc_IMG-20260320-WA0055%282%29.jpg", name: "MonCash", num: "46261180" },
                                { img: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/6vevjn52_IMG-20260320-WA0054%282%29.jpg", name: "Digicel", num: "50955460588" },
                                { img: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/59klzhb1_IMG-20260320-WA0053%283%29.jpg", name: "MonCash QR", num: "+50947096761" }
                              ].map((qr, i) => (
                                <div key={qr.num || qr.name} style={{ flex: "0 0 130px", borderRadius: 8, border: "1px solid #E2E8F0", overflow: "hidden" }}>
                                  <img src={qr.img} alt={qr.name} style={{ width: "100%", display: "block" }} />
                                  <div style={{ padding: "0.25rem", textAlign: "center", fontSize: "0.65rem" }}>
                                    <strong>{qr.name}</strong><br/><span style={{ color: "#2563EB" }}>{qr.num}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <input type="text" className="form-input" placeholder="ID de transaction"
                              value={solTxId} onChange={e => setSolTxId(e.target.value)} data-testid="sol-tx-id" />
                          </>
                        )}
                        <button className="btn btn-primary" disabled={solPaying || (solPayMethod === "qr" && !solTxId.trim())}
                          data-testid="sol-pay-btn"
                          onClick={async () => {
                            setSolPaying(true);
                            try {
                              const res = await apiCall("post", "/affiliate/sol/pay", { payment_method: solPayMethod, transaction_id: solPayMethod === "qr" ? solTxId : null });
                              toast.success(res.data.message);
                              setSolTxId("");
                              fetchData();
                            } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            finally { setSolPaying(false); }
                          }}>
                          {solPaying ? "Envoi..." : `Payer ${solStatus.amount_per_day} Gdes`}
                        </button>
                        <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>
                          Paiements valides: {solStatus.my_payments_validated} | En attente: {solStatus.my_payments_pending}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Ranking */}
                {solStatus.ranking && solStatus.ranking.length > 0 && (
                  <div className="card" data-testid="sol-ranking">
                    <h3 className="card-title"><Trophy size={18} /> Classement Sol</h3>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                      {solStatus.ranking.map((r, i) => (
                        <div key={r.code || `pos-${i}`} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0.6rem", background: r.is_me ? "#EFF6FF" : "#F8FAFC", borderRadius: 8, border: r.is_me ? "1.5px solid #2563EB" : "1px solid #E2E8F0" }}>
                          <span style={{ width: 24, height: 24, borderRadius: "50%", background: i === 0 ? "#2563EB" : "#E2E8F0", color: i === 0 ? "white" : "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700 }}>{r.position}</span>
                          <span style={{ flex: 1, fontWeight: r.is_me ? 700 : 500, fontSize: "0.85rem", color: r.is_me ? "#2563EB" : "#0F172A" }}>{r.code}{r.is_me ? " (vous)" : ""}</span>
                          <span style={{ fontSize: "0.75rem", color: "#475569" }}>{r.clients} clients</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sol History */}
                {solHistory && (
                  <div className="card" data-testid="sol-history">
                    <h3 className="card-title"><History size={18} /> Historique Sol</h3>
                    {solHistory.distributions.length > 0 && (
                      <>
                        <h4 style={{ fontSize: "0.8rem", fontWeight: 600, color: "#16A34A", marginBottom: "0.35rem" }}>Distributions recues</h4>
                        {solHistory.distributions.map((d, i) => (
                          <div key={`dist-${d.round_number || i}`} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #F1F5F9", fontSize: "0.8rem" }}>
                            <span>Tour {d.round_number}</span>
                            <span style={{ fontWeight: 700, color: "#16A34A" }}>+{d.amount} Gdes</span>
                            <span style={{ color: "#94A3B8", fontSize: "0.7rem" }}>{formatDate(d.distributed_at)}</span>
                          </div>
                        ))}
                      </>
                    )}
                    {solHistory.payments.length > 0 && (
                      <>
                        <h4 style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569", marginTop: "0.75rem", marginBottom: "0.35rem" }}>Mes paiements</h4>
                        {solHistory.payments.slice(0, 15).map((p, i) => (
                          <div key={`pay-${p.payment_date || ''}-${i}`} style={{ display: "flex", justifyContent: "space-between", padding: "0.35rem 0", borderBottom: "1px solid #F1F5F9", fontSize: "0.75rem" }}>
                            <span>{p.payment_date}</span>
                            <span>{p.amount} Gdes ({p.payment_method})</span>
                            <span className={`badge ${p.status === "validated" ? "badge-success" : p.status === "pending" ? "badge-warning" : "badge-danger"}`}>{p.status === "validated" ? "Valide" : p.status === "pending" ? "En attente" : "Refuse"}</span>
                          </div>
                        ))}
                      </>
                    )}
                    {solHistory.payments.length === 0 && solHistory.distributions.length === 0 && (
                      <div className="empty-state"><div className="empty-state-text">Aucun historique Sol</div></div>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Affiliate Orders Tab */}
        {activeTab === "orders" && (
          <div className="card" data-testid="aff-orders-tab">
            <h3 className="card-title"><ShoppingCart size={18} /> Commandes</h3>
            {/* Filter */}
            <div style={{ display: "flex", gap: "0.3rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
              {[["all","Toutes"],["validated","À traiter"],["pending_delivery","En attente"],["completed","Complétées"]].map(([val, lbl]) => (
                <button key={val} className="btn btn-sm" style={{ background: affOrderFilter === val ? "#2563EB" : "#F1F5F9", color: affOrderFilter === val ? "#FFF" : "#475569", fontSize: "0.72rem", padding: "0.2rem 0.5rem", borderRadius: "999px" }}
                  onClick={() => setAffOrderFilter(val)}>{lbl} ({affValidatedOrders.filter(o => val === "all" || o.status === val).length})</button>
              ))}
            </div>
            {(() => {
              const filtered = affValidatedOrders.filter(o => affOrderFilter === "all" || o.status === affOrderFilter);
              return filtered.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><ShoppingCart size={24} /></div>
                <div className="empty-state-text">Aucune commande</div>
              </div>
            ) : (
              <div>
                {filtered.map((order) => (
                  <div key={order.id} style={{ padding: "0.75rem", marginBottom: "0.75rem", borderRadius: "10px", background: order.affiliate_validated ? "#F0FDF4" : (order.is_open ? "#FFFBEB" : "#FFF"), border: `1px solid ${order.affiliate_validated ? "#BBF7D0" : (order.is_open ? "#FCD34D" : "#E2E8F0")}` }} data-testid={`aff-order-${order.id}`}>
                    {/* Claim badges */}
                    {order.is_open && (
                      <div style={{ background: "#F59E0B", color: "#FFF", padding: "0.15rem 0.5rem", borderRadius: 999, display: "inline-block", fontSize: "0.65rem", fontWeight: 700, marginBottom: 6 }}>
                        DISPONIBLE — première personne qui clique sur WhatsApp prend la commande
                      </div>
                    )}
                    {order.is_mine && order.claimed_by && (
                      <div style={{ background: "#10B981", color: "#FFF", padding: "0.15rem 0.5rem", borderRadius: 999, display: "inline-block", fontSize: "0.65rem", fontWeight: 700, marginBottom: 6 }}>
                        ✓ Vous avez pris cette commande
                      </div>
                    )}
                    {order.referrer_code && order.referrer_code !== user?.code && (
                      <div style={{ fontSize: "0.65rem", color: "#64748B", marginBottom: 4 }}>
                        Parrain : <strong>{order.referrer_code}</strong> · vous toucherez 50% de la commission
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: "0.9rem" }}>{order.client_name}</div>
                        <div style={{ fontSize: "0.78rem", color: "#475569", display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          {order.client_phone}
                          <button onClick={async () => {
                            try {
                              const res = await apiCall("post", `/affiliate/claim-order/${order.id}`);
                              toast.success("Commande prise en charge");
                              if (res.data?.whatsapp_url) {
                                window.open(res.data.whatsapp_url, "_blank");
                              }
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Erreur");
                              fetchData();
                            }
                          }}
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.2rem", background: "#25D366", color: "white", border: "none", borderRadius: "6px", padding: "0.15rem 0.4rem", fontSize: "0.65rem", fontWeight: 600, cursor: "pointer" }}
                            data-testid={`wa-client-${order.id}`}>
                            <MessageSquareText size={12} /> WhatsApp
                          </button>
                        </div>
                      </div>
                      <span style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", fontWeight: 600, background: order.status === "completed" ? "#DCFCE7" : order.status === "pending_delivery" ? "#FEF3C7" : "#DBEAFE", color: order.status === "completed" ? "#16A34A" : order.status === "pending_delivery" ? "#92400E" : "#1E40AF" }}>
                        {order.status === "completed" ? "Livré" : order.status === "pending_delivery" ? "En attente admin" : "À traiter"}
                      </span>
                    </div>
                    <div style={{ fontSize: "0.8rem", color: "#1E293B", marginTop: "0.3rem" }}>
                      {order.service_name} - {order.plan_name} | <strong>{formatUSD(order.price, exchangeRate)}</strong>
                      <span style={{ fontSize: "0.7rem", color: "#94A3B8", marginLeft: 6 }}>(≈ {formatHTG(order.price)})</span>
                    </div>
                    {order.profile_name && (
                      <div style={{ fontSize: "0.78rem", color: "#1E40AF", marginTop: "0.2rem" }}>Profil: <strong>{order.profile_name}</strong></div>
                    )}
                    {/* Recipient info (Free Fire ID, Wise tag, gift card email, crypto address…) — affiliate must see this to deliver. */}
                    <RecipientInfo payload={order.client_payload} testidPrefix={`aff-recipient-${order.id}`} />
                    {/* Show assigned resources (hidden info) */}
                    {order.assigned_resources && order.assigned_resources.length > 0 && (
                      <div style={{ marginTop: "0.4rem", padding: "0.5rem", background: "#EFF6FF", borderRadius: "8px", border: "1px solid #BFDBFE" }}>
                        <div style={{ fontSize: "0.72rem", color: "#1E40AF", marginBottom: "0.3rem", fontWeight: 700 }}>
                          Stock attribué pour {order.client_name} — {order.service_name}
                        </div>
                        {order.assigned_resources.map((res, i) => (
                          <div key={res.id || res._id || `res-${i}`} style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.2rem", padding: "0.25rem 0.4rem", background: "#FFF", borderRadius: "6px", border: "1px solid #E2E8F0" }}>
                            <span style={{ fontSize: "0.6rem", padding: "0.1rem 0.3rem", borderRadius: "4px", background: "#DBEAFE", color: "#1E40AF", fontWeight: 600, whiteSpace: "nowrap" }}>
                              {res.resource_type === "account" ? "Compte" : res.resource_type === "code" ? "Code" : "Info"}
                            </span>
                            <code style={{ flex: 1, fontSize: "0.8rem", fontWeight: 600, color: "#1E293B", wordBreak: "break-all" }}>{res.content}</code>
                            <button style={{ background: "#2563EB", color: "white", border: "none", borderRadius: "6px", padding: "0.2rem 0.5rem", cursor: "pointer", fontSize: "0.7rem", display: "flex", alignItems: "center", gap: "0.2rem", whiteSpace: "nowrap" }}
                              onClick={() => { navigator.clipboard.writeText(res.content); toast.success("Copié!"); }}>
                              Copier
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: "0.65rem", color: "#94A3B8", marginTop: "0.3rem" }}>{formatDate(order.validated_at || order.created_at)}</div>
                    {order.status === "validated" && !order.affiliate_validated && (
                      <div style={{ marginTop: "0.5rem" }}>
                        <div style={{ border: "2px dashed #CBD5E1", borderRadius: "8px", padding: "0.5rem", textAlign: "center", cursor: "pointer", background: order._proofUrl ? "#F0FDF4" : "#F8FAFC", marginBottom: "0.4rem" }}
                          onClick={() => document.getElementById(`proof-aff-${order.id}`).click()}>
                          <input id={`proof-aff-${order.id}`} type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const formData = new FormData();
                            formData.append("file", file);
                            try {
                              const res = await axios.post(`${API}/affiliate/upload-delivery-proof`, formData, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "multipart/form-data" }
                              });
                              const updated = affValidatedOrders.map(o => o.id === order.id ? {...o, _proofUrl: res.data.url} : o);
                              setAffValidatedOrders(updated);
                              toast.success("Preuve téléversée!");
                            } catch { toast.error("Erreur upload"); }
                          }} />
                          {order._proofUrl ? (
                            <span style={{ color: "#16A34A", fontSize: "0.78rem", fontWeight: 600 }}>Preuve jointe</span>
                          ) : (
                            <span style={{ color: "#64748B", fontSize: "0.75rem" }}>Joindre preuve de livraison (capture)</span>
                          )}
                        </div>
                        <button className="btn btn-success btn-sm" style={{ width: "100%" }} data-testid={`aff-validate-delivery-${order.id}`}
                          onClick={async () => {
                            try {
                              const res = await apiCall("post", `/affiliate/validate-delivery/${order.id}`, { delivery_proof_url: order._proofUrl || "" });
                              toast.success(res.data.message);
                              // Per business rule: after affiliate validates, open WhatsApp DIRECTLY to the CLIENT
                              // with the site link + thank-you message.
                              if (res.data.whatsapp_client_url) {
                                window.open(res.data.whatsapp_client_url, "_blank");
                              } else if (res.data.whatsapp_admin_url) {
                                window.open(res.data.whatsapp_admin_url, "_blank");
                              }
                              fetchData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Erreur");
                            }
                          }}>
                          <Send size={16} /> Soumettre la livraison
                        </button>
                      </div>
                    )}
                    {order.status === "pending_delivery" && (
                      <div style={{ marginTop: "0.4rem", padding: "0.3rem 0.5rem", background: "#FEF3C7", borderRadius: "6px", fontSize: "0.75rem", color: "#92400E", fontWeight: 600, textAlign: "center" }}>
                        En attente de validation admin
                      </div>
                    )}
                    {order.status === "completed" && order.affiliate_validated && (
                      <div style={{ marginTop: "0.4rem", padding: "0.3rem 0.5rem", background: "#DCFCE7", borderRadius: "6px", fontSize: "0.75rem", color: "#16A34A", fontWeight: 600, textAlign: "center" }}>
                        Livraison validée - Commission reçue
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
            })()}
          </div>
        )}

        {/* Affiliate Crypto Tab */}
        {activeTab === "crypto" && (
          <LazyPanel>
            <div className="card" data-testid="aff-crypto-tab">
              <CryptoExchangeUser apiCall={apiCall} API={API} tokenKey="token" role="affiliate" />
            </div>
          </LazyPanel>
        )}

        {/* Affiliate Client Transactions Tab (recharges + P2P) */}
        {activeTab === "client-tx" && (
          <LazyPanel><AffiliateClientTransactions apiCall={apiCall} /></LazyPanel>
        )}
      </div>
      </div>{/* /.sidebar-content */}
    </div>
  );
};

// Admin Dashboard

const StreamEditForm = ({ conn, onSave, onCancel }) => {
  const [email, setEmail] = useState(conn.email);
  const [profiles, setProfiles] = useState([...conn.profiles, ...Array(5 - conn.profiles.length).fill("")].slice(0, 5));
  const [vipProfiles, setVipProfiles] = useState([...(conn.vip_profiles || []), ...Array(2 - (conn.vip_profiles || []).length).fill("")].slice(0, 2));
  const [affiliate, setAffiliate] = useState(conn.assigned_affiliate || "");
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
      <input className="form-input" style={{ fontSize: "0.8rem" }} value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" />
      <div style={{ fontSize: "0.7rem", color: "#1E40AF", fontWeight: 600 }}>Écrans :</div>
      {profiles.map((p, i) => (
        <input key={i} className="form-input" style={{ fontSize: "0.78rem" }} value={p} onChange={e => { const arr = [...profiles]; arr[i] = e.target.value; setProfiles(arr); }} placeholder={`Écran ${i + 1}`} />
      ))}
      <div style={{ fontSize: "0.7rem", color: "#D97706", fontWeight: 600 }}>Écrans VIP :</div>
      {vipProfiles.map((p, i) => (
        <input key={`v${i}`} className="form-input" style={{ fontSize: "0.78rem", borderColor: "#FDE68A" }} value={p} onChange={e => { const arr = [...vipProfiles]; arr[i] = e.target.value; setVipProfiles(arr); }} placeholder={`Écran VIP ${i + 1}`} />
      ))}
      <input className="form-input" style={{ fontSize: "0.78rem" }} value={affiliate} onChange={e => setAffiliate(e.target.value)} placeholder="Code affilié" />
      <div style={{ display: "flex", gap: "0.3rem" }}>
        <button className="btn btn-success btn-sm" onClick={() => onSave({ email: email.trim(), profiles: profiles.filter(p => p.trim()), vip_profiles: vipProfiles.filter(p => p.trim()), assigned_affiliate: affiliate.trim().toUpperCase() })}>Sauver</button>
        <button className="btn btn-sm" style={{ background: "#E2E8F0" }} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
};

// Admin: full transactions history with fraud detection
const AdminTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, completed_orders: 0, fraud_flagged: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [fraudOnly, setFraudOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [proofView, setProofView] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (searchQ) params.set("search", searchQ);
      if (fraudOnly) params.set("fraud_only", "true");
      params.set("limit", "300");
      const res = await apiCall("get", `/admin/transactions?${params.toString()}`);
      setTransactions(res.data.transactions || []);
      setStats(res.data.stats || { total_orders: 0, completed_orders: 0, fraud_flagged: 0 });
    } catch (e) {
      toast.error("Erreur chargement transactions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQ, fraudOnly]);

  useEffect(() => { load(); }, [load]);

  const statusBadge = (st) => {
    const colors = {
      pending: ["#FEF3C7", "#92400E"],
      validated: ["#DBEAFE", "#1E40AF"],
      pending_delivery: ["#FED7AA", "#9A3412"],
      completed: ["#DCFCE7", "#166534"],
      rejected: ["#FEE2E2", "#B91C1C"],
    };
    const [bg, fg] = colors[st] || ["#F1F5F9", "#475569"];
    return <span style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.65rem", fontWeight: 700, background: bg, color: fg }}>{st}</span>;
  };

  return (
    <div className="card" data-testid="admin-transactions-panel">
      <h3 className="card-title"><History size={18} /> Historique des transactions</h3>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "0.4rem", marginBottom: "0.6rem" }}>
        <div style={{ padding: "0.5rem", borderRadius: "8px", background: "#F0F9FF", border: "1px solid #BAE6FD" }}>
          <div style={{ fontSize: "0.65rem", color: "#0C4A6E", fontWeight: 700 }}>Total commandes</div>
          <div style={{ fontSize: "1.3rem", fontWeight: 800 }} data-testid="stat-total-orders">{stats.total_orders}</div>
        </div>
        <div style={{ padding: "0.5rem", borderRadius: "8px", background: "#F0FDF4", border: "1px solid #86EFAC" }}>
          <div style={{ fontSize: "0.65rem", color: "#14532D", fontWeight: 700 }}>Complétées</div>
          <div style={{ fontSize: "1.3rem", fontWeight: 800 }}>{stats.completed_orders}</div>
        </div>
        <div style={{ padding: "0.5rem", borderRadius: "8px", background: "#FEF2F2", border: "1px solid #FCA5A5" }}>
          <div style={{ fontSize: "0.65rem", color: "#991B1B", fontWeight: 700 }}>Fraudes signalées</div>
          <div style={{ fontSize: "1.3rem", fontWeight: 800 }} data-testid="stat-fraud-flagged">{stats.fraud_flagged}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
        <select className="form-input" style={{ maxWidth: "170px", fontSize: "0.78rem" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="tx-status-filter">
          <option value="">Tous les statuts</option>
          <option value="pending">En attente</option>
          <option value="validated">Validées</option>
          <option value="pending_delivery">Livraison en attente</option>
          <option value="completed">Complétées</option>
          <option value="rejected">Rejetées</option>
        </select>
        <input className="form-input" style={{ flex: 1, minWidth: "140px", fontSize: "0.78rem" }} placeholder="Recherche (client/service)..." value={searchQ} onChange={e => setSearchQ(e.target.value)} data-testid="tx-search-input" />
        <label style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", background: fraudOnly ? "#FEE2E2" : "#F1F5F9", padding: "0.2rem 0.5rem", borderRadius: "6px", cursor: "pointer" }}>
          <input type="checkbox" checked={fraudOnly} onChange={e => setFraudOnly(e.target.checked)} data-testid="tx-fraud-only" />
          Fraudes seulement
        </label>
        <button className="btn btn-sm btn-primary" onClick={load} disabled={loading} data-testid="tx-refresh">
          <RefreshCw size={13} /> {loading ? "..." : "Actualiser"}
        </button>
        <button className="btn btn-sm" style={{ background: "#10B981", color: "white" }} data-testid="tx-export-csv"
          onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (statusFilter) params.set("status", statusFilter);
              if (searchQ) params.set("search", searchQ);
              if (fraudOnly) params.set("fraud_only", "true");
              const url = `${API}/admin/transactions/export.csv?${params.toString()}`;
              const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                responseType: "blob",
              });
              const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = `smartech_transactions_${new Date().toISOString().slice(0,10)}.csv`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              toast.success("CSV exporté!");
            } catch (err) {
              toast.error("Erreur export");
            }
          }}>
          <Download size={13} /> Export CSV
        </button>
      </div>

      {/* List */}
      {transactions.length === 0 ? (
        <div style={{ padding: "1rem", textAlign: "center", color: "#94A3B8", fontSize: "0.85rem" }}>
          Aucune transaction trouvée.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: "0.78rem", borderCollapse: "collapse" }} data-testid="tx-table">
            <thead>
              <tr style={{ background: "#F8FAFC", textAlign: "left" }}>
                <th style={{ padding: "0.4rem" }}>Date</th>
                <th style={{ padding: "0.4rem" }}>Client</th>
                <th style={{ padding: "0.4rem" }}>Service</th>
                <th style={{ padding: "0.4rem" }}>Montant</th>
                <th style={{ padding: "0.4rem" }}>Statut</th>
                <th style={{ padding: "0.4rem" }}>Preuve</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.id} data-testid={`tx-row-${t.id}`} style={{
                  borderBottom: "1px solid #E2E8F0",
                  background: t.fraud_flag ? "#FEF2F2" : "transparent"
                }}>
                  <td style={{ padding: "0.4rem", whiteSpace: "nowrap" }}>{formatDate(t.created_at)}</td>
                  <td style={{ padding: "0.4rem" }}>
                    <div style={{ fontWeight: 700 }}>{t.client_name}</div>
                    <div style={{ fontSize: "0.7rem", color: "#64748B" }}>{t.client_phone}</div>
                  </td>
                  <td style={{ padding: "0.4rem" }}>
                    <div>{t.service_name}</div>
                    <div style={{ fontSize: "0.7rem", color: "#64748B" }}>{t.plan_name}</div>
                  </td>
                  <td style={{ padding: "0.4rem", fontWeight: 700 }}>{t.price ? `${t.price.toLocaleString("fr-FR")} Gdes` : "-"}</td>
                  <td style={{ padding: "0.4rem" }}>{statusBadge(t.status)}</td>
                  <td style={{ padding: "0.4rem" }}>
                    {t.payment_proof_url ? (
                      <button className="btn btn-sm" style={{ background: "#E0E7FF", color: "#3730A3", fontSize: "0.7rem", padding: "0.15rem 0.5rem" }} onClick={() => setProofView(t.payment_proof_url)} data-testid={`tx-view-proof-${t.id}`}>
                        Voir
                      </button>
                    ) : t.transaction_id ? (
                      <code style={{ fontSize: "0.7rem", color: "#64748B" }}>{t.transaction_id.substring(0, 10)}...</code>
                    ) : <span style={{ color: "#94A3B8", fontSize: "0.7rem" }}>-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Proof modal */}
      {proofView && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: "1rem" }} onClick={() => setProofView(null)}>
          <img src={proofView} alt="preuve" style={{ maxWidth: "92%", maxHeight: "92%", borderRadius: "10px" }} onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};




const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isSubAdmin = user?.role === "sub_admin";
  const [activeTab, setActiveTab] = useState("pending");
  const [highlightedSmmOrderId, setHighlightedSmmOrderId] = useState(null);

  // Deep-link from admin WhatsApp notification : /admin?smm_order_id=<id>
  // → auto-switch to SMM tab and highlight the order.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get("smm_order_id");
      if (sid) {
        setActiveTab("smm");
        setHighlightedSmmOrderId(sid);
        const url = new URL(window.location.href);
        url.searchParams.delete("smm_order_id");
        window.history.replaceState({}, "", url.toString());
      }
    } catch { /* ignore */ }
  }, []);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [pendingPasswordResets, setPendingPasswordResets] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  // Free Fire low-stock alert (shown as a red badge on the sidebar tab)
  const [lowStockAlertCount, setLowStockAlertCount] = useState(0);
  // Pending deliveries
  const [pendingDeliveries, setPendingDeliveries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // New: messaging, promotions, affiliate management
  const [msgTarget, setMsgTarget] = useState("affiliates");
  const [msgText, setMsgText] = useState("");
  const [sendingMsg, setSendingMsg] = useState(false);
  const [promos, setPromos] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  // Site stats & popup management
  const [siteStats, setSiteStats] = useState(null);
  const [popups, setPopups] = useState([]);
  const [popupImageUrl, setPopupImageUrl] = useState("");
  const [popupLinkUrl, setPopupLinkUrl] = useState("");
  const [popupDuration, setPopupDuration] = useState(24);
  const [uploadingPopup, setUploadingPopup] = useState(false);
  // Sol admin
  const [adminSol, setAdminSol] = useState(null);
  const [solAmount, setSolAmount] = useState(20);
  const [solDuration, setSolDuration] = useState(15);
  const [solMaxParticipants, setSolMaxParticipants] = useState("");
  const [solDistHistory, setSolDistHistory] = useState([]);
  // Subscriptions
  const [subscriptions, setSubscriptions] = useState([]);
  const [subStats, setSubStats] = useState(null);
  const [subFilter, setSubFilter] = useState("all");
  // Subscription search
  const [subSearch, setSubSearch] = useState("");
  // Expiring soon
  const [expiringSoon, setExpiringSoon] = useState([]);
  // Subscription creation form (admin)
  const [newSubPhone, setNewSubPhone] = useState("");
  const [newSubName, setNewSubName] = useState("");
  const [newSubService, setNewSubService] = useState("Netflix");
  const [newSubDays, setNewSubDays] = useState("");
  const [newSubEmail, setNewSubEmail] = useState("");
  const [creatingSub, setCreatingSub] = useState(false);
  const [catalogServices, setCatalogServices] = useState([]);
  // Streaming connections
  const [streamingConns, setStreamingConns] = useState([]);
  const [newStreamEmail, setNewStreamEmail] = useState("");
  const [newStreamProfiles, setNewStreamProfiles] = useState(["", "", "", "", ""]);
  const [newStreamVip, setNewStreamVip] = useState(false);
  const [newStreamVipProfiles, setNewStreamVipProfiles] = useState(["", ""]);
  const [newStreamAffiliate, setNewStreamAffiliate] = useState("");
  const [editingStream, setEditingStream] = useState(null);
  // Explainer videos
  const [explainerVideos, setExplainerVideos] = useState([]);
  // Order resource selection
  const [orderResources, setOrderResources] = useState({});
  const [selectedResources, setSelectedResources] = useState({});
  const [postValidateInfo, setPostValidateInfo] = useState(null);
  const [showOrderResources, setShowOrderResources] = useState(null);
  const [validatingOrderId, setValidatingOrderId] = useState(null);
  
  // Forms
  const [addClientPhone, setAddClientPhone] = useState("");
  const [addClientCode, setAddClientCode] = useState("");
  const [addClientService, setAddClientService] = useState("netflix");
  const [addClientPlan, setAddClientPlan] = useState("daily");
  const [newPasswordMap, setNewPasswordMap] = useState({});
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [rewardCode, setRewardCode] = useState("");
  const [rewardAmount, setRewardAmount] = useState("");
  const [rewardReason, setRewardReason] = useState("");
  
  // Reassignment dialog
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [reassignData, setReassignData] = useState(null);
  
  // Phone check for admin
  const { checking: adminPhoneChecking, phoneStatus: adminPhoneStatus, checkPhone: adminCheckPhone, clearStatus: adminClearStatus } = usePhoneCheck();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // PHASE 1 (critical) — single aggregated endpoint returns 8 lists in one
      // round trip. The dashboard renders immediately after this completes.
      try {
        const r = await apiCall("get", "/admin/dashboard-bundle");
        const b = r.data || {};
        setStats(b.stats || {});
        setPending(b.pending || []);
        setPendingRegistrations(b.pending_registrations || []);
        setPendingWithdrawals(b.pending_withdrawals || []);
        setPendingPasswordResets(b.password_resets || []);
        setAffiliates(b.affiliates || []);
        setLeaderboard(b.leaderboard || []);
        setPendingOrders(b.pending_orders || []);
      } catch (e) {
        // Fallback to old individual endpoints if bundle ever fails.
        const safeGet = (url) => apiCall("get", url).then(r => r.data).catch(() => null);
        const [s, p, pr, pw, ppw, af, lb, po] = await Promise.all([
          safeGet("/admin/stats"), safeGet("/admin/pending"),
          safeGet("/admin/pending-registrations"), safeGet("/admin/pending-withdrawals"),
          safeGet("/admin/password-resets"), safeGet("/admin/affiliates"),
          safeGet("/leaderboard"), safeGet("/admin/pending-orders"),
        ]);
        if (s) setStats(s); if (p) setPending(p);
        if (pr) setPendingRegistrations(pr); if (pw) setPendingWithdrawals(pw);
        if (ppw) setPendingPasswordResets(ppw); if (af) setAffiliates(af);
        if (lb) setLeaderboard(lb); if (po) setPendingOrders(po);
      }
      setIsLoading(false);   // ← Render the dashboard NOW, then keep loading the rest in background.

      // PHASE 2 (non-critical / secondary panels) — fire and forget, never blocks UI.
      const fire = (url, setter) => apiCall("get", url).then(r => setter(r.data)).catch(() => {});
      fire("/admin/pending-deliveries", setPendingDeliveries);
      fire("/admin/promotions", setPromos);
      fire("/admin/site-stats", setSiteStats);
      fire("/admin/popups", setPopups);
      fire("/admin/sol/status", setAdminSol);
      fire("/admin/sol/history", setSolDistHistory);
      fire("/admin/subscriptions", setSubscriptions);
      fire("/admin/subscription-stats", setSubStats);
      fire("/admin/expiring-subscriptions", setExpiringSoon);
      fire("/services", setCatalogServices);
      fire("/admin/streaming-connections", setStreamingConns);
      fire("/admin/explainer-videos", setExplainerVideos);
    } catch (err) {
      console.error("Erreur chargement:", err);
      if (err.response?.status === 403) {
        const role = (JSON.parse(localStorage.getItem("user") || "{}").role) || "";
        if (!["admin", "sub_admin"].includes(role)) {
          navigate("/dashboard");
        }
      }
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh on focus only (no interval polling). Admin uses the refresh
  // button or simply switches back to the tab to get the latest data.
  // No auto-refresh: the admin clicks the "Actualiser" button when they want
  // the latest data. Keeps the UI calm and prevents focus-stealing on weak
  // connections. Only the Balances panel keeps a light polling for new client
  // deposits/withdrawals (see BalanceAndAffiliates.js).

  // Poll the Free Fire low-stock indicator (lightweight) every 60s.
  useEffect(() => {
    let cancelled = false;
    const fetchLowStock = async () => {
      try {
        const r = await apiCall("get", "/admin/freefire/low-stock-alerts");
        if (!cancelled) setLowStockAlertCount(r.data?.low_count || 0);
      } catch { /* noop */ }
    };
    fetchLowStock();
    const t = setInterval(fetchLowStock, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [apiCall]);

  // Handler functions
  const handleValidateClient = async (pendingId) => {
    try {
      const res = await apiCall("post", "/admin/validate-client", { pending_id: pendingId });
      toast.success(`Client validé! ${res.data.affiliate_code} → ${res.data.new_level}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de validation");
    }
  };

  const handleRejectClient = async (pendingId) => {
    try {
      await apiCall("delete", `/admin/reject-client/${pendingId}`);
      toast.success("Client rejeté");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de rejet");
    }
  };

  // Order handlers
  const handleValidateOrder = async (orderId) => {
    // Guard against double-clicks / rapid re-renders that would call the
    // endpoint twice — the 2nd call would fail with "Commande non trouvée"
    // because the order is no longer in status=pending.
    if (validatingOrderId === orderId) return;
    setValidatingOrderId(orderId);
    try {
      const resIds = selectedResources[orderId] || [];
      const res = await apiCall("post", "/admin/validate-order", { order_id: orderId, resource_ids: resIds.length > 0 ? resIds : null });
      toast.success(res.data.message + (res.data.resources_assigned > 0 ? ` (${res.data.resources_assigned} ressources attribuées)` : ""));
      setSelectedResources(prev => { const n = {...prev}; delete n[orderId]; return n; });
      setShowOrderResources(null);
      // Store post-validation info so UI can offer WhatsApp actions
      if (res.data && (res.data.whatsapp_affiliate_url || res.data.whatsapp_client_confirm_url)) {
        setPostValidateInfo(res.data);
      }
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur de validation";
      // Specifically suppress the "Commande non trouvée" error when it stems
      // from a benign retry on an already-validated order.
      if (err.response?.status === 404 && detail.includes("non trouvée")) {
        // Order is already validated — refresh quietly.
        fetchData();
      } else {
        toast.error(detail);
      }
    } finally {
      setValidatingOrderId(null);
    }
  };

  const fetchOrderResources = async (orderId) => {
    try {
      const res = await apiCall("get", `/admin/available-resources-for-order/${orderId}`);
      setOrderResources(prev => ({ ...prev, [orderId]: res.data }));
    } catch {}
  };

  const toggleResourceSelection = (orderId, resourceId) => {
    setSelectedResources(prev => {
      const current = prev[orderId] || [];
      if (current.includes(resourceId)) {
        return { ...prev, [orderId]: current.filter(id => id !== resourceId) };
      }
      return { ...prev, [orderId]: [...current, resourceId] };
    });
  };

  // Subscription handlers
  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    if (!newSubPhone.trim() || !newSubName.trim() || !newSubDays) return;
    if (!newSubEmail.trim()) { toast.error("Email streaming requis"); return; }
    setCreatingSub(true);
    try {
      const res = await apiCall("post", "/admin/create-subscription", {
        client_phone: newSubPhone.trim(),
        client_name: newSubName.trim(),
        service_name: newSubService,
        duration_days: parseInt(newSubDays),
        streaming_email: newSubEmail.trim().toLowerCase(),
      });
      toast.success(res.data.message);
      setNewSubPhone(""); setNewSubName(""); setNewSubDays(""); setNewSubService("Netflix"); setNewSubEmail("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setCreatingSub(false);
    }
  };

  const handleValidateSubscription = async (subId) => {
    try {
      const res = await apiCall("post", `/admin/validate-subscription/${subId}`);
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRejectSubscription = async (subId) => {
    try {
      await apiCall("delete", `/admin/reject-subscription/${subId}`);
      toast.success("Abonnement rejeté");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRejectOrder = async (orderId) => {
    try {
      await apiCall("delete", `/admin/reject-order/${orderId}`);
      toast.success("Commande rejetée");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Send targeted message
  const handleSendMessage = async () => {
    if (!msgText.trim()) { toast.error("Entrez un message"); return; }
    setSendingMsg(true);
    try {
      const res = await apiCall("post", "/admin/send-message", { message: msgText.trim(), target: msgTarget });
      toast.success(res.data.message);
      setMsgText("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSendingMsg(false);
    }
  };

  // Create promotion
  const handleCreatePromo = async (percent) => {
    try {
      const res = await apiCall("post", "/admin/create-promotion", { discount_percent: percent });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Toggle affiliate
  const handleToggleAffiliate = async (affiliateId) => {
    try {
      const res = await apiCall("post", "/admin/toggle-affiliate", { affiliate_id: affiliateId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Promote/demote affiliate <-> Sub-Admin (admin principal only)
  const handleToggleSubAdmin = async (aff) => {
    const isSub = aff.role === "sub_admin";
    const action = isSub ? "rétrograder" : "promouvoir";
    if (!window.confirm(`Voulez-vous vraiment ${action} l'affilié ${aff.code} ${isSub ? "en simple affilié" : "en Administrateur Adjoint"} ?`)) return;
    try {
      const endpoint = isSub ? "/admin/affiliates/demote-subadmin" : "/admin/affiliates/promote-subadmin";
      const res = await apiCall("post", endpoint, { affiliate_id: aff.id });
      toast.success(res.data.message || (isSub ? "Rétrogradé" : "Promu Admin Adjoint"));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Delete affiliate
  const handleDeleteAffiliate = async (affiliateId) => {
    try {
      await apiCall("delete", `/admin/delete-affiliate/${affiliateId}`);
      toast.success("Affilié supprimé");
      setConfirmDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };



  const handleApproveAffiliate = async (affiliateId) => {
    try {
      await apiCall("post", "/admin/approve-affiliate", { affiliate_id: affiliateId });
      toast.success("Affilié approuvé!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'approbation");
    }
  };

  const handleRejectAffiliate = async (affiliateId) => {
    try {
      await apiCall("delete", `/admin/reject-affiliate/${affiliateId}`);
      toast.success("Affilié rejeté");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de rejet");
    }
  };

  const handleApproveWithdrawal = async (withdrawalId) => {
    try {
      const res = await apiCall("post", "/admin/approve-withdrawal", { withdrawal_id: withdrawalId });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'approbation");
    }
  };

  const handleRejectWithdrawal = async (withdrawalId) => {
    try {
      await apiCall("delete", `/admin/reject-withdrawal/${withdrawalId}`);
      toast.success("Retrait rejeté");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de rejet");
    }
  };

  const handleApprovePasswordReset = async (requestId) => {
    const newPassword = newPasswordMap[requestId];
    if (!newPassword || newPassword.length < 4) {
      toast.error("Entrez un nouveau mot de passe (min 4 caractères)");
      return;
    }
    try {
      const res = await apiCall("post", "/admin/approve-password-reset", { request_id: requestId, new_password: newPassword });
      toast.success("Mot de passe réinitialisé!");
      // For client resets, open WhatsApp with the new credentials.
      const data = res?.data || {};
      if (data.kind === "client" && data.client_phone) {
        const phone = String(data.client_phone).replace(/\D/g, "");
        const msg = encodeURIComponent(
          `Bonjour ${data.client_name || ""}, votre mot de passe SMARTECH SYSTEM PRO a été réinitialisé.\n\n` +
          `Nouveau mot de passe : ${newPassword}\n\n` +
          `Connectez-vous sur https://smartechsystempro.com avec votre numéro de téléphone et ce mot de passe. ` +
          `Pensez à le changer dans votre profil après votre prochaine connexion.\n\n— SMARTECH SYSTEM PRO`
        );
        const url = `https://wa.me/${phone}?text=${msg}`;
        try { window.open(url, "_blank", "noopener"); } catch (e) { /* ignore */ }
      } else if (data.kind === "affiliate" && data.affiliate_code) {
        const msg = encodeURIComponent(
          `Bonjour ${data.affiliate_code}, votre mot de passe SMARTECH a été réinitialisé.\nNouveau mot de passe : ${newPassword}\nConnectez-vous et changez-le rapidement.`
        );
        const url = `https://wa.me/?text=${msg}`;
        try { window.open(url, "_blank", "noopener"); } catch (e) { /* ignore */ }
      }
      setNewPasswordMap(prev => { const copy = {...prev}; delete copy[requestId]; return copy; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRejectPasswordReset = async (requestId) => {
    try {
      await apiCall("delete", `/admin/reject-password-reset/${requestId}`);
      toast.success("Demande rejetée");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleAddClient = async (e, forceReassign = false) => {
    e?.preventDefault();
    if (!addClientPhone.trim() || !addClientCode.trim()) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    
    try {
      const res = await apiCall("post", "/admin/add-client", {
        phone_number: addClientPhone,
        affiliate_code: addClientCode,
        service: addClientService,
        plan: addClientPlan,
        force_reassign: forceReassign
      });
      
      // Check if conflict requires confirmation
      if (res.data.status === "conflict" || res.data.status === "pending_conflict") {
        setReassignData({
          phone: addClientPhone,
          newCode: addClientCode,
          existingOwner: res.data.existing_owner,
          message: res.data.message
        });
        setShowReassignDialog(true);
        return;
      }
      
      // Success or reassigned
      if (res.data.status === "reassigned") {
        toast.success(`Client réassigné de ${res.data.old_owner} à ${res.data.new_owner}`);
      } else {
        const commissionMsg = res.data.commission ? `+${res.data.commission} Gdes` : "+2.5 Gdes";
        toast.success(`Client ajouté pour ${res.data.affiliate_code}! ${commissionMsg}`);
      }
      
      setAddClientPhone("");
      setAddClientCode("");
      setAddClientService("netflix");
      setAddClientPlan("daily");
      adminClearStatus();
      setShowReassignDialog(false);
      setReassignData(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'ajout");
    }
  };

  const handleConfirmReassign = async () => {
    if (!reassignData) return;
    
    try {
      const res = await apiCall("post", "/admin/add-client", {
        phone_number: reassignData.phone,
        affiliate_code: reassignData.newCode,
        force_reassign: true
      });
      
      toast.success(`Client réassigné de ${res.data.old_owner} à ${res.data.new_owner}`);
      setAddClientPhone("");
      setAddClientCode("");
      adminClearStatus();
      setShowReassignDialog(false);
      setReassignData(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de réassignation");
    }
  };

  const handleCancelReassign = () => {
    setShowReassignDialog(false);
    setReassignData(null);
  };

  const handleAdminPhoneChange = (e) => {
    const value = e.target.value;
    setAddClientPhone(value);
    adminCheckPhone(value);
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      toast.error("Remplissez tous les champs");
      return;
    }
    try {
      await apiCall("post", "/admin/create-notification", {
        title: notificationTitle,
        message: notificationMessage,
        type: "info"
      });
      toast.success("Notification envoyée à tous les affiliés!");
      setNotificationTitle("");
      setNotificationMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleGiveReward = async (e) => {
    e.preventDefault();
    if (!rewardCode.trim() || !rewardAmount || !rewardReason.trim()) {
      toast.error("Remplissez tous les champs");
      return;
    }
    try {
      await apiCall("post", "/admin/manual-reward", {
        affiliate_code: rewardCode,
        amount: parseFloat(rewardAmount),
        reason: rewardReason
      });
      toast.success(`Bonus de ${rewardAmount} Gdes donné!`);
      setRewardCode("");
      setRewardAmount("");
      setRewardReason("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCalculateMonthlyRewards = async () => {
    if (!window.confirm("Calculer et distribuer les récompenses du mois? Cette action réinitialisera les compteurs mensuels.")) {
      return;
    }
    try {
      const res = await apiCall("post", "/admin/calculate-monthly-rewards");
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (isLoading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalPending = (stats?.pending_clients || 0) + (stats?.pending_affiliates || 0) + 
                       (stats?.pending_withdrawals || 0) + (stats?.pending_password_resets || 0) +
                       (stats?.pending_orders || 0);

  return (
    <div className="dashboard has-sidebar" data-testid="admin-dashboard">
      <Sidebar
        title="SMARTECH PRO"
        subtitle={isSubAdmin ? "Admin Adjoint" : "Espace Admin"}
        accent="#DC2626"
        active={activeTab}
        onSelect={setActiveTab}
        onLogout={handleLogout}
        items={[
          { key: "pending", label: "Clients", icon: Users, badge: pending.length },
          { key: "registrations", label: "Inscriptions", icon: UserPlus, badge: pendingRegistrations.length, testid: "tab-admin-registrations" },
          { key: "withdrawals", label: "Retraits", icon: ArrowUpCircle, badge: pendingWithdrawals.length },
          { key: "passwords", label: "Mots de passe", icon: Key, badge: pendingPasswordResets.length },
          { key: "leaderboard", label: "Classement", icon: Trophy },
          { key: "orders", label: "Commandes", icon: Package, badge: pendingOrders.length, testid: "tab-admin-orders" },
          { key: "subscriptions", label: "Abonnements", icon: CalendarDays, testid: "tab-admin-subscriptions" },
          { key: "sol", label: "Sol", icon: Users, testid: "tab-admin-sol" },
          { key: "catalogue", label: "Catalogue", icon: FolderTree, testid: "tab-admin-catalogue" },
          // 2026-06-08 : onglet "Streaming" supprimé — fonctionnalités intégrées au panneau Abonnements.
          { key: "videos", label: "Vidéos", icon: Video, testid: "tab-admin-videos" },
          { key: "formations", label: "Formations", icon: BookOpen, testid: "tab-admin-formations" },
          { key: "professeurs", label: "Professeurs", icon: GraduationCap, testid: "tab-admin-professeurs" },
          { key: "crypto", label: "Crypto", icon: Bitcoin, testid: "tab-admin-crypto" },
          { key: "balances", label: "Balances", icon: Wallet, testid: "tab-admin-balances" },
          { key: "history", label: "Historique soldes", icon: History, testid: "tab-admin-history" },
          { key: "receipts", label: "Reçus paiements", icon: Receipt, testid: "tab-admin-receipts" },
          { key: "global-history", label: "Historique global", icon: FileSpreadsheet, testid: "tab-admin-global-history" },
          { key: "transactions", label: "Transactions", icon: FileSpreadsheet, testid: "tab-admin-transactions" },
          { key: "audit", label: "Audit Client", icon: FileSpreadsheet, testid: "tab-admin-audit" },
          { key: "p2p", label: "Vérification P2P", icon: ArrowRightLeft, testid: "tab-admin-p2p" },
          { key: "htg-p2p", label: "Échange Gourdes P2P", icon: ArrowRightLeft, testid: "tab-admin-htg-p2p" },
          { key: "profit", label: "Bénéfices Admin", icon: TrendingUp, testid: "tab-admin-profit" },
          { key: "freefire", label: "Topup Free Fire", icon: Gamepad2, testid: "tab-admin-freefire", badge: lowStockAlertCount > 0 ? lowStockAlertCount : null, badgeColor: "#DC2626" },
          { key: "game-topup", label: "Topup Jeux", icon: Gamepad2, testid: "tab-admin-game-topup", badge: (stats?.pending_game_topup_orders || 0) > 0 ? stats.pending_game_topup_orders : null, badgeColor: "#DC2626" },
          { key: "messages", label: "Messages", icon: MessageCircle, testid: "tab-admin-messages", badge: (stats?.unread_chat_messages || 0) > 0 ? stats.unread_chat_messages : null, badgeColor: "#DC2626" },
          { key: "wa2", label: "WhatsApp Secondaire", icon: MessageCircle, testid: "tab-admin-wa2", badge: (stats?.pending_wa2_requests || 0) > 0 ? stats.pending_wa2_requests : null, badgeColor: "#16A34A" },
          { key: "safety", label: "Surveillance", icon: MessageCircle, testid: "tab-admin-safety", badge: (stats?.clients_under_surveillance || 0) > 0 ? stats.clients_under_surveillance : null, badgeColor: "#DC2626" },
          { key: "smm", label: "SMM", icon: Megaphone, testid: "tab-admin-smm", badge: (stats?.pending_smm_orders || 0) > 0 ? stats.pending_smm_orders : null, badgeColor: "#DC2626" },
          { key: "contacts", label: "Contacts Clients", icon: MessageCircle, testid: "tab-admin-contacts" },
          { key: "settings", label: "Paramètres", icon: Settings, testid: "tab-admin-settings" },
          { key: "tools", label: "Outils", icon: Settings },
        ].filter(item => {
          if (!isSubAdmin) return true;
          // Sub-Admin: hide sensitive/financial tabs
          const hidden = new Set([
            "registrations", "withdrawals", "passwords", "catalogue", "crypto",
            "balances", "history", "receipts", "global-history", "transactions",
            "audit", "p2p", "htg-p2p", "profit", "settings", "tools", "contacts",
          ]);
          return !hidden.has(item.key);
        })}
      />
      <div className="sidebar-content">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <h1 className="header-title">SMARTECH PRO - {isSubAdmin ? "Admin Adjoint" : "Admin"}</h1>
          <div className="header-user">
            <button
              onClick={() => fetchData()}
              className="btn btn-sm btn-secondary"
              data-testid="admin-refresh-btn"
              title="Actualiser les données du dashboard"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <RefreshCw size={16} /> Actualiser
            </button>
            <NotificationCenter apiCall={apiCall} role="admin" />
            <span className="user-code level-badge level-admin" data-testid="admin-role-badge">{isSubAdmin ? "ADJOINT" : "ADMIN"}</span>
            <button onClick={handleLogout} className="btn-logout" data-testid="admin-logout-btn">
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="dashboard-content">
        {/* Stats */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon clients"><Users size={20} /></div>
              <div className="stat-value">{stats.total_affiliates}</div>
              <div className="stat-label">Affiliés</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon" style={{ background: "#FEE2E2", color: "#DC2626" }}><AlertCircle size={20} /></div>
              <div className="stat-value">{totalPending}</div>
              <div className="stat-label">En attente</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon gains"><CheckCircle size={20} /></div>
              <div className="stat-value">{stats.total_clients_validated}</div>
              <div className="stat-label">Validés</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon level"><DollarSign size={20} /></div>
              <div className="stat-value">{stats.total_gains_distributed.toFixed(0)}</div>
              <div className="stat-label">Gdes distribués</div>
            </div>
            {subStats && (
              <>
                <div className="stat-card">
                  <div className="stat-icon gains"><CalendarDays size={20} /></div>
                  <div className="stat-value">{subStats.active}</div>
                  <div className="stat-label">Abonnements actifs</div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon" style={{ background: "#FEE2E2", color: "#DC2626" }}><Timer size={20} /></div>
                  <div className="stat-value">{subStats.expired}</div>
                  <div className="stat-label">Expirés</div>
                </div>
                {subStats.expiring_soon > 0 && (
                  <div className="stat-card" style={{ border: "2px solid #F59E0B" }}>
                    <div className="stat-icon" style={{ background: "#FEF3C7", color: "#D97706" }}><AlertTriangle size={20} /></div>
                    <div className="stat-value" style={{ color: "#D97706" }}>{subStats.expiring_soon}</div>
                    <div className="stat-label">Expirent sous 48h</div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Tabs replaced by Sidebar */}

        {/* Pending Clients Tab */}
        {activeTab === "pending" && (
          <div className="card">
            <Suspense fallback={null}>
              <LiveSmmWidget
                apiCall={apiCall}
                onOpenOrder={(id) => { setHighlightedSmmOrderId(id); setActiveTab("smm"); }}
              />
            </Suspense>
            <h3 className="card-title"><Clock size={18} /> Clients en attente</h3>
            {pending.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><CheckCircle size={24} /></div>
                <div className="empty-state-text">Aucun client en attente</div>
              </div>
            ) : (
              <div>
                {pending.map((item) => {
                  const serviceLabel = item.service === "prime_video" ? "Prime Video" : "Netflix";
                  const planLabel = item.plan === "vip" ? "VIP Mensuel" : "Quotidien";
                  const reward = item.plan === "vip" ? "100 Gdes" : "2.5 Gdes";
                  return (
                    <div key={item.id} className="pending-card">
                      <div className="pending-info">
                        <div className="pending-phone">{item.phone_number}</div>
                        <div className="pending-code">
                          Affilié: {item.affiliate_code} | {formatDate(item.submitted_at)}
                        </div>
                        {item.disputed && (
                          <div data-testid={`pending-disputed-${item.id}`} style={{ background: "#FEE2E2", color: "#991B1B", padding: "0.25rem 0.5rem", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <AlertTriangle size={12} /> Litige — déjà associé à {item.disputed_from || "un autre affilié"}
                          </div>
                        )}
                        <div className="pending-product">
                          <span className={`product-badge ${item.service}`}>
                            {item.service === "netflix" ? <Tv size={12} /> : <Play size={12} />}
                            {serviceLabel}
                          </span>
                          <span className={`plan-badge ${item.plan}`}>
                            {planLabel} ({reward})
                          </span>
                        </div>
                      </div>
                      <div className="pending-actions">
                        <button onClick={() => handleValidateClient(item.id)} className="btn btn-success btn-sm" data-testid={`validate-client-${item.id}`}>
                          <CheckCircle size={16} /> Valider
                        </button>
                        <button onClick={() => handleRejectClient(item.id)} className="btn btn-danger btn-sm">
                          <XCircle size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pending Registrations Tab */}
        {activeTab === "registrations" && (
          <>
            <LazyPanel><AdminCreateAffiliatePanel apiCall={apiCall} /></LazyPanel>
            <div className="card">
              <h3 className="card-title"><UserPlus size={18} /> Inscriptions en attente <span style={{ fontSize: "0.7rem", fontWeight: 500, color: "#64748B", background: "#F1F5F9", padding: "2px 8px", borderRadius: 999, marginLeft: 8 }}>Legacy</span></h3>
              <p style={{ fontSize: "0.75rem", color: "#64748B", margin: "0 0 0.6rem" }}>
                Les nouvelles inscriptions affiliées sont désormais directement créées par l'admin via le panneau ci-dessus. Cette liste contient uniquement les anciennes demandes en attente.
              </p>
              {pendingRegistrations.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">👥</div>
                  <div className="empty-state-text">Aucune inscription en attente</div>
                </div>
              ) : (
                <div>
                  {pendingRegistrations.map((item) => (
                    <div key={item.id} className="pending-card">
                      <div className="pending-info">
                        <div className="pending-phone">{item.code}</div>
                        <div className="pending-code">Parrain: {item.sponsor_code || "Aucun"} • {formatDate(item.created_at)}</div>
                      </div>
                      <div className="pending-actions">
                        <button onClick={() => handleApproveAffiliate(item.id)} className="btn btn-success btn-sm">
                          <CheckCircle size={16} /> Approuver
                        </button>
                        <button onClick={() => handleRejectAffiliate(item.id)} className="btn btn-danger btn-sm">
                          <XCircle size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Pending Withdrawals Tab */}
        {activeTab === "withdrawals" && (
          <div className="card">
            <h3 className="card-title"><Wallet size={18} /> Demandes de retrait</h3>
            {pendingWithdrawals.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><DollarSign size={24} /></div>
                <div className="empty-state-text">Aucune demande en attente</div>
              </div>
            ) : (
              <div>
                {pendingWithdrawals.map((item) => {
                  const cur = Number(item.current_balance) || 0;
                  const amt = Number(item.amount) || 0;
                  return (
                  <div key={item.id} className="pending-card">
                    <div className="pending-info">
                      <div className="pending-phone">{item.affiliate_code || "—"}</div>
                      <div className="pending-code">
                        <strong>{amt} Gdes</strong> | Solde: {cur.toFixed(2)} Gdes
                      </div>
                      <div className="payment-phone-info">
                        <Phone size={12} /> Paiement: <strong>{item.payment_phone || "Non spécifié"}</strong>
                      </div>
                    </div>
                    <div className="pending-actions">
                      <button 
                        onClick={() => handleApproveWithdrawal(item.id)} 
                        className="btn btn-success btn-sm"
                        disabled={amt > cur}
                      >
                        <CheckCircle size={16} /> Approuver
                      </button>
                      <button onClick={() => handleRejectWithdrawal(item.id)} className="btn btn-danger btn-sm">
                        <XCircle size={16} />
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Password Resets Tab */}
        {activeTab === "passwords" && (
          <div className="card">
            <h3 className="card-title"><Key size={18} /> Réinitialisations de mot de passe</h3>
            {pendingPasswordResets.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔐</div>
                <div className="empty-state-text">Aucune demande en attente</div>
              </div>
            ) : (
              <div>
                {pendingPasswordResets.map((item) => {
                  const isClient = item.kind === "client";
                  const displayName = isClient
                    ? `${item.client_name || "Client"} · ${item.client_phone || ""}`
                    : item.affiliate_code;
                  return (
                    <div key={item.id} className="pending-card password-reset-card" data-testid={`reset-card-${item.id}`}>
                      <div className="pending-info">
                        <div className="pending-phone">
                          {displayName}
                          <span style={{ marginLeft: 8, background: isClient ? "#DBEAFE" : "#FEF3C7", color: isClient ? "#1E40AF" : "#92400E", padding: "1px 6px", borderRadius: 4, fontSize: "0.65rem", fontWeight: 700 }}>
                            {isClient ? "CLIENT" : "AFFILIÉ"}
                          </span>
                        </div>
                        <div className="pending-code">{formatDate(item.requested_at)}</div>
                      </div>
                      <div className="password-reset-form">
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Nouveau mot de passe"
                          value={newPasswordMap[item.id] || ""}
                          onChange={(e) => setNewPasswordMap(prev => ({...prev, [item.id]: e.target.value}))}
                          data-testid={`reset-newpass-${item.id}`}
                        />
                        <div className="pending-actions">
                          <button onClick={() => handleApprovePasswordReset(item.id)} className="btn btn-success btn-sm" data-testid={`reset-approve-${item.id}`} title="Réinitialiser + envoyer sur WhatsApp">
                            <CheckCircle size={16} />
                          </button>
                          <button onClick={() => handleRejectPasswordReset(item.id)} className="btn btn-danger btn-sm" data-testid={`reset-reject-${item.id}`}>
                            <XCircle size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <>
            <Leaderboard leaderboard={leaderboard} />
            <div className="card" style={{ marginTop: "1rem" }}>
              <h3 className="card-title"><Star size={18} /> Récompenses Mensuelles</h3>
              <button onClick={handleCalculateMonthlyRewards} className="btn btn-primary">
                <Award size={18} /> Calculer & Distribuer Récompenses du Mois
              </button>
              <p style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: "#64748B" }}>
                TOP 1 (min 100 clients): 500 Gdes • TOP 2 (min 50 clients): 250 Gdes
              </p>
            </div>
          </>
        )}

        {/* Tools Tab */}
        {activeTab === "tools" && (
          <>
            {/* Add Client */}
            <div className="card">
              <h3 className="card-title"><UserPlus size={18} /> Ajouter un client (Vente directe)</h3>
              <form onSubmit={handleAddClient}>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Code Affilié"
                    value={addClientCode}
                    onChange={(e) => setAddClientCode(e.target.value)}
                    required
                  />
                </div>
                <PhoneInputWithStatus
                  value={addClientPhone}
                  onChange={handleAdminPhoneChange}
                  phoneStatus={adminPhoneStatus}
                  checking={adminPhoneChecking}
                  placeholder="Numéro Client"
                  testId="admin-add-client-phone"
                />
                
                {/* Service Selection */}
                <div className="service-selection" style={{ marginTop: "0.75rem" }}>
                  <label className="form-label">Service</label>
                  <div className="service-options">
                    <button 
                      type="button"
                      className={`service-btn ${addClientService === "netflix" ? "active netflix" : ""}`}
                      onClick={() => setAddClientService("netflix")}
                    >
                      <Tv size={14} /> Netflix
                    </button>
                    <button 
                      type="button"
                      className={`service-btn ${addClientService === "prime_video" ? "active prime" : ""}`}
                      onClick={() => setAddClientService("prime_video")}
                    >
                      <Play size={14} /> Prime Video
                    </button>
                  </div>
                </div>
                
                {/* Plan Selection */}
                <div className="plan-selection" style={{ marginTop: "0.5rem" }}>
                  <label className="form-label">Plan</label>
                  <div className="plan-options">
                    <button 
                      type="button"
                      className={`plan-btn ${addClientPlan === "vip" ? "active" : ""}`}
                      onClick={() => setAddClientPlan("vip")}
                    >
                      <Crown size={14} /> VIP (100 Gdes)
                    </button>
                    <button 
                      type="button"
                      className={`plan-btn ${addClientPlan === "daily" ? "active" : ""}`}
                      onClick={() => setAddClientPlan("daily")}
                    >
                      <Clock size={14} /> Quotidien (2.5 Gdes)
                    </button>
                  </div>
                </div>
                
                {adminPhoneStatus?.exists && (
                  <div className="reassign-warning">
                    <AlertTriangle size={14} />
                    <span>Si vous continuez, vous pourrez réassigner ce client à {addClientCode}</span>
                  </div>
                )}
                <button 
                  type="submit" 
                  className="btn btn-primary btn-sm" 
                  style={{ marginTop: "0.75rem" }}
                >
                  <CheckCircle size={16} /> {adminPhoneStatus?.exists ? "Vérifier / Réassigner" : "Ajouter"}
                </button>
              </form>
            </div>

            {/* Manual Reward */}
            <div className="card">
              <h3 className="card-title"><Gift size={18} /> Donner un bonus manuel</h3>
              <form onSubmit={handleGiveReward}>
                <div className="form-row">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Code Affilié"
                    value={rewardCode}
                    onChange={(e) => setRewardCode(e.target.value)}
                    required
                  />
                  <input
                    type="number"
                    className="form-input"
                    placeholder="Montant (Gdes)"
                    value={rewardAmount}
                    onChange={(e) => setRewardAmount(e.target.value)}
                    required
                  />
                </div>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Raison du bonus"
                  value={rewardReason}
                  onChange={(e) => setRewardReason(e.target.value)}
                  required
                  style={{ marginTop: "0.5rem" }}
                />
                <button type="submit" className="btn btn-success btn-sm" style={{ marginTop: "0.75rem" }}>
                  <Gift size={16} /> Envoyer Bonus
                </button>
              </form>
            </div>

            {/* Targeted Notifications */}
            <div className="card">
              <h3 className="card-title"><Bell size={18} /> Envoyer un message</h3>
              <div className="msg-target-toggle">
                <button className={`msg-target-btn ${msgTarget === "affiliates" ? "active" : ""}`} onClick={() => setMsgTarget("affiliates")} data-testid="msg-target-affiliates">
                  Affiliés
                </button>
                <button className={`msg-target-btn ${msgTarget === "clients" ? "active" : ""}`} onClick={() => setMsgTarget("clients")} data-testid="msg-target-clients">
                  Clients
                </button>
              </div>
              <textarea
                className="form-input"
                placeholder={`Message pour tous les ${msgTarget === "affiliates" ? "affiliés" : "clients"}...`}
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                rows={3}
                style={{ marginTop: "0.5rem", resize: "vertical" }}
                data-testid="admin-message-input"
              />
              <button onClick={handleSendMessage} className="btn btn-primary btn-sm" style={{ marginTop: "0.75rem" }} disabled={sendingMsg} data-testid="send-message-btn">
                <Send size={16} /> {sendingMsg ? "Envoi..." : "Envoyer"}
              </button>
            </div>

            {/* Old global notification form */}
            <div className="card">
              <h3 className="card-title"><Bell size={18} /> Notification affiliés (ancienne)</h3>
              <form onSubmit={handleCreateNotification}>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Titre"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  required
                />
                <textarea
                  className="form-input"
                  placeholder="Message"
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  required
                  rows={3}
                  style={{ marginTop: "0.5rem", resize: "vertical" }}
                />
                <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: "0.75rem" }}>
                  <Send size={16} /> Envoyer à tous les affiliés
                </button>
              </form>
            </div>

            {/* Bonus */}
            <div className="card">
              <h3 className="card-title"><Tag size={18} /> Bonus affilies</h3>
              <p style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "0.75rem" }}>
                Cliquez pour creer un bonus. Les affilies doivent l'activer via leur dashboard. Usage unique par client.
              </p>
              <div className="promo-buttons" data-testid="promo-buttons">
                {[1, 2, 3, 4, 5].map(pct => (
                  <button key={pct} className="promo-pct-btn" onClick={() => handleCreatePromo(pct)} data-testid={`promo-${pct}`}>
                    {pct}%
                  </button>
                ))}
              </div>
              {promos.length > 0 && (
                <div className="promo-history">
                  <h4 style={{ fontSize: "0.75rem", fontWeight: 600, marginTop: "0.75rem", color: "#475569" }}>Bonus actifs</h4>
                  {promos.slice(0, 10).map(p => (
                    <div key={p.id} className="promo-history-item" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <span className="promo-pct-badge">{p.discount_percent}%</span>
                      <span style={{ flex: 1, fontSize: "0.75rem" }}>{p.activations} activations | {p.usages || 0} utilises</span>
                      <button
                        className={`btn btn-sm ${p.is_enabled ? "btn-warning" : "btn-success"}`}
                        style={{ fontSize: "0.65rem", padding: "0.2rem 0.5rem" }}
                        data-testid={`toggle-promo-${p.id}`}
                        onClick={async () => {
                          try {
                            const res = await apiCall("put", `/admin/promotions/${p.id}/toggle`);
                            toast.success(res.data.message);
                            fetchData();
                          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}>
                        {p.is_enabled ? "Desactiver" : "Activer"}
                      </button>
                      <span className="promo-date">{formatDate(p.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Affiliates List with Management */}
            <div className="card">
              <h3 className="card-title"><Users size={18} /> Gestion des affiliés</h3>
              {affiliates.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon"><Users size={24} /></div>
                  <div className="empty-state-text">Aucun affilié</div>
                </div>
              ) : (
                <div className="affiliates-mgmt-list">
                  {affiliates.map((aff) => (
                    <div key={aff.id} className={`affiliate-mgmt-card ${aff.status === "disabled" ? "disabled" : ""}`} data-testid={`aff-${aff.code}`}>
                      <div className="aff-info">
                        <div className="aff-code">
                          {aff.code}
                          {aff.role === "sub_admin" && (
                            <span style={{ marginLeft: 8, background: "#FEF3C7", color: "#92400E", padding: "2px 8px", borderRadius: 6, fontSize: "0.7rem", fontWeight: 700 }} data-testid={`aff-${aff.code}-subadmin-badge`}>
                              ADMIN ADJOINT
                            </span>
                          )}
                        </div>
                        <div className="aff-stats">{aff.clients} clients | {aff.gains.toFixed(0)} Gdes | <span className={`level-badge-sm ${getLevelClass(aff.level)}`}>{aff.level}</span></div>
                        {aff.status === "disabled" && <span className="aff-disabled-badge">Désactivé</span>}
                      </div>
                      <div className="aff-actions">
                        {user?.role === "admin" && (
                          <button
                            onClick={() => handleToggleSubAdmin(aff)}
                            className={`btn btn-sm ${aff.role === "sub_admin" ? "btn-warning" : ""}`}
                            style={aff.role === "sub_admin" ? {} : { background: "#7C3AED", color: "white", borderColor: "#7C3AED" }}
                            data-testid={`toggle-subadmin-${aff.code}`}
                            title={aff.role === "sub_admin" ? "Rétrograder en simple affilié" : "Promouvoir en Administrateur Adjoint"}
                          >
                            {aff.role === "sub_admin" ? "Rétrograder" : "Promouvoir Adjoint"}
                          </button>
                        )}
                        <button onClick={() => handleToggleAffiliate(aff.id)} className={`btn btn-sm ${aff.status === "approved" ? "btn-warning" : "btn-success"}`} data-testid={`toggle-aff-${aff.code}`}>
                          {aff.status === "approved" ? "Désactiver" : "Réactiver"}
                        </button>
                        {confirmDelete === aff.id ? (
                          <div className="confirm-delete">
                            <span style={{ fontSize: "0.7rem", color: "#DC2626" }}>Confirmer ?</span>
                            <button onClick={() => handleDeleteAffiliate(aff.id)} className="btn btn-danger btn-sm" data-testid={`confirm-delete-${aff.code}`}>Oui</button>
                            <button onClick={() => setConfirmDelete(null)} className="btn btn-sm">Non</button>
                          </div>
                        ) : (
                          <button onClick={() => setConfirmDelete(aff.id)} className="btn btn-danger btn-sm" data-testid={`delete-aff-${aff.code}`}>
                            <XCircle size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Site Stats */}
            {siteStats && (
              <div className="card" data-testid="admin-site-stats">
                <h3 className="card-title"><TrendingUp size={18} /> Statistiques du site</h3>
                <div className="stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                  <div className="stat-card">
                    <div className="stat-value">{siteStats.total_visits}</div>
                    <div className="stat-label">Visites totales</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{siteStats.total_clients}</div>
                    <div className="stat-label">Utilisateurs</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{siteStats.total_orders}</div>
                    <div className="stat-label">Commandes</div>
                  </div>
                </div>
                <div style={{ marginTop: "0.75rem", display: "flex", gap: "1rem", flexWrap: "wrap", fontSize: "0.75rem", color: "#64748B" }}>
                  <span>Aujourd'hui: {siteStats.visitors_today} visites ({siteStats.unique_visitors_today} uniques)</span>
                  <span>Affilies: {siteStats.total_affiliates}</span>
                  <span>Avis: {siteStats.total_reviews}</span>
                </div>
              </div>
            )}

            {/* Popup Management */}
            <div className="card" data-testid="admin-popup-management">
              <h3 className="card-title"><Tag size={18} /> Popup promotionnel</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <input type="text" className="form-input" placeholder="URL de l'image (ou uploader ci-dessous)"
                  value={popupImageUrl} onChange={e => setPopupImageUrl(e.target.value)} data-testid="popup-image-url" />
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <label className="btn btn-outline btn-sm" style={{ cursor: "pointer" }}>
                    Upload Image
                    <input type="file" accept="image/*" style={{ display: "none" }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingPopup(true);
                        try {
                          const fd = new FormData();
                          fd.append("file", file);
                          const token = localStorage.getItem("token");
                          const res = await axios.post(`${API}/admin/upload-image`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
                          setPopupImageUrl(res.data.image_url);
                          toast.success("Image uploadee");
                        } catch { toast.error("Erreur upload"); }
                        finally { setUploadingPopup(false); }
                      }}
                    />
                  </label>
                  {uploadingPopup && <span style={{ fontSize: "0.75rem", color: "#64748B" }}>Upload en cours...</span>}
                </div>
                <input type="text" className="form-input" placeholder="Lien cliquable (optionnel)"
                  value={popupLinkUrl} onChange={e => setPopupLinkUrl(e.target.value)} data-testid="popup-link-url" />
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                  <label className="form-label" style={{ marginBottom: 0, whiteSpace: "nowrap" }}>Duree (heures):</label>
                  <input type="number" className="form-input" style={{ width: "80px" }}
                    value={popupDuration} onChange={e => setPopupDuration(parseInt(e.target.value) || 24)} min={1} data-testid="popup-duration" />
                </div>
                <button className="btn btn-primary btn-sm" data-testid="create-popup-btn"
                  onClick={async () => {
                    if (!popupImageUrl) { toast.error("Ajoutez une image"); return; }
                    try {
                      await apiCall("post", "/admin/popup", { image_url: popupImageUrl, link_url: popupLinkUrl || null, is_active: true, duration_hours: popupDuration });
                      toast.success("Popup cree et active!");
                      setPopupImageUrl(""); setPopupLinkUrl(""); setPopupDuration(24); fetchData();
                    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                  }}>
                  Creer Popup
                </button>
              </div>
              {popups.length > 0 && (
                <div style={{ marginTop: "1rem" }}>
                  <h4 style={{ fontSize: "0.8rem", fontWeight: 600, color: "#475569", marginBottom: "0.5rem" }}>Historique Popups</h4>
                  {popups.map(p => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.4rem 0", borderBottom: "1px solid #F1F5F9" }}>
                      <img src={p.image_url} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                      <div style={{ flex: 1, fontSize: "0.75rem" }}>
                        <span style={{ fontWeight: 600 }}>{p.is_active ? "Actif" : "Inactif"}</span> | {p.duration_hours}h
                        <div style={{ color: "#94A3B8", fontSize: "0.65rem" }}>{formatDate(p.created_at)}</div>
                      </div>
                      <button onClick={async () => {
                        try { await apiCall("put", `/admin/popup/${p.id}/toggle`); toast.success("Popup modifie"); fetchData(); }
                        catch { toast.error("Erreur"); }
                      }} className="btn btn-sm btn-outline" data-testid={`toggle-popup-${p.id}`}>
                        {p.is_active ? "Desactiver" : "Activer"}
                      </button>
                      <button onClick={async () => {
                        try { await apiCall("delete", `/admin/popup/${p.id}`); toast.success("Popup supprime"); fetchData(); }
                        catch { toast.error("Erreur"); }
                      }} className="btn btn-sm btn-danger" data-testid={`delete-popup-${p.id}`}>
                        <XCircle size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Pending Orders Tab */}
        {activeTab === "orders" && (
          <div className="card">
            <h3 className="card-title"><ShoppingCart size={18} /> Commandes clients en attente</h3>
            {pendingOrders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon"><CheckCircle size={24} /></div>
                <div className="empty-state-text">Aucune commande en attente</div>
              </div>
            ) : (
              <div>
                {pendingOrders.map((order) => (
                  <div key={order.id} className="pending-card" data-testid={`order-${order.id}`} style={{ borderLeft: "3px solid #2563EB", marginBottom: "1rem" }}>
                    <div className="pending-info" style={{ width: "100%" }}>
                      <div className="pending-phone" style={{ fontWeight: 700 }}>{order.client_name} ({order.client_phone})</div>
                      <div className="pending-code">
                        {order.service_name} - {order.plan_name} | <strong>{order.price.toLocaleString()} Gdes</strong>
                        {order.duration_days > 0 && <span> | {order.duration_days}j</span>}
                      </div>
                      {order.profile_name && (
                        <div style={{ fontSize: "0.78rem", color: "#1E40AF", marginTop: "0.2rem" }}>
                          Profil: <strong>{order.profile_name}</strong>
                        </div>
                      )}
                      {/* Recipient info (client_payload) — admin needs visibility too. */}
                      <RecipientInfo payload={order.client_payload} testidPrefix={`admin-recipient-${order.id}`} />
                      {/* Free Fire Topup Bot — assign code + launch automation. */}
                      {order.service_id === "freefire" && (
                        <FreeFireTopupActions order={order} apiCall={apiCall} onChange={fetchData} />
                      )}
                      <div className="pending-product" style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", alignItems: "center", marginTop: "0.2rem" }}>
                        {order.transaction_id && <span className="plan-badge vip" style={{ fontSize: "0.7rem" }}>ID: {order.transaction_id}</span>}
                        {order.affiliate_code ? (
                          <span className="product-badge netflix" style={{ fontSize: "0.7rem" }}>Affilié: {order.affiliate_code}</span>
                        ) : (
                          <span style={{ fontSize: "0.7rem", padding: "0.1rem 0.4rem", borderRadius: "999px", background: "#FEF3C7", color: "#92400E" }}>Pas d'affilié</span>
                        )}
                        {/* AI verification badge */}
                        {order.ai_verdict && order.ai_verdict !== "skipped" && (() => {
                          const v = order.ai_verdict;
                          const meta = v === "verified"
                            ? { bg: "#DCFCE7", color: "#166534", label: "✅ IA Vérifié", border: "#86EFAC" }
                            : v === "suspect"
                            ? { bg: "#FEF3C7", color: "#92400E", label: "⚠️ IA Suspect", border: "#FCD34D" }
                            : { bg: "#FEE2E2", color: "#B91C1C", label: "❌ IA Rejeté", border: "#FCA5A5" };
                          return (
                            <span
                              style={{
                                fontSize: "0.7rem", fontWeight: 700,
                                padding: "0.1rem 0.45rem", borderRadius: "999px",
                                background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                                cursor: "pointer"
                              }}
                              data-testid={`ai-verdict-${order.id}`}
                              title={
                                (order.ai_verification?.reasons || []).join(" • ")
                                + (order.ai_verification?.extracted ? `\nMontant lu: ${order.ai_verification.extracted.amount} | Numéro: ${order.ai_verification.extracted.recipient_phone || "—"} | Date: ${order.ai_verification.extracted.date || "—"}` : "")
                              }
                            >
                              {meta.label}
                            </span>
                          );
                        })()}
                      </div>

                      {/* AI verification details box */}
                      {order.ai_verification && order.ai_verification.extracted && (
                        <div style={{
                          marginTop: "0.4rem",
                          padding: "0.45rem 0.6rem",
                          background: "#F8FAFC",
                          border: "1px dashed #CBD5E1",
                          borderRadius: "8px",
                          fontSize: "0.7rem",
                          color: "#475569",
                          lineHeight: 1.45,
                        }}>
                          <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>🤖 Analyse IA :</div>
                          <div>Montant lu : <b>{order.ai_verification.extracted.amount ?? "—"}</b> {order.ai_verification.extracted.currency || ""}</div>
                          <div>Numéro destinataire : <b>{order.ai_verification.extracted.recipient_phone || "—"}</b></div>
                          <div>Date : {order.ai_verification.extracted.date || "—"}</div>
                          {(order.ai_verification.reasons || []).length > 0 && (
                            <div style={{ marginTop: 3, color: "#92400E" }}>
                              {order.ai_verification.reasons.map((r, i) => <div key={`${order.id}-r-${i}`}>• {r}</div>)}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Payment proof */}
                      {order.payment_proof_url && order.payment_proof_url.startsWith("data:image") && (
                        <div style={{ marginTop: "0.5rem" }}>
                          <div style={{ fontSize: "0.75rem", color: "#64748B", marginBottom: "0.2rem" }}>Preuve de paiement:</div>
                          <img src={order.payment_proof_url} alt="Preuve" style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: "8px", border: "1px solid #E2E8F0", cursor: "pointer" }}
                            onClick={() => window.open(order.payment_proof_url, "_blank")} />
                        </div>
                      )}
                      <div className="pending-code" style={{ fontSize: "0.7rem" }}>{formatDate(order.created_at)}</div>
                      
                      {/* Resource selection for validation */}
                      {showOrderResources === order.id && orderResources[order.id] && (
                        <div style={{ marginTop: "0.4rem", padding: "0.5rem", background: "#F0FDF4", borderRadius: "8px", border: "1px solid #BBF7D0" }}>
                          <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#16A34A", marginBottom: "0.3rem" }}>
                            <Key size={12} style={{ verticalAlign: "middle" }} /> Sélectionnez les ressources à attribuer:
                          </div>
                          {orderResources[order.id].length === 0 ? (
                            <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>Aucune ressource disponible pour ce plan</div>
                          ) : (
                            orderResources[order.id].map(r => {
                              const isSelected = (selectedResources[order.id] || []).includes(r.id);
                              return (
                                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: "0.3rem", padding: "0.25rem 0.4rem", marginBottom: "0.15rem", borderRadius: "6px", background: isSelected ? "#DCFCE7" : "#FFF", border: `1px solid ${isSelected ? "#16A34A" : "#E2E8F0"}`, cursor: "pointer", fontSize: "0.75rem" }}
                                  onClick={() => toggleResourceSelection(order.id, r.id)}
                                  data-testid={`select-res-${r.id}`}>
                                  <input type="checkbox" checked={isSelected} readOnly style={{ accentColor: "#16A34A" }} />
                                  <code style={{ flex: 1, wordBreak: "break-all" }}>{r.content}</code>
                                </div>
                              );
                            })
                          )}
                        </div>
                      )}
                      
                      {/* 2026-06-08 — Notify any affiliate via WhatsApp with the
                          full order brief. The admin picks which affiliate
                          will process the order in-game. For Free Fire orders,
                          the unlocked code is auto-injected once assigned. */}
                      {(() => {
                        const approved = (affiliates || []).filter(a => (a.status || "approved") === "approved");
                        if (approved.length === 0) return null;
                        const buildMessage = () => {
                          const lines = [];
                          lines.push("🛎️ Commande à traiter — SMARTECH PRO");
                          lines.push(`Client : ${order.client_name || "—"} (${order.client_phone || "—"})`);
                          lines.push(`Service : ${order.service_name || "—"}${order.plan_name ? " — " + order.plan_name : ""}`);
                          if (order.duration_days > 0) lines.push(`Durée : ${order.duration_days} jour(s)`);
                          // Show NET amount when the service charges a percentage fee.
                          // The admin must NOT send the gross amount — they'd lose the fee.
                          const feePct = Number(order.service_fee_percent || 0);
                          const gross = Number(order.price || order.original_price || 0);
                          if (feePct > 0 && gross > 0) {
                            const feeAmt = gross * feePct / 100;
                            const net = gross - feeAmt;
                            lines.push(`Prix payé par le client : ${gross.toLocaleString("fr-FR")} Gdes`);
                            lines.push(`Frais plateforme (${feePct}%) : ${feeAmt.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} Gdes`);
                            lines.push(`💰 *Montant NET à envoyer* : ${net.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} Gdes`);
                          } else {
                            lines.push(`Prix : ${gross.toLocaleString("fr-FR")} Gdes`);
                          }
                          if (order.transaction_id) lines.push(`ID transaction : ${order.transaction_id}`);
                          if (order.profile_name) lines.push(`Profil : ${order.profile_name}`);
                          if (order.payment_method) lines.push(`Mode paiement : ${order.payment_method}`);
                          if (order.custom_amount_usd) lines.push(`Montant USD : $${order.custom_amount_usd}`);
                          // Free Fire specifics
                          if (order.service_id === "freefire") {
                            const code = order.freefire_code || "";
                            if (code) lines.push(`🎮 Code Free Fire débloqué : ${code}`);
                            else lines.push("🎮 Code Free Fire : (à débloquer par l'admin)");
                          }
                          // Recipient payload — dump EVERY entry the client filled
                          // so agents never miss a phone/wallet/link/etc.
                          const cp = order.client_payload || {};
                          const skipKeys = new Set(["computed_price_htg", "base_price_htg", "type"]);
                          const labelize = (k) => k.replace(/_/g, " ");
                          const cpLines = [];
                          for (const [k, v] of Object.entries(cp)) {
                            if (skipKeys.has(k)) continue;
                            if (v === null || v === undefined || v === "") continue;
                            const val = typeof v === "object" ? JSON.stringify(v) : String(v);
                            cpLines.push(`  • ${labelize(k)} : ${val}`);
                          }
                          if (cpLines.length > 0) {
                            lines.push("Détails commande :");
                            lines.push(...cpLines);
                          }
                          if (order.affiliate_code) lines.push(`Affilié d'origine : ${order.affiliate_code}`);
                          lines.push("");
                          lines.push("Merci de traiter la commande puis de confirmer.");
                          return lines.join("\n");
                        };
                        return (
                          <div
                            data-testid={`order-affiliates-row-${order.id}`}
                            style={{
                              marginTop: "0.55rem",
                              padding: "0.55rem 0.65rem",
                              background: "#F8FAFC",
                              border: "1px dashed #CBD5E1",
                              borderRadius: 8,
                            }}
                          >
                            <div style={{ fontSize: "0.72rem", color: "#475569", fontWeight: 700, marginBottom: 4 }}>
                              📣 Notifier un affilié pour traitement
                            </div>
                            {/* Aperçu live du message — native <details> so no
                                extra React state is needed. The admin can
                                verify all client info is present before sending. */}
                            <details
                              data-testid={`order-preview-${order.id}`}
                              style={{
                                marginBottom: 6,
                                background: "#FFFFFF",
                                border: "1px solid #E2E8F0",
                                borderRadius: 6,
                                padding: "0.35rem 0.55rem",
                              }}
                            >
                              <summary
                                data-testid={`order-preview-toggle-${order.id}`}
                                style={{ cursor: "pointer", fontSize: "0.7rem", fontWeight: 700, color: "#1E40AF", userSelect: "none" }}
                              >
                                👁️ Voir le message qui sera envoyé
                              </summary>
                              <pre
                                data-testid={`order-preview-text-${order.id}`}
                                style={{
                                  marginTop: 6,
                                  background: "#F8FAFC",
                                  padding: "0.5rem 0.7rem",
                                  borderRadius: 6,
                                  fontSize: "0.72rem",
                                  lineHeight: 1.5,
                                  color: "#0F172A",
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                  maxHeight: 260,
                                  overflowY: "auto",
                                  fontFamily: "inherit",
                                }}
                              >{buildMessage()}</pre>
                            </details>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                              {/* Group WhatsApp shortcut — broadcasts to ALL agents.
                                  WA group invite links don't support pre-filled
                                  text, so we copy the message to clipboard then
                                  open the group in a new tab. */}
                              <button
                                type="button"
                                data-testid={`order-${order.id}-affiliate-group`}
                                onClick={async () => {
                                  const msg = buildMessage();
                                  try { await navigator.clipboard.writeText(msg); }
                                  catch { /* clipboard might be blocked — ignore */ }
                                  toast.success("Message copié — collez-le dans le groupe WhatsApp", { duration: 4500 });
                                  window.open("https://chat.whatsapp.com/BnxKzPYl8PZDSRDkb5uTsw", "_blank", "noopener,noreferrer");
                                }}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  background: "linear-gradient(95deg, #7C3AED, #DB2777)",
                                  color: "#FFFFFF",
                                  border: "none",
                                  padding: "3px 11px",
                                  borderRadius: 999,
                                  fontSize: "0.72rem", fontWeight: 800,
                                  cursor: "pointer",
                                  boxShadow: "0 2px 6px rgba(124,58,237,0.25)",
                                }}
                                title="Copie le message et ouvre le groupe WhatsApp Agents"
                              >
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                Groupe Agents
                              </button>
                              <span style={{ height: 18, width: 1, background: "#CBD5E1", margin: "0 4px" }} aria-hidden />
                              {approved.map(aff => {
                                const digits = String(aff.whatsapp || aff.phone || "").replace(/\D/g, "");
                                const disabled = !digits;
                                return (
                                  <button
                                    key={aff.id || aff.code}
                                    type="button"
                                    data-testid={`order-${order.id}-affiliate-${aff.code}`}
                                    disabled={disabled}
                                    title={disabled ? "Aucun WhatsApp enregistré pour cet affilié" : `Envoyer à ${aff.code}`}
                                    onClick={() => {
                                      if (disabled) return;
                                      const msg = buildMessage();
                                      window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
                                    }}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 4,
                                      background: disabled ? "#E2E8F0" : "#16A34A",
                                      color: disabled ? "#94A3B8" : "#FFFFFF",
                                      border: "none",
                                      padding: "3px 9px",
                                      borderRadius: 999,
                                      fontSize: "0.72rem", fontWeight: 700,
                                      cursor: disabled ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                                    {aff.full_name || aff.code}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="pending-actions" style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem", flexWrap: "wrap" }}>
                        {order.client_phone && (
                          <button
                            className="btn btn-sm"
                            style={{ background: "#DCFCE7", color: "#15803D", fontSize: "0.75rem", fontWeight: 600 }}
                            data-testid={`order-whatsapp-${order.id}`}
                            onClick={() => {
                              const digits = String(order.client_phone || "").replace(/\D/g, "");
                              if (!digits) { toast.error("Numéro WhatsApp manquant"); return; }
                              const msg = "Bonjour, nous avons bien reçu votre commande. Merci de patienter 5 à 10 minutes pendant le traitement.";
                              window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
                            }}
                            title="Envoyer un message WhatsApp au client"
                          >
                            <Send size={14} /> WhatsApp
                          </button>
                        )}
                        <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", fontSize: "0.75rem" }} data-testid={`show-resources-${order.id}`}
                          onClick={() => {
                            if (showOrderResources === order.id) { setShowOrderResources(null); }
                            else { setShowOrderResources(order.id); fetchOrderResources(order.id); }
                          }}>
                          <Key size={14} /> {showOrderResources === order.id ? "Masquer" : "Ressources"}
                        </button>
                        <button onClick={() => handleValidateOrder(order.id)}
                          disabled={validatingOrderId === order.id}
                          className="btn btn-success btn-sm"
                          data-testid={`validate-order-${order.id}`}
                          style={validatingOrderId === order.id ? { opacity: 0.6, cursor: "not-allowed" } : {}}>
                          <CheckCircle size={16} /> {validatingOrderId === order.id ? "Validation…" : `Valider ${(selectedResources[order.id] || []).length > 0 ? `(${(selectedResources[order.id] || []).length})` : ""}`}
                        </button>
                        <button onClick={() => handleRejectOrder(order.id)} className="btn btn-danger btn-sm">
                          <XCircle size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Pending Deliveries - shown in orders tab */}
        {activeTab === "orders" && pendingDeliveries.length > 0 && (
          <div className="card" data-testid="admin-pending-deliveries" style={{ marginTop: "1rem" }}>
            <h3 className="card-title" style={{ color: "#D97706" }}><Clock size={18} /> Livraisons en attente de validation ({pendingDeliveries.length})</h3>
            {pendingDeliveries.map((order) => (
              <div key={order.id} className="pending-card" data-testid={`delivery-${order.id}`} style={{ borderLeft: "3px solid #D97706", marginBottom: "0.75rem" }}>
                <div className="pending-info" style={{ width: "100%" }}>
                  <div className="pending-phone" style={{ fontWeight: 700 }}>{order.client_name} ({order.client_phone})</div>
                  <div className="pending-code">
                    {order.service_name} - {order.plan_name} | <strong>{order.price.toLocaleString()} Gdes</strong>
                  </div>
                  <div className="pending-product" style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", alignItems: "center", marginTop: "0.2rem" }}>
                    <span style={{ fontSize: "0.7rem", padding: "0.1rem 0.4rem", borderRadius: "999px", background: "#FEF3C7", color: "#92400E" }}>Affilié: {order.affiliate_code}</span>
                    <span style={{ fontSize: "0.65rem", color: "#94A3B8" }}>{formatDate(order.affiliate_submitted_at)}</span>
                  </div>
                  {/* Delivery proof */}
                  {order.delivery_proof_url && order.delivery_proof_url.startsWith("data:image") && (
                    <div style={{ marginTop: "0.4rem" }}>
                      <div style={{ fontSize: "0.72rem", color: "#64748B", marginBottom: "0.2rem" }}>Preuve de livraison:</div>
                      <img src={order.delivery_proof_url} alt="Preuve" style={{ maxWidth: "200px", maxHeight: "150px", borderRadius: "8px", border: "1px solid #E2E8F0", cursor: "pointer" }}
                        onClick={() => window.open(order.delivery_proof_url, "_blank")} />
                    </div>
                  )}
                  {/* Resources assigned */}
                  {order.assigned_resources && order.assigned_resources.length > 0 && (
                    <div style={{ marginTop: "0.3rem", fontSize: "0.72rem", color: "#475569" }}>
                      Stock attribué: {order.assigned_resources.map(r => r.content).join(", ")}
                    </div>
                  )}
                  <div className="pending-actions" style={{ marginTop: "0.5rem", display: "flex", gap: "0.4rem" }}>
                    <button className="btn btn-success btn-sm" data-testid={`admin-validate-delivery-${order.id}`}
                      onClick={async () => {
                        try {
                          const res = await apiCall("post", `/admin/validate-delivery/${order.id}`);
                          toast.success(res.data.message);
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                      }}>
                      <CheckCircle size={16} /> Valider livraison
                    </button>
                    <button className="btn btn-danger btn-sm" data-testid={`admin-reject-delivery-${order.id}`}
                      onClick={async () => {
                        try {
                          await apiCall("delete", `/admin/reject-delivery/${order.id}`);
                          toast.success("Livraison rejetée, renvoyée à l'affilié");
                          fetchData();
                        } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                      }}>
                      <XCircle size={16} /> Rejeter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}



        {/* Subscriptions Tab */}
        {activeTab === "subscriptions" && (
          <>
            {/* Streaming master accounts management (former "Outils Streaming"
                tab, now embedded as a collapsible panel for one-stop admin). */}
            <Suspense fallback={null}>
              <StreamingAccountsPanel apiCall={apiCall} />
            </Suspense>
            {/* Creation Form */}
            <div className="card" data-testid="admin-create-subscription-form">
              <h3 className="card-title"><CalendarDays size={18} /> Créer un abonnement</h3>
              <form onSubmit={handleCreateSubscription} style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <input type="tel" className="form-input" placeholder="Numéro du client" value={newSubPhone} onChange={e => setNewSubPhone(e.target.value)} required data-testid="sub-phone-input" />
                <input type="text" className="form-input" placeholder="Nom du client" value={newSubName} onChange={e => setNewSubName(e.target.value)} required data-testid="sub-name-input" />
                <select className="form-input" value={newSubService} onChange={e => setNewSubService(e.target.value)} data-testid="sub-service-select">
                  {catalogServices.length > 0 ? catalogServices.map(s => (
                    <option key={s.id} value={s.name}>{s.name}</option>
                  )) : (
                    <>
                      <option value="Netflix">Netflix</option>
                      <option value="Prime Video">Prime Video</option>
                      <option value="Disney+">Disney+</option>
                    </>
                  )}
                </select>
                <input type="number" className="form-input" placeholder="Durée (en jours)" value={newSubDays} onChange={e => setNewSubDays(e.target.value)} min="1" required data-testid="sub-days-input" />
                <input type="email" className="form-input" placeholder="Email streaming (optionnel — modifiable plus tard)" value={newSubEmail} onChange={e => setNewSubEmail(e.target.value)} data-testid="sub-streaming-email-input" />
                <div style={{ fontSize: "0.7rem", color: "#64748B", marginTop: "-0.25rem" }}>
                  Si un email est fourni, le client est ajouté automatiquement dans le compte streaming. Sinon, vous pourrez l'attribuer plus tard.
                </div>
                <button type="submit" className="btn btn-primary" disabled={creatingSub} data-testid="sub-create-btn">
                  {creatingSub ? "Création..." : <><CalendarDays size={16} /> Créer l'abonnement</>}
                </button>
              </form>
            </div>

            <div className="card" data-testid="admin-subscriptions-tab">
              <h3 className="card-title"><CalendarDays size={18} /> Gestion des abonnements</h3>
              
              {/* Search */}
              <input className="form-input" style={{ marginBottom: "0.75rem" }} placeholder="Rechercher par nom, téléphone ou service..." value={subSearch} onChange={e => { setSubSearch(e.target.value); }} data-testid="sub-search" />

              {/* Expiring Soon Alert */}
              {expiringSoon.length > 0 && (
                <div style={{ padding: "0.6rem", marginBottom: "0.75rem", borderRadius: "10px", background: "#FFFBEB", border: "2px solid #FDE68A" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#D97706", marginBottom: "0.4rem" }}>
                    <AlertTriangle size={14} style={{ verticalAlign: "middle" }} /> {expiringSoon.length} abonnement(s) expirent dans les 48h
                  </div>
                  {expiringSoon.map((s, i) => (
                    <div key={s.id || s._id || `exp-${i}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.3rem 0.4rem", marginBottom: "0.2rem", borderRadius: "6px", background: "#FFF", fontSize: "0.75rem" }}>
                      <div>
                        <span style={{ fontWeight: 600 }}>{s.client_name}</span> — {s.service_name} <span style={{ color: "#D97706", fontWeight: 600 }}>({s.hours_left}h restantes)</span>
                      </div>
                      <a href={s.whatsapp_link} target="_blank" rel="noopener noreferrer"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.15rem", background: "#25D366", color: "white", border: "none", borderRadius: "6px", padding: "0.15rem 0.4rem", fontSize: "0.65rem", fontWeight: 600, textDecoration: "none" }}>
                        <MessageSquareText size={11} /> Notifier
                      </a>
                    </div>
                  ))}
                </div>
              )}

              {/* Subscription Stats */}
              {subStats && (
                <div className="stats-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)", marginBottom: "1rem" }}>
                  <div className="stat-card" style={{ cursor: "pointer", border: subFilter === "all" ? "2px solid #2563EB" : "none" }} onClick={() => setSubFilter("all")}>
                    <div className="stat-value">{subStats.total}</div>
                    <div className="stat-label">Total</div>
                  </div>
                  <div className="stat-card" style={{ cursor: "pointer", border: subFilter === "pending" ? "2px solid #D97706" : "none" }} onClick={() => setSubFilter("pending")}>
                    <div className="stat-value" style={{ color: "#D97706" }}>{subStats.pending}</div>
                    <div className="stat-label">En attente</div>
                  </div>
                  <div className="stat-card" style={{ cursor: "pointer", border: subFilter === "active" ? "2px solid #16A34A" : "none" }} onClick={() => setSubFilter("active")}>
                    <div className="stat-value" style={{ color: "#16A34A" }}>{subStats.active}</div>
                    <div className="stat-label">Actifs</div>
                  </div>
                  <div className="stat-card" style={{ cursor: "pointer", border: subFilter === "expired" ? "2px solid #DC2626" : "none" }} onClick={() => setSubFilter("expired")}>
                    <div className="stat-value" style={{ color: "#DC2626" }}>{subStats.expired}</div>
                    <div className="stat-label">Expirés</div>
                  </div>
                </div>
              )}

              {/* Type stats row */}
              {subStats && (
                <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                  <span style={{ background: "#DBEAFE", color: "#1E40AF", padding: "0.25rem 0.6rem", borderRadius: "999px", fontSize: "0.75rem" }}>
                    <UserPlus size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} /> Nouveaux: {subStats.new_count}
                  </span>
                  <span style={{ background: "#FEF3C7", color: "#92400E", padding: "0.25rem 0.6rem", borderRadius: "999px", fontSize: "0.75rem" }}>
                    <RotateCcw size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} /> Renouvellements: {subStats.renewal_count}
                  </span>
                  <span style={{ background: "#F3E8FF", color: "#7C3AED", padding: "0.25rem 0.6rem", borderRadius: "999px", fontSize: "0.75rem" }}>
                    <ArrowUpCircle size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} /> Upgrades: {subStats.upgrade_count}
                  </span>
                </div>
              )}

              {/* Subscription List — sorted by streaming_email so clients
                  sharing an account are grouped together (2026-06-08 spec). */}
              {(() => {
                let filtered = subscriptions.filter(s => subFilter === "all" || s.status === subFilter);
                if (subSearch.trim()) {
                  const q = subSearch.toLowerCase();
                  filtered = filtered.filter(s =>
                    (s.client_name || "").toLowerCase().includes(q) ||
                    (s.client_phone || "").toLowerCase().includes(q) ||
                    (s.service_name || "").toLowerCase().includes(q) ||
                    (s.streaming_email || "").toLowerCase().includes(q)
                  );
                }
                // Group-by email : keep the rendering flat but sort by
                // streaming_email so all clients on the same account appear consecutively.
                filtered = [...filtered].sort((a, b) => {
                  const ea = (a.streaming_email || "zzz_no_email").toLowerCase();
                  const eb = (b.streaming_email || "zzz_no_email").toLowerCase();
                  if (ea !== eb) return ea < eb ? -1 : 1;
                  return (b.created_at || "").localeCompare(a.created_at || "");
                });
                let lastEmail = "__init__";
                return filtered.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon"><CalendarDays size={24} /></div>
                    <div className="empty-state-text">Aucun abonnement {subFilter === "pending" ? "en attente" : subFilter === "active" ? "actif" : subFilter === "expired" ? "expiré" : ""}</div>
                  </div>
                ) : (
                  <div>
                    {filtered.map((sub) => {
                      // Insert a sticky group header each time the streaming_email changes.
                      const emailKey = (sub.streaming_email || "").toLowerCase();
                      const showHeader = emailKey !== lastEmail;
                      lastEmail = emailKey;
                      const isPending = sub.status === "pending";
                      const isExpired = sub.status === "expired";
                      const isActive = sub.status === "active";
                      const isFinal = ["expired", "rejected", "completed", "cancelled"].includes((sub.status || "").toLowerCase());
                      const endDate = sub.end_date ? new Date(sub.end_date) : null;
                      const now = new Date();
                      const daysLeft = endDate ? Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24))) : 0;
                      const borderColor = isPending ? "#D97706" : isExpired ? "#DC2626" : "#16A34A";
                      return (
                        <React.Fragment key={sub.id}>
                          {showHeader && (
                            <div
                              data-testid={`sub-email-header-${emailKey || "no-email"}`}
                              style={{
                                background: emailKey ? "#EFF6FF" : "#FEF3C7",
                                color: emailKey ? "#1E40AF" : "#92400E",
                                padding: "0.35rem 0.6rem",
                                borderRadius: 6,
                                fontWeight: 800,
                                fontSize: "0.78rem",
                                marginTop: "0.5rem",
                                marginBottom: "0.25rem",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <Mail size={13} />
                              {emailKey || "Sans email streaming"}
                              {(() => {
                                const sameEmail = filtered.filter(s => (s.streaming_email || "").toLowerCase() === emailKey);
                                const active = sameEmail.filter(s => s.status === "active").length;
                                const expired = sameEmail.filter(s => ["expired", "rejected", "completed", "cancelled"].includes(s.status)).length;
                                const pending = sameEmail.filter(s => s.status === "pending").length;
                                return (
                                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4, alignItems: "center" }}>
                                    {active > 0 && <span style={{ background: "#16A34A", color: "#FFF", padding: "0 7px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 800 }}>✓ {active}</span>}
                                    {pending > 0 && <span style={{ background: "#F59E0B", color: "#FFF", padding: "0 7px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 800 }}>⏳ {pending}</span>}
                                    {expired > 0 && <span style={{ background: "#DC2626", color: "#FFF", padding: "0 7px", borderRadius: 999, fontSize: "0.68rem", fontWeight: 800, textDecoration: "line-through" }}>✕ {expired}</span>}
                                    <span style={{ fontSize: "0.68rem", opacity: 0.7 }}>· total {sameEmail.length}</span>
                                  </span>
                                );
                              })()}
                            </div>
                          )}
                        <div className="pending-card" data-testid={`sub-${sub.id}`} style={{ borderLeft: `3px solid ${borderColor}` }}>
                          <div className="pending-info">
                            <div className="pending-phone" style={isFinal ? { textDecoration: "line-through", color: "#DC2626" } : undefined}>
                              {sub.client_name}
                              <span style={{ fontSize: "0.75rem", color: "#64748B", marginLeft: "0.5rem" }}>({sub.client_phone})</span>
                            </div>
                            <div className="pending-code">
                              {sub.service_name} {sub.plan_name ? `- ${sub.plan_name}` : ""} | {sub.duration_days}j
                              {sub.price > 0 && <> | <strong>{sub.price.toLocaleString()} Gdes</strong></>}
                            </div>
                            <div className="pending-product" style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", alignItems: "center" }}>
                              <span style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", fontWeight: 600, background: isPending ? "#FEF3C7" : isExpired ? "#FEE2E2" : "#DCFCE7", color: isPending ? "#92400E" : isExpired ? "#DC2626" : "#16A34A" }}>
                                {isPending ? "En attente" : isExpired ? "Expiré" : `Actif (${daysLeft}j restants)`}
                              </span>
                              <span style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: sub.type === "new" ? "#DBEAFE" : sub.type === "renewal" ? "#FEF3C7" : "#F3E8FF", color: sub.type === "new" ? "#1E40AF" : sub.type === "renewal" ? "#92400E" : "#7C3AED" }}>
                                {sub.type === "new" ? "Nouveau" : sub.type === "renewal" ? "Renouvellement" : "Upgrade"}
                              </span>
                              {sub.affiliate_code && (
                                <span style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#E0E7FF", color: "#3730A3" }}>
                                  Affilié: {sub.affiliate_code}
                                </span>
                              )}
                              {sub.streaming_email ? (
                                <span data-testid={`sub-email-${sub.id}`} style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#F1F5F9", color: "#0F172A", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <Mail size={11} /> {sub.streaming_email}
                                </span>
                              ) : (
                                <span data-testid={`sub-no-email-${sub.id}`} style={{ padding: "0.15rem 0.5rem", borderRadius: "999px", fontSize: "0.7rem", background: "#FEF3C7", color: "#92400E", fontWeight: 700 }}>
                                  ⚠️ Sans email
                                </span>
                              )}
                            </div>
                            {!isPending && sub.start_date && (
                              <div className="pending-code" style={{ fontSize: "0.7rem" }}>
                                Début: {formatDate(sub.start_date)} | Fin: {formatDate(sub.end_date)}
                              </div>
                            )}
                          </div>
                          <div className="pending-actions">
                            {/* 2026-06-08 — admin can change the streaming email on any
                                 subscription (covers : sub created without email, OR client
                                 wants a different account on renewal). */}
                            <button
                              type="button"
                              data-testid={`edit-email-${sub.id}`}
                              className="btn btn-sm"
                              style={{ background: "#F1F5F9", color: "#0F172A", fontSize: "0.72rem", padding: "0.3rem 0.55rem", borderRadius: 8, border: "1px solid #CBD5E1" }}
                              onClick={async () => {
                                const next = window.prompt(
                                  sub.streaming_email
                                    ? `Modifier l'email streaming actuel (${sub.streaming_email}) :`
                                    : "Saisissez l'email streaming à attacher à cet abonnement :",
                                  sub.streaming_email || "",
                                );
                                if (!next) return;
                                try {
                                  await apiCall("put", `/admin/subscriptions/${sub.id}/email`, { streaming_email: next.trim().toLowerCase() });
                                  toast.success("Email streaming mis à jour");
                                  fetchData();
                                } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                              }}
                            >
                              <Mail size={13} /> {sub.streaming_email ? "Modifier email" : "Ajouter email"}
                            </button>
                            {isFinal && (
                              <button
                                type="button"
                                data-testid={`delete-sub-${sub.id}`}
                                className="btn btn-sm btn-danger"
                                style={{ fontSize: "0.72rem", padding: "0.3rem 0.55rem" }}
                                onClick={async () => {
                                  if (!window.confirm(`Supprimer définitivement l'abonnement de ${sub.client_name} (${sub.service_name}) ?`)) return;
                                  try {
                                    await apiCall("delete", `/admin/subscriptions/${sub.id}`);
                                    toast.success("Abonnement supprimé");
                                    fetchData();
                                  } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                                }}
                              >
                                <Trash2 size={13} /> Supprimer
                              </button>
                            )}
                            {isPending && (
                              <>
                                <button onClick={() => handleValidateSubscription(sub.id)} className="btn btn-success btn-sm" data-testid={`validate-sub-${sub.id}`}>
                                  <CheckCircle size={16} /> Valider
                                </button>
                                <button onClick={() => handleRejectSubscription(sub.id)} className="btn btn-danger btn-sm" data-testid={`reject-sub-${sub.id}`}>
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                            {isExpired && (
                              <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", alignItems: "center" }}>
                                <input type="number" min="1" placeholder="Jours" style={{ width: 60, fontSize: "0.7rem", padding: "0.25rem 0.4rem", borderRadius: "6px", border: "1px solid #CBD5E1" }}
                                  data-testid={`renew-days-${sub.id}`}
                                  onChange={e => { sub._renewDays = e.target.value; }} />
                                <button className="btn btn-sm" data-testid={`renew-sub-${sub.id}`}
                                  style={{ background: "#2563EB", color: "white", display: "flex", alignItems: "center", gap: "0.2rem", fontSize: "0.72rem", padding: "0.3rem 0.5rem", borderRadius: "8px", border: "none", cursor: "pointer" }}
                                  onClick={async () => {
                                    const days = parseInt(document.querySelector(`[data-testid="renew-days-${sub.id}"]`).value);
                                    if (!days || days < 1) { toast.error("Entrez le nombre de jours"); return; }
                                    try {
                                      const res = await apiCall("post", `/admin/renew-subscription/${sub.id}`, { duration_days: days });
                                      toast.success(res.data.message);
                                      fetchData();
                                    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                                  }}>
                                  <RotateCcw size={13} /> Renouvellement
                                </button>
                                <button
                                  className="btn btn-sm"
                                  data-testid={`whatsapp-remind-${sub.id}`}
                                  style={{ background: "#25D366", color: "white", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.72rem", padding: "0.3rem 0.5rem", borderRadius: "8px", border: "none", cursor: "pointer" }}
                                  onClick={async () => {
                                    try {
                                      const res = await apiCall("get", `/admin/subscription-whatsapp/${sub.id}`);
                                      const d = res.data;
                                      const phone = d.client_phone || d.affiliate_phone;
                                      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(d.message)}`, "_blank");
                                    } catch { toast.error("Erreur WhatsApp"); }
                                  }}
                                >
                                  <MessageSquareText size={13} /> Relance
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </>
        )}


        {/* Sol Admin Tab */}
        {activeTab === "sol" && (
          <>
            {/* Create or Status */}
            {!adminSol || adminSol.status === "completed" ? (
              <div className="card" data-testid="admin-sol-create">
                <h3 className="card-title"><DollarSign size={18} /> Creer un nouveau Sol</h3>
                {adminSol?.status === "completed" && (
                  <div style={{ padding: "0.5rem", background: "#F0FDF4", borderRadius: 8, marginBottom: "0.75rem", fontSize: "0.8rem", color: "#16A34A" }}>
                    Le dernier cycle est termine. Vous pouvez en lancer un nouveau.
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <div>
                    <label className="form-label">Cotisation par jour (Gdes)</label>
                    <input type="number" className="form-input" value={solAmount} onChange={e => setSolAmount(parseInt(e.target.value) || 20)} data-testid="admin-sol-amount" />
                  </div>
                  <div>
                    <label className="form-label">Duree du cycle (jours)</label>
                    <input type="number" className="form-input" value={solDuration} onChange={e => setSolDuration(parseInt(e.target.value) || 15)} data-testid="admin-sol-duration" />
                  </div>
                  <div>
                    <label className="form-label">Max participants (vide = illimite)</label>
                    <input type="number" className="form-input" value={solMaxParticipants} onChange={e => setSolMaxParticipants(e.target.value)} placeholder="Illimite" data-testid="admin-sol-max" />
                  </div>
                  <button className="btn btn-primary" data-testid="admin-sol-create-btn"
                    onClick={async () => {
                      try {
                        await apiCall("post", "/admin/sol/create", { amount_per_day: solAmount, cycle_duration_days: solDuration, max_participants: solMaxParticipants ? parseInt(solMaxParticipants) : null });
                        toast.success("Sol cree!");
                        fetchData();
                      } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                    }}>
                    Creer le Sol
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Sol Status */}
                <div className="card" data-testid="admin-sol-status">
                  <h3 className="card-title"><DollarSign size={18} /> Sol en cours</h3>
                  <div className="stats-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
                    <div className="stat-card">
                      <div className="stat-value">{adminSol.amount_per_day} Gdes</div>
                      <div className="stat-label">Cotisation/jour</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{adminSol.total_collected?.toLocaleString()} Gdes</div>
                      <div className="stat-label">Cagnotte</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{adminSol.participants?.length || 0}</div>
                      <div className="stat-label">Participants</div>
                    </div>
                  </div>
                  <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <span className={`badge ${adminSol.status === "active" ? "badge-success" : adminSol.status === "collecting" ? "badge-warning" : "badge-danger"}`}>
                      {adminSol.status === "active" ? "Actif" : adminSol.status === "collecting" ? "Inscriptions" : "Pause"}
                    </span>
                    {adminSol.current_beneficiary && (
                      <span style={{ fontSize: "0.75rem", color: "#475569" }}>Prochain beneficiaire: <strong style={{ color: "#2563EB" }}>{adminSol.current_beneficiary.code}</strong> ({adminSol.current_beneficiary.clients} clients)</span>
                    )}
                    {adminSol.next_distribution_date && (
                      <span style={{ fontSize: "0.75rem", color: "#475569" }}>Distribution: <strong>{new Date(adminSol.next_distribution_date).toLocaleDateString("fr-FR")}</strong></span>
                    )}
                  </div>
                  <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    {adminSol.status === "collecting" && (
                      <button className="btn btn-success btn-sm" data-testid="admin-sol-start"
                        onClick={async () => {
                          try {
                            const res = await apiCall("post", "/admin/sol/start-cycle");
                            toast.success(res.data.message);
                            fetchData();
                          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}>
                        Lancer le cycle
                      </button>
                    )}
                    {adminSol.status === "active" && (
                      <button className="btn btn-primary btn-sm" data-testid="admin-sol-distribute"
                        onClick={async () => {
                          try {
                            const res = await apiCall("post", "/admin/sol/distribute");
                            toast.success(res.data.message);
                            fetchData();
                          } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                        }}>
                        Distribuer la cagnotte
                      </button>
                    )}
                    <button className="btn btn-outline btn-sm" data-testid="admin-sol-toggle"
                      onClick={async () => {
                        try {
                          await apiCall("put", "/admin/sol/settings", { is_active: !adminSol.is_active });
                          toast.success(adminSol.is_active ? "Sol mis en pause" : "Sol reactive");
                          fetchData();
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}>
                      {adminSol.is_active ? "Mettre en pause" : "Reactiver"}
                    </button>
                    <button className="btn btn-sm" style={{ background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE" }} data-testid="admin-sol-send-reminders"
                      onClick={async () => {
                        try {
                          const res = await apiCall("post", "/admin/sol/send-reminders");
                          toast.success(res.data.message);
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}>
                      <Bell size={14} /> Envoyer rappels
                    </button>
                  </div>
                </div>

                {/* Participants */}
                {adminSol.participants && adminSol.participants.length > 0 && (
                  <div className="card" data-testid="admin-sol-participants">
                    <h3 className="card-title"><Users size={18} /> Participants ({adminSol.participants.length})</h3>
                    {adminSol.participants.map((p, i) => (
                      <div key={p.code || `sol-p-${i}`} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.5rem", borderBottom: "1px solid #F1F5F9" }}>
                        <span style={{ width: 24, height: 24, borderRadius: "50%", background: i === adminSol.current_beneficiary_index ? "#2563EB" : "#E2E8F0", color: i === adminSol.current_beneficiary_index ? "white" : "#475569", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 700 }}>{i + 1}</span>
                        <span style={{ flex: 1, fontWeight: 600, fontSize: "0.85rem" }}>{p.code}</span>
                        <span style={{ fontSize: "0.75rem", color: "#475569" }}>{p.clients} clients</span>
                        <span style={{ fontSize: "0.7rem", color: "#16A34A" }}>{p.payments_validated} payes</span>
                        {p.payments_pending > 0 && <span className="badge badge-warning" style={{ fontSize: "0.6rem" }}>{p.payments_pending} en attente</span>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending Payments */}
                {adminSol.pending_payments && adminSol.pending_payments.length > 0 && (
                  <div className="card" data-testid="admin-sol-pending-payments">
                    <h3 className="card-title"><CreditCard size={18} /> Paiements en attente</h3>
                    {adminSol.pending_payments.map((p) => (
                      <div key={p.id} className="pending-card">
                        <div className="pending-info">
                          <div className="pending-phone">{p.affiliate_code}</div>
                          <div className="pending-code">{p.amount} Gdes ({p.payment_method}) {p.transaction_id ? `| TX: ${p.transaction_id}` : ""}</div>
                          <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{formatDate(p.created_at)}</div>
                        </div>
                        <div className="pending-actions">
                          <button className="btn btn-success btn-sm" data-testid={`sol-validate-${p.id}`}
                            onClick={async () => {
                              try {
                                const res = await apiCall("put", `/admin/sol/validate-payment/${p.id}`, { action: "validate" });
                                toast.success(res.data.message);
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            }}>
                            <CheckCircle size={14} /> Valider
                          </button>
                          <button className="btn btn-danger btn-sm"
                            onClick={async () => {
                              try {
                                await apiCall("put", `/admin/sol/validate-payment/${p.id}`, { action: "refuse" });
                                toast.success("Paiement refuse");
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                            }}>
                            <XCircle size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Distribution History */}
            {solDistHistory.length > 0 && (
              <div className="card" data-testid="admin-sol-dist-history">
                <h3 className="card-title"><History size={18} /> Historique distributions</h3>
                {solDistHistory.map((d, i) => (
                  <div key={d.id || `dist-h-${d.round_number}-${i}`} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #F1F5F9", fontSize: "0.8rem" }}>
                    <span><strong>{d.beneficiary_code}</strong> (Tour {d.round_number})</span>
                    <span style={{ fontWeight: 700, color: "#16A34A" }}>{d.amount.toLocaleString()} Gdes</span>
                    <span style={{ color: "#94A3B8", fontSize: "0.7rem" }}>{formatDate(d.distributed_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Catalogue Tab */}
        {activeTab === "catalogue" && <LazyPanel><AdminServices /></LazyPanel>}

        {/* Streaming Connections Tab */}
        {activeTab === "streaming" && (
          <div data-testid="admin-streaming-tab">
            {/* Free slots WhatsApp alert */}
            <div className="card" style={{ marginBottom: "0.8rem", background: "linear-gradient(135deg, #F0FDF4, #ECFDF5)", border: "1px solid #86EFAC" }}>
              <h3 className="card-title" style={{ color: "#14532D" }}>
                <MessageSquareText size={18} /> Alerte WhatsApp — Places disponibles
              </h3>
              <div style={{ fontSize: "0.78rem", color: "#166534", marginBottom: "0.4rem" }}>
                Génère automatiquement un message pour le groupe WhatsApp avec tous les emails ayant des places disponibles.
              </div>
              <button
                className="btn btn-primary"
                data-testid="btn-whatsapp-free-slots"
                style={{ width: "100%", background: "#25D366", border: "none" }}
                onClick={async () => {
                  try {
                    const res = await apiCall("get", "/admin/streaming/whatsapp-alert");
                    const { group_link, message, free_count } = res.data;
                    if (free_count === 0) {
                      toast("Aucune place disponible actuellement.", { icon: "ℹ️" });
                    }
                    // Copy message + open group link
                    try { await navigator.clipboard.writeText(message); toast.success("Message copié — collez-le dans le groupe WhatsApp!"); } catch { /* ignore */ }
                    window.open(group_link, "_blank", "noopener,noreferrer");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Erreur");
                  }
                }}
              >
                <MessageSquareText size={16} /> Générer + Ouvrir le groupe WhatsApp
              </button>
            </div>

            {/* Add connection form */}
            <div className="card" style={{ marginBottom: "1rem" }}>
              <h3 className="card-title"><Tv size={18} /> Ajouter une connexion streaming</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <input className="form-input" placeholder="Email du compte streaming" value={newStreamEmail} onChange={e => setNewStreamEmail(e.target.value)} data-testid="stream-email" />
                <div style={{ fontSize: "0.75rem", color: "#1E40AF", fontWeight: 600, marginTop: "0.2rem" }}>Écrans (max 5) :</div>
                {newStreamProfiles.map((p, i) => (
                  <input key={i} className="form-input" style={{ fontSize: "0.8rem" }} placeholder={`Écran ${i + 1}`} value={p} onChange={e => { const arr = [...newStreamProfiles]; arr[i] = e.target.value; setNewStreamProfiles(arr); }} data-testid={`stream-profile-${i}`} />
                ))}
                <div style={{ fontSize: "0.75rem", color: "#D97706", fontWeight: 600, marginTop: "0.3rem" }}><Crown size={13} style={{ verticalAlign: "middle" }} /> Écrans VIP (max 2) :</div>
                {newStreamVipProfiles.map((p, i) => (
                  <input key={`vip-${i}`} className="form-input" style={{ fontSize: "0.8rem", borderColor: "#FDE68A" }} placeholder={`Écran VIP ${i + 1}`} value={p} onChange={e => { const arr = [...newStreamVipProfiles]; arr[i] = e.target.value; setNewStreamVipProfiles(arr); }} data-testid={`stream-vip-${i}`} />
                ))}
                <input className="form-input" placeholder="Code affilié (optionnel)" value={newStreamAffiliate} onChange={e => setNewStreamAffiliate(e.target.value)} data-testid="stream-affiliate" />
                <button className="btn btn-primary" data-testid="stream-add-btn" onClick={async () => {
                  if (!newStreamEmail.trim()) { toast.error("Email requis"); return; }
                  try {
                    await apiCall("post", "/admin/streaming-connections", {
                      email: newStreamEmail.trim(),
                      profiles: newStreamProfiles.filter(p => p.trim()),
                      vip_profiles: newStreamVipProfiles.filter(p => p.trim()),
                      assigned_affiliate: newStreamAffiliate.trim().toUpperCase()
                    });
                    toast.success("Connexion ajoutée!");
                    setNewStreamEmail(""); setNewStreamProfiles(["", "", "", "", ""]); setNewStreamVipProfiles(["", ""]); setNewStreamAffiliate("");
                    fetchData();
                  } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                }}>
                  <Tv size={16} /> Ajouter
                </button>
              </div>
            </div>

            {/* Normal connections */}
            {(() => {
              const allConns = streamingConns;
              return (
                <div className="card">
                  <h3 className="card-title"><Tv size={18} /> Connexion Streaming ({allConns.length})</h3>
                  {allConns.length === 0 ? (
                    <div className="empty-state"><div className="empty-state-text">Aucune connexion</div></div>
                  ) : allConns.map(conn => (
                    <div key={conn.id} style={{ padding: "0.6rem", marginBottom: "0.5rem", borderRadius: "10px", background: "#F8FAFC", border: "1px solid #E2E8F0" }} data-testid={`stream-${conn.id}`}>
                      {editingStream === conn.id ? (
                        <StreamEditForm conn={conn} onSave={async (data) => {
                          try { await apiCall("put", `/admin/streaming-connections/${conn.id}`, data); toast.success("Mis à jour!"); setEditingStream(null); fetchData(); } catch { toast.error("Erreur"); }
                        }} onCancel={() => setEditingStream(null)} />
                      ) : (
                        <>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ fontWeight: 700, fontSize: "0.88rem", color: "#1E293B", wordBreak: "break-all" }}>{conn.email}</div>
                            <div style={{ display: "flex", gap: "0.3rem" }}>
                              <button style={{ background: "#EFF6FF", border: "none", borderRadius: "6px", padding: "0.2rem 0.4rem", cursor: "pointer", color: "#2563EB", fontSize: "0.65rem" }} onClick={() => setEditingStream(conn.id)}>Modifier</button>
                              <button style={{ background: "#FEF2F2", border: "none", borderRadius: "6px", padding: "0.2rem 0.4rem", cursor: "pointer", color: "#DC2626", fontSize: "0.65rem" }} onClick={async () => {
                                try { await apiCall("delete", `/admin/streaming-connections/${conn.id}`); toast.success("Supprimé"); fetchData(); } catch { toast.error("Erreur"); }
                              }}>Suppr.</button>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginTop: "0.3rem" }}>
                            {conn.profiles.map((p, i) => (
                              <span key={`p-${conn._id || conn.id || ''}-${p}-${i}`} style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.72rem", background: "#DBEAFE", color: "#1E40AF", fontWeight: 500 }}>{p}</span>
                            ))}
                          </div>
                          {conn.vip_profiles && conn.vip_profiles.length > 0 && (
                            <div style={{ marginTop: "0.3rem" }}>
                              <span style={{ fontSize: "0.65rem", fontWeight: 700, color: "#D97706" }}>VIP:</span>
                              <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap", marginTop: "0.15rem" }}>
                                {conn.vip_profiles.map((p, i) => (
                                  <span key={`vip-${conn._id || conn.id || ''}-${p}-${i}`} style={{ padding: "0.2rem 0.5rem", borderRadius: "999px", fontSize: "0.72rem", background: "#FEF3C7", color: "#92400E", fontWeight: 500 }}>{p}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {conn.assigned_affiliate && (
                            <div style={{ fontSize: "0.68rem", color: "#16A34A", marginTop: "0.2rem", fontWeight: 600 }}>Affilié: {conn.assigned_affiliate}</div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Explainer Videos Tab */}
        {activeTab === "videos" && (
          <div data-testid="admin-videos-tab">
            <div className="card">
              <h3 className="card-title"><Play size={18} /> Vidéos explicatives</h3>
              <div style={{ fontSize: "0.78rem", color: "#64748B", marginBottom: "0.75rem" }}>
                Configurez les vidéos pour le popup d'accueil, le dashboard client et le dashboard affilié. Utilisez une URL YouTube ou Vimeo.
              </div>
              {[
                { target: "popup", label: "Popup d'accueil (StoreFront)", desc: "Affichée au-dessus du bouton 'Voir les services'" },
                { target: "client", label: "Dashboard Client", desc: "Explique comment acheter un service" },
                { target: "affiliate", label: "Dashboard Affilié", desc: "Explique comment vendre efficacement" }
              ].map(({ target, label, desc }) => {
                const existing = explainerVideos.find(v => v.target === target);
                return (
                  <div key={target} style={{ padding: "0.6rem", marginBottom: "0.5rem", borderRadius: "10px", background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#1E293B" }}>{label}</div>
                    <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginBottom: "0.3rem" }}>{desc}</div>
                    {existing ? (
                      <div>
                        <div style={{ fontSize: "0.75rem", color: "#2563EB", wordBreak: "break-all", marginBottom: "0.3rem" }}>{existing.title || existing.video_url}</div>
                        <div style={{ display: "flex", gap: "0.3rem" }}>
                          <button className="btn btn-sm" style={{ background: "#FEF2F2", color: "#DC2626", fontSize: "0.65rem" }}
                            onClick={async () => { try { await apiCall("delete", `/admin/explainer-videos/${target}`); toast.success("Supprimé"); fetchData(); } catch {} }}>
                            Supprimer
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                        <input className="form-input" style={{ fontSize: "0.78rem" }} placeholder="Titre (optionnel)" data-testid={`video-title-${target}`} />
                        <input className="form-input" style={{ fontSize: "0.78rem" }} placeholder="URL vidéo YouTube/Vimeo" data-testid={`video-url-${target}`} />
                        <button className="btn btn-primary btn-sm" data-testid={`video-save-${target}`}
                          onClick={async () => {
                            const title = document.querySelector(`[data-testid="video-title-${target}"]`).value;
                            const url = document.querySelector(`[data-testid="video-url-${target}"]`).value;
                            if (!url.trim()) { toast.error("URL requise"); return; }
                            try {
                              await apiCall("post", "/admin/explainer-videos", { target, title, video_url: url.trim() });
                              toast.success("Vidéo enregistrée!");
                              fetchData();
                            } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                          }}>
                          <Play size={14} /> Enregistrer
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}





        {/* Formations Tab */}
        {activeTab === "formations" && <LazyPanel><AdminCourses /></LazyPanel>}

        {/* Professeurs Tab */}
        {activeTab === "professeurs" && <LazyPanel><AdminProfessors /></LazyPanel>}

        {/* Admin Crypto Tab */}
        {activeTab === "crypto" && (
          <LazyPanel>
            <div className="card" data-testid="admin-crypto-tab">
              <h3 className="card-title"><Bitcoin size={18} /> Échange Crypto (Escrow)</h3>
              <CryptoExchangeAdmin apiCall={apiCall} />
            </div>
          </LazyPanel>
        )}

        {/* Admin Transactions Tab */}
        {activeTab === "transactions" && <AdminTransactions />}

        {/* Admin Balances Tab */}
        {activeTab === "balances" && <AdminBalancePanel apiCall={apiCall} />}

        {/* Admin Balance History Tab */}
        {activeTab === "history" && <LazyPanel><AdminBalanceHistory apiCall={apiCall} /></LazyPanel>}

        {/* Admin Receipts Tab — payment proofs anti-fraud history */}
        {activeTab === "receipts" && <LazyPanel><AdminReceipts /></LazyPanel>}

        {/* Admin Client Audit Tab — full per-client transactions + corrections */}
        {activeTab === "audit" && <LazyPanel><AdminClientAudit /></LazyPanel>}

        {/* Admin P2P Verification — Zelle/Wise marketplace stuck orders */}
        {activeTab === "p2p" && <LazyPanel><AdminP2PVerification /></LazyPanel>}
        {activeTab === "htg-p2p" && <LazyPanel><AdminHtgP2PExchanges apiCall={apiCall} /></LazyPanel>}
        {activeTab === "profit" && <LazyPanel><AdminProfitDashboard apiCall={apiCall} currentUser={user} /></LazyPanel>}

        {/* Admin Global History Tab — full traceability */}
        {activeTab === "global-history" && <LazyPanel><AdminGlobalHistory apiCall={apiCall} /></LazyPanel>}
        {activeTab === "freefire" && <LazyPanel><AdminFreeFire apiCall={apiCall} /></LazyPanel>}
        {activeTab === "game-topup" && <LazyPanel><AdminGameTopup apiCall={apiCall} currentUser={user} /></LazyPanel>}
        {activeTab === "messages" && <LazyPanel><AdminChat apiCall={apiCall} /></LazyPanel>}
        {activeTab === "wa2" && <LazyPanel><AdminWhatsAppRequests apiCall={apiCall} /></LazyPanel>}
        {activeTab === "safety" && <LazyPanel><AdminClientSafety apiCall={apiCall} /></LazyPanel>}
        {activeTab === "smm" && <LazyPanel><AdminSMM apiCall={apiCall} highlightOrderId={highlightedSmmOrderId} /></LazyPanel>}
        {activeTab === "contacts" && <LazyPanel><AdminClientContacts /></LazyPanel>}

        {activeTab === "settings" && <LazyPanel><AdminSettings apiCall={apiCall} /></LazyPanel>}



      </div>
      </div>{/* /.sidebar-content */}
      <ConfirmDialog
        isOpen={showReassignDialog}
        title="Client déjà assigné"
        message={reassignData ? `${reassignData.message}\n\nVoulez-vous réassigner ce client à ${reassignData.newCode}?` : ""}
        onConfirm={handleConfirmReassign}
        onCancel={handleCancelReassign}
        confirmText="Réassigner"
        cancelText="Annuler"
        type="warning"
      />

      {/* Post-validation WhatsApp actions (admin) */}
      {postValidateInfo && (
        <div
          onClick={() => setPostValidateInfo(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "1rem", zIndex: 900,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="card"
            data-testid="post-validate-modal"
            style={{ maxWidth: 520, width: "100%", padding: "1.25rem" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <CheckCircle size={22} color="#16A34A" />
              <h3 style={{ margin: 0, fontSize: "1.05rem", color: "#0F172A" }}>Commande validée</h3>
            </div>
            {postValidateInfo.reminder && (
              <div style={{
                background: "#FEF3C7", border: "1px solid #F59E0B",
                borderRadius: 8, padding: "0.6rem", fontSize: "0.82rem", color: "#78350F",
                marginBottom: "0.75rem", lineHeight: 1.4,
              }}>
                ⚡ {postValidateInfo.reminder}
              </div>
            )}
            <div style={{ fontSize: "0.8rem", color: "#475569", marginBottom: "0.5rem" }}>
              {postValidateInfo.service} — {postValidateInfo.plan} ({postValidateInfo.price} Gdes)
            </div>

            <div style={{ display: "grid", gap: "0.45rem", marginTop: "0.5rem" }}>
              {postValidateInfo.whatsapp_affiliate_url ? (
                <a
                  href={postValidateInfo.whatsapp_affiliate_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  data-testid="post-validate-wa-affiliate"
                  style={{
                    background: "#25D366", color: "white", padding: "0.65rem",
                    borderRadius: 10, textDecoration: "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: "0.4rem", fontWeight: 700,
                  }}
                >
                  <Send size={15} /> Contacter l'affilié {postValidateInfo.affiliate_code || ""} sur WhatsApp
                </a>
              ) : postValidateInfo.affiliate_code ? (
                <div style={{ fontSize: "0.78rem", color: "#B91C1C", padding: "0.5rem", background: "#FEF2F2", borderRadius: 8 }}>
                  ⚠ L'affilié {postValidateInfo.affiliate_code} n'a pas de numéro WhatsApp enregistré.
                </div>
              ) : null}

              {postValidateInfo.whatsapp_client_confirm_url && (
                <a
                  href={postValidateInfo.whatsapp_client_confirm_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn"
                  data-testid="post-validate-wa-client"
                  style={{
                    background: "#2563EB", color: "white", padding: "0.55rem",
                    borderRadius: 10, textDecoration: "none",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: "0.4rem", fontWeight: 700, fontSize: "0.85rem",
                  }}
                >
                  <Send size={14} /> Envoyer confirmation au client ({postValidateInfo.client_whatsapp})
                </a>
              )}

              <button
                className="btn"
                onClick={() => setPostValidateInfo(null)}
                style={{
                  background: "#F1F5F9", color: "#475569", padding: "0.5rem",
                  borderRadius: 10, border: "none", marginTop: "0.3rem", fontSize: "0.82rem",
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !["admin", "sub_admin"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Sub-Admins and Admins must always use /admin (not the affiliate /dashboard)
  if (!adminOnly && ["admin", "sub_admin"].includes(user.role)) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

// Client Page Wrapper
const ClientPage = ({ initialView }) => {
  const navigate = useNavigate();
  const [clientUser, setClientUser] = useState(null);

  useEffect(() => {
    // === EMERGENT GOOGLE AUTH CALLBACK ===
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // After Google sign-in, the user lands on /client#session_id=<id>.
    // We exchange it ONCE for a normal client JWT and persist it.
    const hash = window.location.hash || "";
    if (hash.includes("session_id=")) {
      const params = new URLSearchParams(hash.replace(/^#/, ""));
      const sessionId = params.get("session_id");
      if (sessionId) {
        // Strip the fragment immediately to prevent loops on remount.
        try {
          const url = new URL(window.location.href);
          url.hash = "";
          window.history.replaceState({}, "", url.toString());
        } catch {}
        (async () => {
          try {
            const res = await axios.post(`${API}/auth/google/exchange`, {}, {
              headers: { "X-Session-ID": sessionId },
            });
            setStoredTokens(res.data.token, res.data.refresh_token, "client");
            localStorage.setItem("client_user", JSON.stringify(res.data));
            setClientUser(res.data);
            try { subscribePushIfPossible(); } catch { /* ignore */ }
            toast.success(res.data.is_new ? "Bienvenue !" : "Connexion réussie !");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Échec de la connexion Google");
            navigate("/login?mode=client");
          }
        })();
        return;
      }
    }

    const stored = localStorage.getItem("client_user");
    const token = localStorage.getItem("client_token");
    if (stored && token) {
      setClientUser(JSON.parse(stored));
    } else {
      // Preserve `?thanks=1&order=...` so the toast still fires after login
      try {
        const search = window.location.search || "";
        if (search) {
          sessionStorage.setItem("client_post_login_redirect", `/client${search}`);
        }
      } catch {}
      navigate("/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("client_token");
    localStorage.removeItem("client_user");
    navigate("/");
  };

  if (!clientUser) return <div className="loading"><div className="spinner"></div></div>;

  return <ClientDashboard clientUser={clientUser} onLogout={handleLogout} initialView={initialView} />;
};

// StoreFront Page Wrapper
const StoreFrontPage = () => {
  const navigate = useNavigate();
  const handleNavigate = (dest, query = "") => {
    if (dest === "client") navigate(`/client${query || ""}`);
    else if (dest === "login-client") navigate("/login?mode=client&action=login");
    else if (dest === "register-client") navigate("/login?mode=client&action=register");
    else if (dest === "login-affiliate") navigate("/login?mode=affiliate");
    else if (dest === "login-professor") navigate("/login?mode=professor");
    else navigate("/login");
  };
  return <StoreFront onNavigate={handleNavigate} />;
};

// Professor Page Wrapper
const ProfessorPage = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("prof_token");
    if (!token) navigate("/login?mode=professor");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("prof_token");
    localStorage.removeItem("prof_user");
    navigate("/");
  };

  if (!localStorage.getItem("prof_token")) return <div className="loading"><div className="spinner"></div></div>;
  return <ProfessorDashboard onLogout={handleLogout} />;
};

// App
function App() {
  useVersionCheck();
  return (
    <div className="app-container">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<StoreFrontPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/activate" element={<LazyPanel><ActivateClientPage /></LazyPanel>} />
            <Route path="/client" element={<ClientPage />} />
            <Route path="/client/smm" element={<ClientPage initialView="smm" />} />
            <Route path="/smm" element={<LazyPanel><PublicSMM /></LazyPanel>} />
            <Route path="/professor" element={<ProfessorPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin" element={
              <ProtectedRoute adminOnly>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors duration={2500} offset="64px" />
      <CookieConsent />
      <NotificationsPrompt />
    </div>
  );
}

export default App;
